import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getUIComponentsForDesign } from '../services/uiComponentsService';
import { analyzeDesignImage, extractColorsFromAnalysis, generateDesignName } from '../services/designAnalysisService';

const DesignSelector = ({ onDesignSelected, onBack }) => {
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [customColors, setCustomColors] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeColorIndex, setActiveColorIndex] = useState(0);
  const [hue, setHue] = useState(180);
  const [saturation, setSaturation] = useState(50);
  const [lightness, setLightness] = useState(50);
  const [uploadedDesigns, setUploadedDesigns] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const colorPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const continueSectionRef = useRef(null);

  useEffect(() => {
    if (selectedDesign && continueSectionRef.current) {
      continueSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedDesign]);

  // Extract colors from design prompts
  const extractColors = (designId) => {
    const colorMaps = {
      'homerun': [
        { name: 'Background', color: '#FFFFFF', description: 'Pure white background' },
        { name: 'Secondary Background', color: '#FAFAF7', description: 'Light off-white' },
        { name: 'Primary Blue', color: '#4F75FE', description: 'Vibrant blue for CTAs' },
        { name: 'Primary Green', color: '#00C275', description: 'Professional green' },
        { name: 'Accent Pink', color: '#F0A8FA', description: 'Soft pink accent' },
        { name: 'Primary Text', color: '#2D2323', description: 'Dark charcoal text' },
        { name: 'Secondary Text', color: '#6B7280', description: 'Medium gray text' }
      ],
      'cluely': [
        { name: 'Background', color: '#FFFFFF', description: 'Pure white background' },
        { name: 'Gradient Start', color: '#E0F2FE', description: 'Light blue gradient start' },
        { name: 'Gradient End', color: '#DBEAFE', description: 'Light blue gradient end' },
        { name: 'Primary Text', color: '#18181B', description: 'Dark zinc text' },
        { name: 'Secondary Text', color: '#71717A', description: 'Medium zinc text' },
        { name: 'Accent Blue', color: '#3B82F6', description: 'Blue accent color' },
        { name: 'Border', color: '#E4E4E7', description: 'Light zinc border' }
      ],
      'airbnb': [
        { name: 'Background', color: '#FFFFFF', description: 'Pure white background' },
        { name: 'Secondary Background', color: '#F7F7F7', description: 'Light gray background' },
        { name: 'Primary Red', color: '#FF385C', description: 'Airbnb brand red' },
        { name: 'Secondary Red', color: '#E61E4D', description: 'Darker red variant' },
        { name: 'Primary Text', color: '#222222', description: 'Dark text' },
        { name: 'Secondary Text', color: '#717171', description: 'Medium gray text' },
        { name: 'Border', color: '#DDDDDD', description: 'Light gray border' }
      ],
      'discord': [
        { name: 'Background', color: '#1A1625', description: 'Dark purple background' },
        { name: 'Surface', color: '#2B2D31', description: 'Lighter dark surface' },
        { name: 'Primary Text', color: '#FFFFFF', description: 'White text' },
        { name: 'Secondary Text', color: '#B9BBBE', description: 'Light gray text' },
        { name: 'Brand Purple', color: '#5865F2', description: 'Discord brand purple' },
        { name: 'Accent Blue', color: '#00B0F4', description: 'Blue accent' },
        { name: 'Border', color: '#40444B', description: 'Dark border' }
      ],
      'notion': [
        { name: 'Background', color: '#FFFFFF', description: 'Pure white background' },
        { name: 'Secondary Background', color: '#F7F6F3', description: 'Warm off-white' },
        { name: 'Primary Text', color: '#37352F', description: 'Dark brown text' },
        { name: 'Secondary Text', color: '#787774', description: 'Medium brown text' },
        { name: 'Accent Blue', color: '#2E75CC', description: 'Notion blue' },
        { name: 'Border', color: '#E3E2E0', description: 'Light brown border' },
        { name: 'Highlight', color: '#FBF3DB', description: 'Warm highlight' }
      ],
      'figma': [
        { name: 'Background', color: '#FFFFFF', description: 'Pure white background' },
        { name: 'Secondary Background', color: '#F5F5F5', description: 'Light gray background' },
        { name: 'Primary Text', color: '#000000', description: 'Black text' },
        { name: 'Secondary Text', color: '#333333', description: 'Dark gray text' },
        { name: 'Brand Purple', color: '#F24E1E', description: 'Figma brand orange' },
        { name: 'Accent Blue', color: '#18A0FB', description: 'Blue accent' },
        { name: 'Border', color: '#E5E5E5', description: 'Light gray border' }
      ],
      'cursor': [
        { name: 'Background', color: '#0D1117', description: 'Dark background' },
        { name: 'Surface', color: '#161B22', description: 'Lighter dark surface' },
        { name: 'Primary Text', color: '#F0F6FC', description: 'Light text' },
        { name: 'Secondary Text', color: '#8B949E', description: 'Medium gray text' },
        { name: 'Accent Blue', color: '#58A6FF', description: 'Blue accent' },
        { name: 'Border', color: '#30363D', description: 'Dark border' },
        { name: 'Highlight', color: '#1F6FEB', description: 'Blue highlight' }
      ],
      'netflix': [
        { name: 'Background', color: '#000000', description: 'Pure black background' },
        { name: 'Surface', color: '#141414', description: 'Dark gray surface' },
        { name: 'Primary Text', color: '#FFFFFF', description: 'White text' },
        { name: 'Secondary Text', color: '#B3B3B3', description: 'Light gray text' },
        { name: 'Brand Red', color: '#E50914', description: 'Netflix brand red' },
        { name: 'Accent', color: '#564D4D', description: 'Dark accent' },
        { name: 'Border', color: '#333333', description: 'Dark border' }
      ],
      'jasper-ai': [
        { name: 'Background', color: '#FFFFFF', description: 'Pure white background' },
        { name: 'Secondary Background', color: '#F8FAFC', description: 'Light slate background' },
        { name: 'Primary Text', color: '#0F172A', description: 'Dark slate text' },
        { name: 'Secondary Text', color: '#64748B', description: 'Medium slate text' },
        { name: 'Brand Green', color: '#10B981', description: 'Jasper brand green' },
        { name: 'Accent Blue', color: '#3B82F6', description: 'Blue accent' },
        { name: 'Border', color: '#E2E8F0', description: 'Light slate border' }
      ]
    };
    
    // Check if it's an uploaded design
    const uploadedDesign = uploadedDesigns.find(d => d.id === designId);
    if (uploadedDesign) {
      const colorsObject = extractColorsFromAnalysis(uploadedDesign.analysis);
      // Convert object format to array format for consistency
      return Object.entries(colorsObject).map(([key, colorInfo]) => ({
        name: colorInfo.name || key,
        color: colorInfo.hex || colorInfo.color || '#000000',
        description: colorInfo.description || 'Custom color from design analysis'
      }));
    }
    
    return colorMaps[designId] || [];
  };

  // Convert hex to HSL
  const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: h = 0; break;
      }
      h /= 6;
    }

    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
  };

  // Convert HSL to hex
  const hslToHex = (h, s, l) => {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = n => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };

  // Handle design selection
  const handleDesignSelect = (design) => {
    setSelectedDesign(design.id);
    const colors = extractColors(design.id);
    const colorMap = {};
    colors.forEach((color, index) => {
      colorMap[index] = color.color;
    });
    setCustomColors(colorMap);
  };

  // Handle color change
  const handleColorChange = (colorIndex, newColor) => {
    setCustomColors(prev => ({
      ...prev,
      [colorIndex]: newColor
    }));
  };

  // Open color picker
  const openColorPicker = (colorIndex, currentColor) => {
    setActiveColorIndex(colorIndex);
    const [h, s, l] = hexToHsl(currentColor);
    setHue(h);
    setSaturation(s);
    setLightness(l);
    setShowColorPicker(true);
  };

  // Close color picker on escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showColorPicker]);

  // Update color from picker
  const updateColorFromPicker = () => {
    const newColor = hslToHex(hue, saturation, lightness);
    handleColorChange(activeColorIndex, newColor);
  };

  useEffect(() => {
    if (showColorPicker) {
      updateColorFromPicker();
    }
  }, [hue, saturation, lightness]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle file upload
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Please upload an image smaller than 10MB');
      return;
    }
    
    try {
      setIsAnalyzing(true);
      
      // Analyze the image using GPT-4o
      const analyzedDesign = await analyzeDesignImage(file);
      
      // Generate a better name based on analysis
      const designName = generateDesignName(analyzedDesign.analysis);
      analyzedDesign.name = designName;
      
      // Add to uploaded designs
      setUploadedDesigns(prev => [...prev, analyzedDesign]);
      
      // Auto-select the new design
      setSelectedDesign(analyzedDesign.id);
      
      // Set up initial colors from analysis
      const colorsObject = extractColorsFromAnalysis(analyzedDesign.analysis);
      const colorMap = {};
      
      // Convert object format to indexed format for the color picker
      Object.entries(colorsObject).forEach(([key, colorInfo], index) => {
        colorMap[index] = colorInfo.hex || colorInfo.color || '#000000';
      });
      
      setCustomColors(colorMap);
      
    } catch (error) {
      console.error('Error analyzing design:', error);
      alert('Failed to analyze the design. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  // Generate updated design prompt with custom colors
  const getUpdatedDesignPrompt = (originalDesign) => {
    if (!selectedDesign || Object.keys(customColors).length === 0) {
      return originalDesign.prompt;
    }

    const colors = extractColors(selectedDesign);
    let updatedPrompt = originalDesign.prompt;

    // Replace color values in the prompt
    colors.forEach((colorInfo, index) => {
      if (customColors[index]) {
        const originalColor = colorInfo.color;
        const newColor = customColors[index];
        updatedPrompt = updatedPrompt.replace(new RegExp(originalColor, 'g'), newColor);
      }
    });

    return updatedPrompt;
  };

  const handleContinue = () => {
    const allDesigns = [...designTypes, ...uploadedDesigns];
    const originalDesign = allDesigns.find(d => d.id === selectedDesign);
    const updatedDesign = {
      ...originalDesign,
      prompt: getUpdatedDesignPrompt(originalDesign),
      customColors: customColors
    };
    onDesignSelected(updatedDesign);
  };

  const designTypes = [
    {
      id: 'homerun',
      name: 'Homerun',
      image: '/designinspoimages/homerun1.png',
      description: 'Clean recruitment platform with intuitive candidate flow',
      prompt: `Transform my current website design, CSS styling and overall frontend into the following strict and descriptive design and frontend specification document. Make sure to not change any copy, context or functionality we currently have in the website, this is solely a design UI/UX and frontend redesign.

IMPORTANT COMPONENT LIBRARY REQUIREMENT:
You MUST use shadcn/ui for all UI components to ensure accessibility, consistency, and modern design patterns. shadcn/ui is a collection of copy-and-paste components built on top of Radix UI primitives and styled with version 3 of Tailwind CSS.

Installation & Setup:
1. Install shadcn/ui CLI: npx shadcn-ui@latest init
2. Configure your components.json when prompted
3. Add components as needed: npx shadcn-ui@latest add [component-name]

Key Benefits:
- Fully accessible components built on Radix UI primitives
- Beautifully designed with consistent styling
- Customizable and themeable
- TypeScript support out of the box
- Copy-paste friendly - you own the code
- Works seamlessly with Tailwind CSS

Usage Guidelines:
- Use shadcn/ui components for buttons, forms, modals, navigation, cards, etc.
- Examples: npx shadcn-ui@latest add button card form dialog navigation-menu
- Import components from your components/ui directory
- Customize component variants using the built-in variant system
- Leverage the built-in dark mode support
- Follow the component composition patterns for complex UI elements

Always prioritize shadcn/ui components over custom implementations to maintain design consistency and accessibility standards.
Website Structure and Technology
The website is built using a standard HTML and CSS structure. A single CSS file, homerundesign.css, contains all the styling rules, indicating a centralized approach to managing the site's appearance. The use of classes like style-0, style-1, etc., suggests a systematic, if not programmatically generated, approach to styling individual elements. The HTML structure is semantic, with clear <header>, <section>, and <footer> elements, which is beneficial for both search engine optimization (SEO) and accessibility.

Color Palette
The color scheme is a key contributor to the website's clean and inviting feel. The palette consists of:

Primary Colors: A vibrant blue (#4F75FE) and a professional green (#00C275) are used for calls-to-action and to highlight important information.

Secondary Colors: A soft pink (#F0A8FA) is used for the "Teams love Homerun" section, adding a touch of warmth and personality.

Neutral Colors: A range of grays and off-whites (#FFFFFF, #FAFAF7, #F5F1EC) are used for backgrounds and text, creating a sense of spaciousness and readability.

Accent Colors: A dark charcoal (#2D2323) is used for typography, providing strong contrast and a modern feel.

Typography
The typography is a standout feature, utilizing a sans-serif font family, likely "GT America" and "GT Walsheim," as referenced in the CSS. The font choice contributes to the modern and clean aesthetic.

Headings: Headings are large, bold, and have tight letter spacing, creating a strong visual impact. The hero section's <h1> is a prominent example of this.

Body Text: The body text is set in a smaller, highly readable sans-serif font, ensuring a comfortable reading experience.

Font Weights: The site makes effective use of different font weights to create a clear visual hierarchy. Bold weights are used for headings and calls-to-action, while lighter weights are used for body copy.

Layout and Spacing
The layout is spacious and uncluttered, with generous use of white space that guides the user's eye and improves readability.

Grid System: The layout appears to be based on a grid system, which ensures consistency and alignment of elements across the page.

Padding and Margins: Ample padding and margins are used around all elements, preventing a cramped or cluttered look.

Max-Width: The content is constrained to a maximum width, which prevents it from becoming too wide on larger screens and maintains a comfortable reading line length.

Navigation Bar
The navigation bar is a sticky element, meaning it remains at the top of the page as the user scrolls. This provides easy access to the main sections of the site at all times.

Logo: The Homerun logo is prominently displayed on the left side of the navigation bar.

Navigation Links: The navigation links are clear and concise, with a "Resources" dropdown to house additional pages without cluttering the main navigation.

Call-to-Action Buttons: The "Book a demo" and "Start free trial" buttons are styled with contrasting colors to make them stand out and encourage user interaction.

Hero Section
The hero section is the first thing a user sees and is designed to make a strong first impression.

Headline: A large, bold headline clearly communicates the value proposition of the product.

Subheading: A concise subheading provides additional information and context.

Call-to-Action Buttons: Prominent call-to-action buttons encourage users to take the next step.

Social Proof: The inclusion of Capterra ratings, customer satisfaction scores, and the number of teams using the platform builds trust and credibility.

Hero Image: A large, high-quality image on the right side of the hero section provides a visual representation of the product.

Feature Sections
The feature sections use a combination of text and images to showcase the key functionalities of the product.

Alternating Layout: The layout alternates between placing the image on the left and the text on the right, which creates visual interest and breaks up the monotony of the page.

Clear Headings and Subheadings: Each feature is introduced with a clear and concise heading and subheading.

Accordion Menus: An accordion-style menu is used to reveal more detailed information about each feature without overwhelming the user with too much text at once.

Testimonial Section
The testimonial section leverages social proof to build trust and credibility with potential customers.

Marquee-style Scroller: Testimonials are displayed in a marquee-style scroller that automatically moves horizontally across the page.

Customer Photos: Including photos of the customers who provided the testimonials adds a personal touch and makes the testimonials feel more authentic.

Clear Attributions: Each testimonial is clearly attributed to the person who provided it, including their name and company.

Footer
The footer is well-organized and provides easy access to important information and resources.

Sitemap: The footer includes a sitemap with links to all the main pages of the website.

Newsletter Signup: A prominent newsletter signup form encourages users to stay connected with the company.

Social Media Links: Links to Homerun's social media profiles are included, allowing users to connect with the company on other platforms.

Animations and Effects
The website uses subtle animations and effects to enhance the user experience and create a more dynamic and engaging feel.

Hover Effects: Buttons and links have hover effects that provide visual feedback to the user and make the interface feel more responsive.

Marquee Scroller: The testimonial section uses a marquee scroller to display a large number of testimonials in a relatively small space.

Fade-in and Slide-in Effects: Elements on the page may use fade-in or slide-in animations as the user scrolls, which adds a touch of elegance and sophistication to the design.

Global Styles & Page Structure
The foundation of the page is set in the <body> tag, establishing the default typography and layout properties for the entire site.

HTML Structure (<body> tag):

HTML

<body style="cursor: auto; clear: none; flex-flow: column nowrap; align-items: center; display: flex; color: rgb(74, 62, 62); background-color: rgb(255, 255, 255); flex-direction: column; font-family: 'GT America', sans-serif; min-height: 100%; margin: 0px; font-size: 14px; line-height: 20px; box-sizing: border-box;">
...
</body>
CSS Analysis (.style-0):

display: flex; flex-direction: column; align-items: center;: This establishes a vertical flexbox layout for the entire page, centering the main content column. This is a modern and effective way to manage the primary layout.

font-family: 'GT America', sans-serif;: Sets the default font for the entire page.

color: rgb(74, 62, 62);: A dark, warm grey for body text, which is softer than pure black and enhances readability.

background-color: rgb(255, 255, 255);: A standard white background.

font-size: 14px; line-height: 20px;: Default text size and line height for base-level text.

box-sizing: border-box;: Applied universally, this is a crucial best practice that simplifies layout calculations by including padding and border in the element's total width and height.

Navigation Bar
The navigation is a clean, sticky header that remains visible on scroll. It contains the brand logo, primary navigation links, a dropdown menu for secondary resources, and prominent call-to-action buttons.

HTML Structure:
The navigation bar is a div element with position: sticky and a high z-index to keep it on top of other content. It uses a flexbox layout to space out its child elements.

HTML

<div theme="white" ... style="z-index: 999; ... position: sticky; top: 0px; ...">
    <div ... style="max-width: 1380px; justify-content: space-between; ... display: flex;">
        <a href="/" ...>...</a>
        <nav role="navigation" ...>
            <div data-nav-btn-container="" ...>
                <a href="/demo" ...>Book a demo</a>
                <a href="/trial" ...>Start free trial</a>
            </div>
        </nav>
    </div>
</div>
Component & Styling Breakdown:

Sticky Container (.style-4):

position: sticky; top: 0px;: This is the key to the navigation bar "sticking" to the top of the viewport when the user scrolls.

z-index: 999;: Ensures the navigation bar stays above other page content.

background-color: rgb(255, 255, 255);: The bar has a solid white background.

Navigation Links (<a> tags inside .style-31):

padding: 18px 12px;: Generous padding creates a large, clickable area.

font-size: 16px; font-weight: 400;: Clear and legible link text.

transition: background-color 0.3s;: A subtle transition effect on hover provides user feedback.

The "Organise your hiring" and "Manage your team" links include a styled SVG icon next to the text, contained within a flex div.

"Resources" Dropdown (.style-57):

This is a div that likely has JavaScript attached to it to toggle the visibility of the nested <nav> menu on hover (indicated by data-hover="true").

The dropdown content (.style-64) is a grid layout, allowing for complex and organized content. It has a box-shadow and a border to visually separate it from the page content below.

Call-to-Action Buttons:

"Log in" Button: Styled as a "ghost" button.

border: 1px solid rgb(237, 235, 231);

background-color: rgba(0, 0, 0, 0); (transparent)

color: rgb(74, 62, 62);

"Book a demo" Button (.style-141): A solid, high-contrast button.

background-color: rgb(45, 35, 35); (dark charcoal)

color: rgb(255, 255, 255); (white text)

"Start free trial" Button (.style-142): The primary call-to-action, using the main brand color.

background-color: rgb(79, 117, 254); (brand blue)

color: rgb(255, 255, 255); (white text)

All buttons share a border-radius: 12px; for a modern, rounded look.

Hero Section
This section makes an immediate impact with a large, bold heading, a clear value proposition, and prominent calls to action, balanced by a visually appealing product image.

HTML Structure:
A two-column layout is achieved using display: flex on the container (.style-316). The left column (.style-317) contains the text content and CTAs, while the right holds the hero image.

HTML

<section class="style-313">
    <div class="style-316">
        <div class="style-317">
            <h1 class="style-319">Beautiful<br>HR tools for modern teams.</h1>
            <p class="style-321">Transform your hiring process...</p>
            <div class="style-322">...</div>
        </div>
        <img ... class="style-349" src="...">
    </div>
</section>
Component & Styling Breakdown:

Main Headline (<h1>, .style-319):

font-family: 'GT Walsheim', sans-serif;: This distinct, bold font is reserved for major headings.

font-size: 64px; line-height: 64px;: Creates a large, impactful title.

font-weight: 900;: A very heavy font weight for maximum emphasis.

letter-spacing: -1px;: Tight letter-spacing gives the headline a compact, modern feel.

Sub-headline (<p>, .style-321):

font-size: 22px; line-height: 30.8px;: A larger-than-body font size that is easy to read and complements the main headline.

color: rgb(74, 62, 62);: Uses the default body text color.

Social Proof (.style-330):

This section uses a display: grid layout to arrange three items in a row.

Each item is a flex container (.style-331, .style-339, .style-344) that aligns an icon and its text.

The star rating for Capterra is cleverly implemented with a background-image on a <strong> tag, showing five stars.

Hero Image (<img>, .style-349):

border-radius: 40px;: Large, rounded corners give the image a soft, modern look that matches the overall aesthetic.

Feature Sections
The "Organise your hiring" and "Manage your team" sections use a consistent, clean layout to present product features. A key UX element is the use of accordions to reveal more information on demand.

HTML Structure:
These sections use a two-column flex layout. One column contains the descriptive text and accordion menu, while the other contains a large product image.

HTML

<section class="style-425">
    <div class="style-426">
        <h2 class="style-431">Organise your hiring.</h2>
        <div class="style-432">...</div>
        <div class="style-435">
            <div data-w-id="..." class="style-436">
                 <div role="button" class="style-437">...</div>
                 <nav class="style-442">...</nav>
            </div>
        </div>
    </div>
    <div class="style-496">...</div>
</section>
Component & Styling Breakdown:

Section Layout (.style-425, .style-498):

These sections have a large gap: 110px between the text and image columns, creating ample white space.

They feature a high border-radius: 40px; and generous padding: 48px 64px;.

The "Manage your team" section uses a background-image: radial-gradient(...) for a subtle, sophisticated background texture.

Accordion Menu:

Each accordion item (.style-436, .style-509) is a self-contained unit with a border-radius: 10px;.

The trigger (.style-437) is a flex container that uses justify-content: space-between; to place the title on the left and the dropdown arrow on the right.

The expandable content area is a <nav> tag (.style-442). Its initial height: 0px; keeps it hidden. An interaction (likely JavaScript triggered by the data-w-id) would change the height to auto to reveal the content with a smooth transition.

The text inside the expanded accordion (.style-444) has a slightly smaller font size (18px) than the feature description, creating a clear hierarchy.

Testimonials Section
This section uses a dynamic, auto-scrolling marquee to display customer testimonials, building trust through social proof.

HTML Structure:
A flex container (.style-583) with overflow: hidden; acts as the viewport for the marquee. Inside, two identical divs (.style-584, .style-650) contain the list of testimonials. This duplication is a classic technique to create a seamless, infinite scrolling loop.

HTML

<section class="style-572">
    ...
    <div class="style-583">
        <div data-duration="50" data-marquee="target" class="style-584">
            <div role="listitem" class="style-586">...</div>
            ...
        </div>
        <div data-duration="50" data-marquee="target" class="style-650">
             <div role="listitem" class="style-652">...</div>
             ...
        </div>
    </div>
</section>
Component & Styling Breakdown:

Section Styling (.style-572): The section has a vibrant pink background-color: rgb(240, 168, 250);, which makes it stand out.

Testimonial Card (.style-586):

background-color: rgb(245, 241, 236);: A warm, off-white background.

border-radius: 20px;: Rounded corners.

box-shadow: rgb(211, 134, 222) 0px 4px 5px 0px;: A subtle, colored drop shadow that adds depth.

padding: 60px 50px;: Very generous padding, giving the content ample space.

The quote itself (.style-587) has a large font-size: 28px;.

Author Information (.style-588):

The author's avatar (.style-589) is made circular by applying border-radius: 100%; to its container and setting overflow: hidden;.

Footer
The footer is a well-organized, multi-column layout providing access to key pages, company information, and a newsletter signup form.

HTML Structure:
The footer uses display: grid (.style-809) with four defined columns to neatly arrange the content.

HTML

<div class="style-807">
    <div class="style-808">
        <div class="style-809">
            <div class="style-810">...</div>
            <div class="style-833">...</div>
            <div class="style-858">...</div>
            <div class="style-884">...</div>
        </div>
    </div>
</div>
Component & Styling Breakdown:

Layout (.style-809):

display: grid; grid-template-columns: 303px 303px 303px 303px;: Creates four equal-width columns.

The first column (newsletter) is visually separated and occupies a different part of the grid in the original design.

Link Lists (<ul>):

The lists of links (.style-837, .style-862, etc.) have list-style: outside none none; and padding-left: 0px; to remove default bullet points and indentation.

Footer links (.style-839, etc.) are styled with a muted color, color: rgb(115, 107, 107);, and have a transition for a subtle hover effect.

Newsletter Form (.style-819):

The email input (.style-820) and the submit button (.style-821) are styled to appear as a single, cohesive unit. This is achieved by giving the input a border-radius only on the left (30px 0px 0px 30px) and the button a border-radius only on the right (0px 30px 30px 0px).

Social Media Icons (.style-826):

Each icon is an <a> tag with a background-color: rgb(45, 35, 35); and border-radius: 100px; to create the circular shape. They are flex containers that center the SVG icon within.

Sitemap: The footer includes a sitemap with links to all the main pages of the website.

Newsletter Signup: A prominent newsletter signup form encourages users to stay connected with the company.

Social Media Links: Links to Homerun's social media profiles are included, allowing users to connect with the company on other platforms.

Animations and Effects
The website uses subtle animations and effects to enhance the user experience and create a more dynamic and engaging feel.

Hover Effects: Buttons and links have hover effects that provide visual feedback to the user and make the interface feel more responsive.

Marquee Scroller: The testimonial section uses a marquee scroller to display a large number of testimonials in a relatively small space.

Fade-in and Slide-in Effects: Elements on the page may use fade-in or slide-in animations as the user scrolls, which adds a touch of elegance and sophistication to the design.

Global Styles & Page Structure
The foundation of the page is set in the <body> tag, establishing the default typography and layout properties for the entire site.

HTML Structure (<body> tag):

<body style="cursor: auto; clear: none; flex-flow: column nowrap; align-items: center; display: flex; color: rgb(74, 62, 62); background-color: rgb(255, 255, 255); flex-direction: column; font-family: 'GT America', sans-serif; min-height: 100%; margin: 0px; font-size: 14px; line-height: 20px; box-sizing: border-box;">
...
</body>

CSS Analysis (.style-0):

display: flex; flex-direction: column; align-items: center;: This establishes a vertical flexbox layout for the entire page, centering the main content column. This is a modern and effective way to manage the primary layout.

font-family: 'GT America', sans-serif;: Sets the default font for the entire page.

color: rgb(74, 62, 62);: A dark, warm grey for body text, which is softer than pure black and enhances readability.

background-color: rgb(255, 255, 255);: A standard white background.

font-size: 14px; line-height: 20px;: Default text size and line height for base-level text.

box-sizing: border-box;: Applied universally, this is a crucial best practice that simplifies layout calculations by including padding and border in the element's total width and height.

Navigation Bar
The navigation is a clean, sticky header that remains visible on scroll. It contains the brand logo, primary navigation links, a dropdown menu for secondary resources, and prominent call-to-action buttons.

HTML Structure:
The navigation bar is a div element with position: sticky and a high z-index to keep it on top of other content. It uses a flexbox layout to space out its child elements.

<div theme="white" ... style="z-index: 999; ... position: sticky; top: 0px; ...">
    <div ... style="max-width: 1380px; justify-content: space-between; ... display: flex;">
        <a href="/" ...>...</a>
        <nav role="navigation" ...>
            <div data-nav-btn-container="" ...>
                <a href="/demo" ...>Book a demo</a>
                <a href="/trial" ...>Start free trial</a>
            </div>
        </nav>
    </div>
</div>

Component & Styling Breakdown:

Sticky Container (.style-4):

position: sticky; top: 0px;: This is the key to the navigation bar "sticking" to the top of the viewport when the user scrolls.

z-index: 999;: Ensures the navigation bar stays above other page content.

background-color: rgb(255, 255, 255);: The bar has a solid white background.

Navigation Links (<a> tags inside .style-31):

padding: 18px 12px;: Generous padding creates a large, clickable area.

font-size: 16px; font-weight: 400;: Clear and legible link text.

transition: background-color 0.3s;: A subtle transition effect on hover provides user feedback.

The "Organise your hiring" and "Manage your team" links include a styled SVG icon next to the text, contained within a flex div.

"Resources" Dropdown (.style-57):

This is a div that likely has JavaScript attached to it to toggle the visibility of the nested <nav> menu on hover (indicated by data-hover="true").

The dropdown content (.style-64) is a grid layout, allowing for complex and organized content. It has a box-shadow and a border to visually separate it from the page content below.

Call-to-Action Buttons:

"Log in" Button: Styled as a "ghost" button.

border: 1px solid rgb(237, 235, 231);

background-color: rgba(0, 0, 0, 0); (transparent)

color: rgb(74, 62, 62);

"Book a demo" Button (.style-141): A solid, high-contrast button.

background-color: rgb(45, 35, 35); (dark charcoal)

color: rgb(255, 255, 255); (white text)

"Start free trial" Button (.style-142): The primary call-to-action, using the main brand color.

background-color: rgb(79, 117, 254); (brand blue)

color: rgb(255, 255, 255); (white text)

All buttons share a border-radius: 12px; for a modern, rounded look.

Hero Section
This section makes an immediate impact with a large, bold heading, a clear value proposition, and prominent calls to action, balanced by a visually appealing product image.

HTML Structure:
A two-column layout is achieved using display: flex on the container (.style-316). The left column (.style-317) contains the text content and CTAs, while the right holds the hero image.

<section class="style-313">
    <div class="style-316">
        <div class="style-317">
            <h1 class="style-319">Beautiful<br>HR tools for modern teams.</h1>
            <p class="style-321">Transform your hiring process...</p>
            <div class="style-322">...</div>
        </div>
        <img ... class="style-349" src="...">
    </div>
</section>

Component & Styling Breakdown:

Main Headline (<h1>, .style-319):

font-family: 'GT Walsheim', sans-serif;: This distinct, bold font is reserved for major headings.

font-size: 64px; line-height: 64px;: Creates a large, impactful title.

font-weight: 900;: A very heavy font weight for maximum emphasis.

letter-spacing: -1px;: Tight letter-spacing gives the headline a compact, modern feel.

Sub-headline (<p>, .style-321):

font-size: 22px; line-height: 30.8px;: A larger-than-body font size that is easy to read and complements the main headline.

color: rgb(74, 62, 62);: Uses the default body text color.

Social Proof (.style-330):

This section uses a display: grid layout to arrange three items in a row.

Each item is a flex container (.style-331, .style-339, .style-344) that aligns an icon and its text.

The star rating for Capterra is cleverly implemented with a background-image on a <strong> tag, showing five stars.

Hero Image (<img>, .style-349):

border-radius: 40px;: Large, rounded corners give the image a soft, modern look that matches the overall aesthetic.

Feature Sections
The "Organise your hiring" and "Manage your team" sections use a consistent, clean layout to present product features. A key UX element is the use of accordions to reveal more information on demand.

HTML Structure:
These sections use a two-column flex layout. One column contains the descriptive text and accordion menu, while the other contains a large product image.

<section class="style-425">
    <div class="style-426">
        <h2 class="style-431">Organise your hiring.</h2>
        <div class="style-432">...</div>
        <div class="style-435">
            <div data-w-id="..." class="style-436">
                 <div role="button" class="style-437">...</div>
                 <nav class="style-442">...</nav>
            </div>
        </div>
    </div>
    <div class="style-496">...</div>
</section>

Component & Styling Breakdown:

Section Layout (.style-425, .style-498):

These sections have a large gap: 110px between the text and image columns, creating ample white space.

They feature a high border-radius: 40px; and generous padding: 48px 64px;.

The "Manage your team" section uses a background-image: radial-gradient(...) for a subtle, sophisticated background texture.

Accordion Menu:

Each accordion item (.style-436, .style-509) is a self-contained unit with a border-radius: 10px;.

The trigger (.style-437) is a flex container that uses justify-content: space-between; to place the title on the left and the dropdown arrow on the right.

The expandable content area is a <nav> tag (.style-442). Its initial height: 0px; keeps it hidden. An interaction (likely JavaScript triggered by the data-w-id) would change the height to auto to reveal the content with a smooth transition.

The text inside the expanded accordion (.style-444) has a slightly smaller font size (18px) than the feature description, creating a clear hierarchy.

Testimonials Section
This section uses a dynamic, auto-scrolling marquee to display customer testimonials, building trust through social proof.

HTML Structure:
A flex container (.style-583) with overflow: hidden; acts as the viewport for the marquee. Inside, two identical divs (.style-584, .style-650) contain the list of testimonials. This duplication is a classic technique to create a seamless, infinite scrolling loop.

<section class="style-572">
    ...
    <div class="style-583">
        <div data-duration="50" data-marquee="target" class="style-584">
            <div role="listitem" class="style-586">...</div>
            ...
        </div>
        <div data-duration="50" data-marquee="target" class="style-650">
             <div role="listitem" class="style-652">...</div>
             ...
        </div>
    </div>
</section>

Component & Styling Breakdown:

Section Styling (.style-572): The section has a vibrant pink background-color: rgb(240, 168, 250);, which makes it stand out.

Testimonial Card (.style-586):

background-color: rgb(245, 241, 236);: A warm, off-white background.

border-radius: 20px;: Rounded corners.

box-shadow: rgb(211, 134, 222) 0px 4px 5px 0px;: A subtle, colored drop shadow that adds depth.

padding: 60px 50px;: Very generous padding, giving the content ample space.

The quote itself (.style-587) has a large font-size: 28px;.

Author Information (.style-588):

The author's avatar (.style-589) is made circular by applying border-radius: 100%; to its container and setting overflow: hidden;.

Footer
The footer is a well-organized, multi-column layout providing access to key pages, company information, and a newsletter signup form.

HTML Structure:
The footer uses display: grid (.style-809) with four defined columns to neatly arrange the content.

<div class="style-807">
    <div class="style-808">
        <div class="style-809">
            <div class="style-810">...</div>
            <div class="style-833">...</div>
            <div class="style-858">...</div>
            <div class="style-884">...</div>
        </div>
    </div>
</div>

Component & Styling Breakdown:

Layout (.style-809):

display: grid; grid-template-columns: 303px 303px 303px 303px;: Creates four equal-width columns.

The first column (newsletter) is visually separated and occupies a different part of the grid in the original design.

Link Lists (<ul>):

The lists of links (.style-837, .style-862, etc.) have list-style: outside none none; and padding-left: 0px; to remove default bullet points and indentation.

Footer links (.style-839, etc.) are styled with a muted color, color: rgb(115, 107, 107);, and have a transition for a subtle hover effect.

Newsletter Form (.style-819):

The email input (.style-820) and the submit button (.style-821) are styled to appear as a single, cohesive unit. This is achieved by giving the input a border-radius only on the left (30px 0px 0px 30px) and the button a border-radius only on the right (0px 30px 30px 0px).

Social Media Icons (.style-826):

Each icon is an <a> tag with a background-color: rgb(45, 35, 35); and border-radius: 100px; to create the circular shape. They are flex containers that center the SVG icon within.`
    },
    {
      id: 'cluely',
      name: 'Cluely',
      image: '/designinspoimages/cluely1.png',
      description: 'Elegant analytics dashboard with perfect data visualization',
      prompt: `Transform my current website design, CSS styling and overall frontend into the following strict and descriptive design and frontend specification document. Make sure to not change any copy, context or functionality we currently have in the website, this is solely a design UI/UX and frontend redesign.

IMPORTANT COMPONENT LIBRARY REQUIREMENT:
You MUST use shadcn/ui for all UI components to ensure accessibility, consistency, and modern design patterns. shadcn/ui is a collection of copy-and-paste components built on top of Radix UI primitives and styled with version 3 of Tailwind CSS.

Installation & Setup:
1. Install shadcn/ui CLI: npx shadcn-ui@latest init
2. Configure your components.json when prompted
3. Add components as needed: npx shadcn-ui@latest add [component-name]

Key Benefits:
- Fully accessible components built on Radix UI primitives
- Beautifully designed with consistent styling
- Customizable and themeable
- TypeScript support out of the box
- Copy-paste friendly - you own the code
- Works seamlessly with Tailwind CSS

Usage Guidelines:
- Use shadcn/ui components for buttons, forms, modals, navigation, cards, etc.
- Examples: npx shadcn-ui@latest add button card form dialog navigation-menu
- Import components from your components/ui directory
- Customize component variants using the built-in variant system
- Leverage the built-in dark mode support
- Follow the component composition patterns for complex UI elements

Always prioritize shadcn/ui components over custom implementations to maintain design consistency and accessibility standards.
This landing page exemplifies modern web design principles with a sophisticated, premium aesthetic. The design leverages a combination of subtle gradients, strategic use of negative space, and a refined color palette transitioning from light to dark themes. The implementation utilizes Next.js with React components, Tailwind CSS for utility-first styling, and demonstrates advanced CSS techniques including backdrop filters, complex gradients, and smooth transitions.
Key Design Characteristics:

Visual Hierarchy: Clear content flow with distinct sections
Color Strategy: Gradient-heavy design with blue/purple accent colors
Typography: Clean, modern sans-serif with careful weight variations
Spacing: Generous padding and margins creating breathing room
Interactive Elements: Subtle hover states and micro-animations
Responsive Design: Mobile-first approach with breakpoint-specific layouts


Section-by-Section Breakdown
1. Navigation Header
Structure & Layout:

Fixed positioning with transparent background
Flexbox layout with three-part structure: logo (left), navigation links (center), CTA buttons (right)
Max-width constraint (76rem) with automatic centering
Responsive behavior: hamburger menu on mobile, full navigation on desktop

Styling Specifications:
css- Position: fixed, top-0, w-full, z-[12]
- Background: transparent with transition effects
- Padding: p-3 lg:p-2
- Typography: text-sm/6 (14px with 1.5 line height)
- Font-weight: 600 (semibold)
Interactive Elements:

Navigation links: border-b border-transparent hover:border-black (underline on hover)
CTA button: Black background with rounded-full, hover opacity change
Logo: SVG icon with size-4 (16px) paired with text

Color Palette:

Text: zinc-900 (#18181b)
CTA Background: black (#000000)
CTA Text: white (#ffffff)

2. Hero Section
Structure & Layout:

Relative positioning with extensive vertical padding
Centered text content with max-width constraints
Layered gradient background effects
Absolute positioned UI mockup at bottom

Background Design:
css- Primary gradient: bg-gradient-to-t from-blue-100 to-blue-200
- Multiple layered blur effects with increasing radius
- Clip-path polygon for organic shape overlay
- Shadow effects: rgba(173,216,255,0.5) with varying opacity
Typography:

Headline: text-4xl sm:text-7xl (responsive sizing)
Font-weight: 500 (medium)
Text-balance for optimal line breaks
Subheading: text-lg/6 lg:text-xl/6 with zinc-500 color

CTA Design:

Primary CTA: Black pill button with group hover effects
Icon integration with transition effects
Secondary link: Underlined text with hover color change
Platform-specific icons (Apple logo for Mac download)

Visual Effects:

Nested div containers creating concentric glow effects
Blur filters: blur-[1px] to blur-[4px] progressive blurring
Absolute positioned mockup with pointer-events-none

3. Feature Showcase Section
Structure & Layout:

Three-part layout: header + 2-column grid + full-width feature
Border-based design system with zinc-200 borders
Responsive grid: lg:grid-cols-2 with stack on mobile

Section Header:
css- Uppercase label: text-lg/10, font-base, text-zinc-500
- Main heading: text-4xl sm:text-5xl, font-medium
- Centered alignment with max-width constraint
Feature Cards:

Image-first design with text overlay at bottom
Blue accent bar: bg-[#0055FE] w-1 h-8.5
Typography hierarchy: h2 (text-2xl) + p (text-base, text-zinc-600)
Positioning: absolute text placement over images

Grid System:

Border integration: lg:border-y border-zinc-200 lg:divide-x
Consistent spacing with strategic negative margins
Pointer-events-none for non-interactive elements

4. Testimonial/Quote Section
Structure & Layout:

Simple centered layout with generous vertical padding
Border-x creating visual containment
Minimal design focusing on typography

Typography:

Quote: text-4xl sm:text-5xl with quotation marks
Font-weight: 500 (medium)
Strategic use of text-balance
CTA button maintaining consistent pill shape design

5. Dark Feature Section
Structure & Layout:

Full-width dark background: bg-[#181B20]
Three-column feature layout with alternating image placement
Consistent spacing: py-16 between features

Color Scheme:

Background: Dark charcoal (#181B20)
Text: White headings, zinc-300/400 for body text
Image backgrounds: bg-[#1D2025] with ring-1 ring-white/5

Component Pattern:
css- Grid: grid-cols-1 lg:grid-cols-5
- Text sections: col-span-2
- Image sections: col-span-3
- Alternating order with lg:order-1/2
Visual Treatment:

Rounded corners: rounded-[18px]
Subtle ring border for depth
Consistent image aspect ratios

6. Scrolling Feature Section
Structure & Layout:

Sticky sidebar pattern for desktop
Sequential content reveal on scroll
Mobile: Linear stack with inline images

Implementation Details:
css- Sidebar: md:sticky md:top-0 md:h-screen
- Content: will-change: transform (scroll optimization)
- Image transitions: opacity-based crossfade
7. Final CTA Section
Structure & Layout:

Full-screen hero-style section
Background image with overlay
Centered content with CTAs

Visual Effects:

Absolute positioned background image
Text overlay with high contrast
Maintained button consistency from hero

8. Footer
Structure & Layout:

Multi-level footer with black background
Grid-based link organization
Compliance badges and social links
Unique animated bottom border

Grid System:
css- Main grid: lg:grid-cols-4 for link sections
- Compliance section with flex layout
- Social icons in horizontal arrangement
Unique Elements:

Animated bottom stripes (increasing height)
Download link for brand assets
Status indicator with live system status
Comprehensive legal/compliance section

Technical Implementation Details
CSS Architecture

Utility-First: Extensive Tailwind CSS usage
Custom Properties: Specific brand colors (#0055FE, #181B20)
Responsive Utilities: Consistent lg: breakpoint usage
Advanced Techniques: backdrop-filter, clip-path, will-change

Animation & Transitions
css- Standard transition: transition duration-300
- Hover states: opacity changes, scale transforms
- Group hover patterns for complex interactions
- Spring easing: ease-spring custom timing function
Typography System

Font Family: System font stack (implicit)
Size Scale: text-xs through text-7xl
Line Height: Tailwind's relaxed scale (/6 ratios)
Weight Scale: 400 (normal) to 600 (semibold)

Color Palette
Primary Blues: blue-100, blue-200, #0055FE
Neutrals: zinc-200, zinc-300, zinc-400, zinc-500, zinc-600, zinc-900
Dark Theme: #181B20, #1D2025
Accents: Green-500 (status indicators)
Spacing System

Consistent use of Tailwind spacing scale
Section padding: py-32 to py-60
Component spacing: p-8, mt-8, gap-8
Micro-spacing: mt-2, gap-2 for tight layouts

Component Patterns

Pill Buttons: rounded-full, px-6/7.5, py-2.5/3
Cards: Border-based with absolute positioning
Icons: Consistent sizing with size-4/6 classes
Links: Underline on hover pattern
Sections: Max-width constraints with mx-auto

This design system demonstrates exceptional attention to detail with its layered visual effects, consistent component patterns, and sophisticated use of modern CSS capabilities. The implementation showcases best practices in responsive design, performance optimization, and maintainable code structure.


Global Design System
Typography Foundation
Font Stack:
cssfont-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
/* Custom font: Aeonik (loaded externally) */
Font Sizes & Line Heights:
css/* Text Size Classes Used */
text-xs: 12px
text-sm: 14px
text-base: 16px
text-lg: 18px
text-xl: 20px
text-2xl: 24px
text-3xl: 30px
text-4xl: 36px
text-5xl: 48px
text-6xl: 60px
text-7xl: 72px

/* Line Height Ratios */
/6: 1.5 line-height ratio
/10: 2.5 line-height ratio
leading-5: 1.25rem
leading-6: 1.5rem
Font Weights:
cssfont-base: 400
font-medium: 500
font-semibold: 600
Color Palette
Primary Colors:
css/* Blues */
blue-100: #dbeafe
blue-200: #bfdbfe
blue-600: #2563eb
#0055FE: Custom brand blue

/* Neutrals */
zinc-200: #e4e4e7
zinc-300: #d4d4d8
zinc-400: #a1a1aa
zinc-500: #71717a
zinc-600: #52525b
zinc-700: #3f3f46
zinc-900: #18181b

/* Dark Theme */
#181B20: Primary dark background
#1D2025: Secondary dark background
#282828: Tertiary dark

/* Accent Colors */
green-500: #22c55e (status indicators)
sky-50: #f0f9ff (hover states)
sky-300: #7dd3fc
Spacing System
css/* Padding/Margin Scale */
0: 0px
0.5: 2px
1: 4px
2: 8px
2.5: 10px
3: 12px
4: 16px
5: 20px
6: 24px
8: 32px
10: 40px
12: 48px
16: 64px
18: 72px
20: 80px
26: 104px
32: 128px
36: 144px
48: 192px
60: 240px
65: 260px
Breakpoint System
css/* Responsive Breakpoints */
sm: 640px
md: 768px
lg: 1024px
xl: 1280px
2xl: 1536px

Navigation Header
HTML Structure:
html<div class="mx-auto fixed flex left-0 right-0 top-0 w-full z-[12] items-center justify-between max-w-[76rem] select-none transition-all duration-300 ease-spring bg-transparent lg:mt-5 translate-y-0">
  <header class="relative isolate w-full">
    <nav class="flex items-center justify-between p-3 lg:p-2">
      <!-- Logo Section -->
      <div class="flex lg:flex-1 ml-2 -mt-0.5">
        <a class="flex items-center gap-x-0.5 transition border-b border-transparent hover:border-black">
          <svg class="transition size-4 mt-1">...</svg>
          <span class="mt-1.5 text-lg font-medium transition">Cluely</span>
        </a>
      </div>
      
      <!-- Mobile Menu -->
      <div class="flex lg:hidden">
        <button class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-zinc-700">
          <span class="sr-only">Open main menu</span>
          <svg class="size-5.5 mr-1 outline-none">...</svg>
        </button>
      </div>
      
      <!-- Desktop Navigation -->
      <div class="hidden lg:flex lg:gap-x-12 -ml-12">
        <a class="text-sm/6 font-semibold text-zinc-900 transition border-b border-transparent hover:border-black">Use cases</a>
        <!-- More links... -->
      </div>
      
      <!-- CTA Section -->
      <div class="hidden lg:flex lg:flex-1 lg:justify-end items-center gap-x-5">
        <a class="text-sm/6 font-semibold text-zinc-900 transition border-b border-transparent hover:border-black">Log in</a>
        <a class="flex px-2 py-1 gap-x-1 text-sm/6 font-semibold rounded-full text-white bg-black hover:opacity-86">
          Sign up
          <svg width="20" height="21">...</svg>
        </a>
      </div>
    </nav>
  </header>
</div>
Detailed Styling Breakdown:
Container Styles:
css/* Positioning & Layout */
position: fixed
left: 0
right: 0
top: 0
z-index: 12
width: 100%
max-width: 76rem (1216px)
margin: 0 auto

/* Responsive Behavior */
@media (min-width: 1024px) {
  margin-top: 20px
}

/* Transitions */
transition: all 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)
background: transparent
transform: translateY(0)
Logo Styling:
css/* Container */
display: flex
align-items: center
gap: 2px
margin-left: 8px
margin-top: -2px

/* Icon */
width: 16px
height: 16px
margin-top: 4px
transition: all 150ms

/* Text */
font-size: 18px
font-weight: 500
margin-top: 6px
Navigation Links:
css/* Base Style */
font-size: 14px
line-height: 1.5
font-weight: 600
color: #18181b
border-bottom: 1px solid transparent
transition: all 150ms

/* Hover State */
&:hover {
  border-color: black
}
CTA Button:
css/* Layout */
display: flex
align-items: center
gap: 4px
padding: 4px 8px

/* Style */
background: black
color: white
border-radius: 9999px
font-size: 14px
font-weight: 600

/* Hover */
&:hover {
  opacity: 0.86
}

/* Arrow Icon */
width: 20px
height: 21px
fill: #282828

Hero Section
HTML Structure:
html<div class="relative py-32 sm:py-48 lg:py-60 select-none bg-gradient-to-t from-blue-100 to-blue-200">
  <!-- Background Effects -->
  <div class="hidden lg:block">
    <div class="absolute inset-0 overflow-hidden contain-paint">
      <div class="absolute inset-[0.5rem] rounded-[12rem] bg-white/2 shadow-[0_0_40px_rgba(173,216,255,0.5)] blur-[4px]"></div>
      <div class="absolute inset-[3rem] rounded-[12rem] bg-white/2 shadow-[0_0_30px_rgba(173,216,255,0.4)] blur-[3px]"></div>
      <div class="absolute inset-[6rem] rounded-[12rem] bg-white/2 shadow-[0_0_20px_rgba(173,216,255,0.3)] blur-[2px]"></div>
      <div class="absolute inset-[10rem] rounded-[8rem] bg-white/2 shadow-[inset_0_0_30px_rgba(255,255,255,0.4)] blur-[1px]"></div>
    </div>
  </div>
  
  <!-- Organic Shape Overlay -->
  <div aria-hidden="true" class="absolute inset-x-0 top-1/2 transform -translate-y-1/2 overflow-hidden blur-3xl">
    <div style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)"></div>
  </div>
  
  <!-- Content -->
  <div class="relative text-center z-[1] mx-auto max-w-3xl lg:pb-16">
    <h1 class="text-4xl font-medium tracking-tight text-balance text-zinc-900 sm:text-7xl">
      AI that helps before you even ask.
    </h1>
    <p class="mx-auto max-w-xl mt-5 text-lg/6 lg:text-xl/6 font-medium text-balance text-zinc-500">
      Cluely uses your screen and audio to provide intelligence during meetings, sales calls, and everything on your computer.
    </p>
    
    <!-- CTAs -->
    <div class="grid items-center justify-center gap-y-2.5 mt-10">
      <a class="flex group items-center gap-x-2 rounded-full bg-black px-7.5 py-3 text-md font-semibold text-white shadow-xs outline-none hover:-translate-y-0.5 transition hover:scale-[100.5%] hover:bg-black/90">
        <svg class="mb-0.5 group-hover:text-sky-50 transition">...</svg>
        <span class="group-hover:text-sky-50 transition">Download for Mac</span>
      </a>
      <a class="text-sm/6 font-semibold text-zinc-900/60 hover:text-blue-600/80 underline">
        Download for Windows
      </a>
    </div>
  </div>
  
  <!-- UI Mockup -->
  <div class="hidden z-[11] lg:block absolute -bottom-[11.5rem] left-1/2 transform -translate-x-1/2 z-[1] max-w-2xl pointer-events-none">
    <img src="/_next/static/media/frame.e956ee74.webp" alt="cluely-ui" />
  </div>
</div>
Detailed Styling Breakdown:
Background Gradient:
cssbackground: linear-gradient(to top, #dbeafe, #bfdbfe)
Layered Glow Effects:
css/* Layer 1 - Outermost */
position: absolute
inset: 0.5rem (8px from all edges)
border-radius: 12rem (192px)
background: rgba(255, 255, 255, 0.02)
box-shadow: 0 0 40px rgba(173, 216, 255, 0.5)
filter: blur(4px)

/* Layer 2 */
inset: 3rem (48px)
box-shadow: 0 0 30px rgba(173, 216, 255, 0.4)
filter: blur(3px)

/* Layer 3 */
inset: 6rem (96px)
box-shadow: 0 0 20px rgba(173, 216, 255, 0.3)
filter: blur(2px)

/* Layer 4 - Innermost */
inset: 10rem (160px)
border-radius: 8rem (128px)
box-shadow: inset 0 0 30px rgba(255, 255, 255, 0.4)
filter: blur(1px)
Typography Specifications:
css/* Headline */
font-size: 36px (mobile) / 72px (desktop)
font-weight: 500
letter-spacing: -0.025em
text-wrap: balance
color: #18181b

/* Subheading */
font-size: 18px / 20px (desktop)
line-height: 1.5
font-weight: 500
color: #71717a
max-width: 36rem (576px)
margin-top: 20px
CTA Button Detailed:
css/* Primary Button */
display: flex
align-items: center
gap: 8px
padding: 12px 30px
background: black
color: white
border-radius: 9999px
font-weight: 600
box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05)
transition: all 150ms

/* Hover States */
&:hover {
  transform: translateY(-2px)
  scale: 1.005
  background: rgba(0, 0, 0, 0.9)
}

/* Group Hover Effects */
.group:hover svg,
.group:hover span {
  color: #f0f9ff
}

Feature Showcase Section
HTML Structure:
html<div class="mx-auto max-w-7xl pt-20 lg:pt-65 select-none lg:border-x border-zinc-200 overflow-hidden">
  <!-- Section Header -->
  <div class="mx-auto max-w-2xl sm:text-center px-5 lg:px-0">
    <h2 class="text-lg/10 font-base text-zinc-500 uppercase">The turning point of thought</h2>
    <p class="mt-2 text-4xl font-medium tracking-tight text-pretty text-black sm:text-5xl sm:text-balance">
      Cluely helps with anything it sees or hears.
    </p>
  </div>
  
  <!-- Two Column Grid -->
  <div class="grid lg:grid-cols-2 mt-20 mb-16 lg:mb-0 pointer-events-none lg:border-y border-zinc-200 lg:divide-x divide-zinc-200">
    <!-- Feature 1 -->
    <div class="relative">
      <img src="/_next/static/media/bento1.3b364800.png" alt="Sees what you see" />
      <div class="lg:absolute px-8 -mt-36 lg:mt-0 lg:pl-0 bottom-16 left-10 w-full lg:w-auto">
        <h2 class="text-2xl font-medium break-words">Sees what you see</h2>
        <p class="w-full lg:w-100 mt-3 text-base leading-5 text-zinc-600 break-words">
          Cluely sees and understands all the content on your screen — code, slides, questions, docs, dashboards.
        </p>
      </div>
      <div class="hidden lg:block absolute bg-[#0055FE] w-1 h-8.5 bottom-28.5 -left-[1px]"></div>
    </div>
    <!-- Feature 2 similar structure -->
  </div>
  
  <!-- Full Width Feature -->
  <div class="relative pointer-events-none lg:border-b border-zinc-200">
    <!-- Similar structure -->
  </div>
</div>
Detailed Styling Breakdown:
Container Specifications:
css/* Main Container */
max-width: 80rem (1280px)
margin: 0 auto
padding-top: 80px (mobile) / 260px (desktop)
overflow: hidden

/* Border System */
@media (min-width: 1024px) {
  border-left: 1px solid #e4e4e7
  border-right: 1px solid #e4e4e7
}
Section Header:
css/* Label */
font-size: 18px
line-height: 2.5
text-transform: uppercase
color: #71717a
font-weight: 400

/* Title */
font-size: 36px (mobile) / 48px (desktop)
font-weight: 500
letter-spacing: -0.025em
text-wrap: pretty / balance
color: black
margin-top: 8px
Feature Card Layout:
css/* Grid Container */
display: grid
grid-template-columns: 1fr (mobile) / repeat(2, 1fr) (desktop)
margin-top: 80px
margin-bottom: 64px (mobile) / 0 (desktop)

/* Borders */
@media (min-width: 1024px) {
  border-top: 1px solid #e4e4e7
  border-bottom: 1px solid #e4e4e7
  
  /* Divider between columns */
  > * + * {
    border-left: 1px solid #e4e4e7
  }
}
Feature Content Positioning:
css/* Text Container */
position: absolute (desktop) / relative (mobile)
padding: 32px
margin-top: -144px (mobile adjustment)
bottom: 64px
left: 40px
width: 100% (mobile) / auto (desktop)

/* Typography */
h2 {
  font-size: 24px
  font-weight: 500
  word-break: break-words
}

p {
  width: 100% (mobile) / 400px (desktop)
  margin-top: 12px
  font-size: 16px
  line-height: 1.25
  color: #52525b
}
Blue Accent Bar:
cssposition: absolute
background: #0055FE
width: 4px
height: 34px
bottom: 114px
left: -1px

Quote Section
Detailed Styling:
css/* Container */
max-width: 80rem
margin: 0 auto
padding: 144px 0
text-align: center
border-left: 1px solid #e4e4e7
border-right: 1px solid #e4e4e7

/* Quote Text */
font-size: 36px (mobile) / 48px (desktop)
font-weight: 500
letter-spacing: -0.025em
text-wrap: pretty / balance
color: black

/* CTA Link */
display: flex
align-items: center
gap: 8px
width: 160px
margin: 32px auto 0
padding: 10px 24px
background: black
color: white
border-radius: 9999px
font-size: 18px
font-weight: 600
transition: all 150ms

&:hover {
  transform: translateY(-2px) scale(1.005)
  background: rgba(0, 0, 0, 0.9)
}

Dark Feature Section
HTML Structure:
html<div class="bg-[#181B20]">
  <div class="mx-auto max-w-6xl sm:text-center pt-18 lg:pt-26">
    <!-- Header -->
    <div class="max-w-80 mx-auto lg:max-w-6xl">
      <h2 class="mt-2 text-3xl lg:text-5xl font-medium tracking-tight text-white sm:text-5xl sm:text-balance">
        Undetectable by design.
      </h2>
      <p class="mt-4 text-lg lg:text-xl max-w-lg mx-auto font-base tracking-tight text-zinc-400 leading-6">
        No bots in the room. No Zoom guests. No screen-share trails. Works on everything.
      </p>
    </div>
    
    <!-- Logo Grid -->
    <img src="/_next/static/media/works.2bbce57b.png" class="h-46 lg:h-50 mt-8 mx-auto" />
    
    <!-- Features -->
    <div class="mt-12 select-none pointer-events-none">
      <!-- Feature Pattern -->
      <div class="py-16 mx-auto grid grid-cols-1 gap-x-12 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-5 items-center">
        <div class="lg:pr-8 pl-8 lg:pl-0 col-span-2">
          <h2 class="text-2xl font-medium tracking-tight text-white mb-4">Doesn't join meetings</h2>
          <p class="text-base text-zinc-300">Description text...</p>
        </div>
        <div class="lg:order-2 col-span-3 p-8 lg:p-0 -mt-14 lg:mt-0">
          <img class="bg-[#1D2025] rounded-[18px] w-full max-w-none ring-1 ring-white/5" />
        </div>
      </div>
    </div>
  </div>
</div>
Detailed Dark Theme Styling:
Background Colors:
css/* Primary Background */
background: #181B20

/* Secondary Background (images) */
background: #1D2025

/* Ring Border */
box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05)
Typography in Dark Mode:
css/* Headings */
color: white
font-size: 24px (feature) / 30px-48px (main)
font-weight: 500
letter-spacing: -0.025em

/* Body Text */
color: #d4d4d8 (zinc-300)
font-size: 16px
line-height: 1.5

/* Subtle Text */
color: #a1a1aa (zinc-400)
Grid Layout Pattern:
css/* Container */
display: grid
grid-template-columns: 1fr (mobile) / repeat(5, 1fr) (desktop)
gap: 48px (x-axis) / 64px-80px (y-axis)
padding: 64px 0
align-items: center

/* Text Column */
grid-column: span 2
padding-right: 32px (desktop)
padding-left: 32px (mobile)

/* Image Column */
grid-column: span 3
padding: 32px (mobile) / 0 (desktop)
margin-top: -56px (mobile) / 0 (desktop)

/* Alternating Layout */
.lg:order-1 (image first on desktop)
.lg:order-2 (text first on desktop)

Scrolling Feature Section
HTML Structure:
html<div class="mx-auto max-w-7xl pt-32 select-none pointer-events-none border-x border-zinc-200">
  <h1 class="text-4xl font-medium max-w-md ml-8 lg:-mb-12">
    Three ways Cluely changes how you think.
  </h1>
  
  <div class="flex flex-col md:flex-row">
    <!-- Sticky Image Container -->
    <div class="w-full md:w-3/5 md:sticky md:top-0 md:h-screen flex items-center justify-center">
      <div class="relative w-full h-full flex items-center justify-center">
        <!-- Multiple images with opacity transitions -->
        <div class="absolute w-full h-full flex items-center justify-center transition-opacity duration-500 opacity-100 z-10">
          <img class="max-w-full max-h-full object-contain rounded-r-[18px] border-r border-y border-zinc-200/70" />
        </div>
        <!-- More images... -->
      </div>
    </div>
    
    <!-- Scrolling Content -->
    <div class="w-full md:w-1/2 md:ml-auto">
      <div style="will-change: transform;">
        <div class="max-w-xl">
          <div class="p-8 lg:pl-20">
            <h2 class="text-5xl font-medium mb-4">Meetings</h2>
            <p class="text-xl text-zinc-600 max-w-94">Description...</p>
          </div>
          <div class="h-80 md:hidden mt-6 flex items-center justify-center">
            <img class="max-h-full object-contain" />
          </div>
        </div>
      </div>
      <!-- More sections... -->
    </div>
  </div>
</div>
Sticky Scroll Implementation:
css/* Sticky Container */
@media (min-width: 768px) {
  position: sticky
  top: 0
  height: 100vh
  width: 60%
}

/* Image Transitions */
.image-container {
  position: absolute
  width: 100%
  height: 100%
  display: flex
  align-items: center
  justify-content: center
  transition: opacity 500ms
  
  /* Active state */
  &.active {
    opacity: 1
    z-index: 10
  }
  
  /* Inactive state */
  &:not(.active) {
    opacity: 0
    z-index: 0
  }
}

/* Content Optimization */
will-change: transform

Final CTA Section
Detailed Styling:
css/* Container */
position: relative
max-width: 80rem
margin: 40px auto 0 (mobile) / 0 auto (desktop)
padding: 240px 0
border: 1px solid #e4e4e7

/* Background Image */
position: absolute
height: 100%
width: 100%
inset: 0
object-fit: cover
z-index: 0

/* Content */
position: relative
z-index: 1
margin-left: 32px (mobile) / 0 (desktop)
text-align: left (mobile) / center (desktop)

/* Typography */
.label {
  text-transform: uppercase
  color: #71717a
  letter-spacing: -0.025em
  font-size: 18px
}

.heading {
  margin-top: 8px
  font-size: 48px (mobile) / 60px (desktop)
  font-weight: 500
  letter-spacing: -0.025em
  text-wrap: balance
  color: #18181b
}

Footer
HTML Structure:
html<footer class="bg-black text-white select-none md:h-screen flex flex-col justify-between">
  <!-- Main Content -->
  <div class="w-full flex flex-col items-center justify-center px-4 pt-8">
    <div class="w-full max-w-[1000px]">
      <!-- Logo Section -->
      <div class="grid grid-cols-1 items-end gap-8 font-mono text-sm lg:grid-cols-[1fr_3fr_346px] lg:gap-0">
        <div class="flex flex-col justify-start space-y-4 lg:space-y-8 mt-4 mb-8">
          <div class="size-16 flex items-center justify-center">
            <svg class="transition mt-1 size-16">...</svg>
          </div>
          <a class="text-zinc-400" download="cluely-logo.svg">└ cluely-logo.svg</a>
        </div>
      </div>
      
      <!-- Links Grid -->
      <div class="grid grid-cols-2 gap-8 lg:gap-16 text-sm lg:grid-cols-4">
        <ul class="space-y-3">
          <li class="font-mono text-zinc-400 text-xs uppercase tracking-wider">GET STARTED</li>
          <li><a class="text-zinc-300 hover:text-white transition-colors">Sign up</a></li>
          <!-- More links... -->
        </ul>
        <!-- More columns... -->
      </div>
      
      <!-- Compliance Section -->
      <div class="inline-flex flex-col space-y-3.5">
        <div class="flex space-x-8 pt-8">
          <!-- Compliance badges -->
        </div>
      </div>
      
      <!-- Bottom Section -->
      <div class="flex flex-col justify-between gap-8 lg:flex-row lg:gap-0 border-t border-dashed border-zinc-700 mt-6 pt-5">
        <!-- Status Links -->
        <div class="grid lg:grid-cols-3 space-y-1 text-xs">
          <a class="group flex items-center space-x-2 py-1 text-white hover:text-zinc-300">
            <span class="text-green-500 group-hover:text-sky-300">
              <svg width="10" height="10">...</svg>
            </span>
            <span>Status: All Systems Operational</span>
          </a>
          <!-- More links... -->
        </div>
        
        <!-- Social Icons -->
        <div class="flex space-x-2 text-white">
          <!-- Social links -->
        </div>
      </div>
    </div>
  </div>
  
  <!-- Animated Bottom Border -->
  <div aria-hidden="true" style="overflow: hidden; height: 200px">
    <div style="margin-top: 0px">
      <div style="height: 1px; background-color: #ffffff; transition: transform 0.1s ease; will-change: transform; margin-top: -2px"></div>
      <!-- More lines with increasing height... -->
    </div>
  </div>
</footer>
Footer Styling Details:
Layout Structure:
css/* Container */
background: black
color: white
display: flex
flex-direction: column
justify-content: space-between
min-height: 100vh (desktop)

/* Content Wrapper */
max-width: 1000px
margin: 0 auto
padding: 32px 16px
Typography System:
css/* Section Headers */
font-family: monospace
font-size: 12px
text-transform: uppercase
letter-spacing: 0.1em
color: #a1a1aa

/* Links */
font-size: 14px
color: #d4d4d8
transition: color 150ms

&:hover {
  color: white
}

/* Status Indicators */
font-size: 12px
display: flex
align-items: center
gap: 8px

/* Icon Color Transitions */
.group:hover .icon {
  color: #7dd3fc (sky-300)
}
Animated Bottom Effect:
css/* Container */
overflow: hidden
height: 200px

/* Individual Lines */
@for $i from 1 through 23 {
  .line-#{$i} {
    height: #{$i}px
    background-color: white
    transition: transform 100ms ease
    will-change: transform
    margin-top: -2px
  }
}

Cookie Banner
Detailed Implementation:
css/* Container */
position: fixed
bottom: 20px
left: 50%
transform: translateX(-50%) translateX(-450px) /* Off-screen initially */
max-width: 900px
width: calc(100vw - 40px)
padding: 12px 20px
background: white
border-radius: 34px
box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08)
backdrop-filter: blur(8px)
z-index: 999999
transition: transform 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms

/* Layout */
display: flex
align-items: center
gap: 16px
flex-wrap: nowrap

/* Icon Container */
flex-shrink: 0
width: 20px
height: 20px

/* Text Content */
flex: 1 1 0%
min-width: 200px
font-size: 12px
line-height: 1.3
color: #6b7280

/* Buttons Container */
display: flex
gap: 6px
flex-shrink: 0

/* Button Styles */
.reject-button {
  background: #f9fafb
  color: #6b7280
  padding: 8px 16px
  font-size: 12px
  font-weight: 500
  border-radius: 17px
  min-height: 32px
}

.accept-button {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)
  color: white
  padding: 8px 20px
  font-size: 12px
  font-weight: 500
  border-radius: 17px
  min-height: 32px
}

/* Progress Bar */
position: absolute
bottom: 0
left: 0
right: 0
height: 3px
background: rgba(0, 0, 0, 0.1)
border-radius: 0 0 50px 50px
overflow: hidden
opacity: 0
transition: opacity 300ms

.progress-fill {
  height: 3px
  background: #3b82f6
  width: 0%
  transition: width linear
  border-radius: 0 0 50px 50px
}

Animation & Interaction Patterns
Global Transition Patterns:
css/* Standard Transition */
transition: all 150ms ease

/* Spring Easing */
transition: all 300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)

/* Hover Lift Effect */
&:hover {
  transform: translateY(-2px)
  scale: 1.005
}

/* Opacity Transitions */
transition: opacity 500ms ease

/* Color Transitions */
transition: color 150ms, background-color 150ms
Interactive States:
css/* Link Underline Pattern */
border-bottom: 1px solid transparent
transition: border-color 150ms

&:hover {
  border-color: currentColor
}

/* Group Hover Pattern */
.group:hover .group-hover:text-sky-50 {
  color: #f0f9ff
}

/* Scale Animations */
.scale-animation {
  transform: scale(1);
  transition: transform 150ms;
}

.scale-animation:hover {
  transform: scale(1.005);
}

/* Pointer Events Control */
.pointer-events-none {
  pointer-events: none; /* For non-interactive decorative elements */
}

/* Performance Optimizations */
/* Will-change for scroll animations */
.will-change-transform {
  will-change: transform;
}

/* Contain paint for performance */
.contain-paint {
  contain: paint;
}

/* Select-none for non-selectable UI */
user-select: none
- Fixed positioning with transparent background
- Flexbox layout with three-part structure: logo (left), navigation links (center), CTA buttons (right)
- Max-width constraint (76rem) with automatic centering
- Responsive behavior: hamburger menu on mobile, full navigation on desktop

Styling Specifications:
- Position: fixed, top-0, w-full, z-[12]
- Background: transparent with transition effects
- Padding: p-3 lg:p-2
- Typography: text-sm/6 (14px with 1.5 line height)
- Font-weight: 600 (semibold)

Interactive Elements:
- Navigation links: border-b border-transparent hover:border-black (underline on hover)
- CTA button: Black background with rounded-full, hover opacity change
- Logo: SVG icon with size-4 (16px) paired with text

Color Palette:
- Text: zinc-900 (#18181b)
- CTA Background: black (#000000)
- CTA Text: white (#ffffff)

2. Hero Section
Layout Architecture
.hero-section {
  padding-top: 144px;
  padding-bottom: 125px;
  background-image: url('texture-headline.webp');
  background-position: 100% -33%;
  background-size: 95.5%;
}

.hero-content {
  display: flex;
  gap: 34.6px;
  max-width: 1240px;
  margin: 0 auto;
}

Typography Hierarchy
Headline:
- Font: "Ggsans" (custom font)
- Size: 48px
- Line-height: 1.2
- Color: #FFFFFF
- Text-transform: uppercase for emphasis

Subheading:
- Size: 20px
- Line-height: 1.4
- Color: rgba(255, 255, 255, 0.8)
- Max-width: 491px for optimal readability

Visual Elements
- Background texture: Subtle gradient mesh overlay
- Floating decorative elements: Star shapes with parallax effect
- Device mockups: 3D perspective transforms
- Character illustrations: Positioned absolutely with animation

CTA Buttons
.cta-button {
  padding: 16px 32px;
  border-radius: 28px;
  font-size: 20px;
  font-weight: 500;
  background-color: #FFFFFF;
  color: #000000;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

3. Feature Sections
Grid-Based Layout
.feature-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 120px;
  align-items: center;
  padding: 120px 0;
}

/* Alternating layout */
.feature-section:nth-child(even) {
  direction: rtl;
}

Content Cards
- Background: Semi-transparent with blur effect
- Border-radius: 24px
- Padding: 40px
- Shadow: Multi-layered for depth
box-shadow: 
  0 0 0 1px rgba(255, 255, 255, 0.1),
  0 8px 32px rgba(0, 0, 0, 0.2),
  0 24px 64px rgba(0, 0, 0, 0.1);

Media Presentation
Screenshot containers:
- Aspect ratio: 16:9
- Border-radius: 16px
- Overflow: hidden for clean edges

- Device frames: Custom SVG overlays
- Hover effects: Scale transform on interaction

4. Animation System
Decorative Elements
@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(180deg); }
}

.floating-element {
  animation: float 6s ease-in-out infinite;
  animation-delay: var(--delay);
}

Scroll-Triggered Animations
- Intersection Observer implementation for performance
- Staggered delays for sequential reveals
- Transform origins carefully set for natural motion

Micro-Interactions
.interactive-element {
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.interactive-element:active {
  transform: scale(0.95);
}

5. Typography System
Font Stack
--font-primary: "Ggsans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
--font-display: "ABC Ginto Normal", var(--font-primary);
--font-mono: "Consolas", "Monaco", monospace;

Type Scale
- Display: 56px / 1.1 line-height
- Headline: 48px / 1.2 line-height
- Title: 32px / 1.3 line-height
- Body Large: 20px / 1.5 line-height
- Body: 16px / 1.6 line-height
- Caption: 14px / 1.4 line-height

Text Styling
.text-gradient {
  background: linear-gradient(135deg, #5865F2 0%, #EB459E 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

6. Color System
Primary Palette
:root {
  --color-primary: #5865F2;     /* Brand Purple */
  --color-secondary: #EB459E;   /* Brand Pink */
  --color-tertiary: #57F287;    /* Success Green */
  --color-danger: #ED4245;      /* Error Red */
  --color-warning: #FEE75C;     /* Warning Yellow */
}

Neutral Scale
:root {
  --gray-900: #0c0e10;  /* Deepest black */
  --gray-800: #1a1c1f;  /* Background dark */
  --gray-700: #2b2d31;  /* Surface dark */
  --gray-600: #313338;  /* Surface light */
  --gray-500: #41434a;  /* Border dark */
  --gray-400: #686a71;  /* Text muted */
  --gray-300: #9d9fa6;  /* Text secondary */
  --gray-200: #b5bac1;  /* Text primary */
  --gray-100: #dbdee1;  /* Border light */
  --gray-50: #f2f3f5;   /* Surface bright */
}

7. Responsive Design System
Breakpoint Strategy
/* Mobile First Approach */
@media (min-width: 640px) { /* Tablet */ }
@media (min-width: 1024px) { /* Desktop */ }
@media (min-width: 1280px) { /* Wide */ }
@media (min-width: 1536px) { /* Ultra-wide */ }

Fluid Typography
.fluid-text {
  font-size: clamp(32px, 5vw, 56px);
  line-height: clamp(1.2, 1.1 + 0.1vw, 1.1);
}

Container Queries
.adaptive-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: clamp(20px, 3vw, 40px);
}

8. Component Library
Button Variants
/* Primary Button */
.btn-primary {
  background: var(--color-primary);
  color: white;
  padding: 12px 24px;
  border-radius: 24px;
  font-weight: 500;
  box-shadow: 0 4px 14px rgba(88, 101, 242, 0.3);
}

/* Secondary Button */
.btn-secondary {
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
}

/* Icon Button */
.btn-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

Card Components
.card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.3);
  border-color: rgba(255, 255, 255, 0.2);
}

9. Footer Design
Structure
.footer {
  background: var(--gray-900);
  padding: 80px 0 24px;
  position: relative;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr repeat(4, 1fr);
  gap: 40px;
  max-width: 1240px;
  margin: 0 auto;
}

Social Links
.social-links {
  display: flex;
  gap: 24px;
}

.social-link {
  width: 32px;
  height: 32px;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.social-link:hover {
  opacity: 1;
}

10. Performance Optimizations
Image Loading
<img 
  loading="lazy"
  srcset="image-500w.webp 500w,
          image-800w.webp 800w,
          image-1200w.webp 1200w"
  sizes="(max-width: 640px) 100vw, 
         (max-width: 1024px) 50vw, 
         33vw"
  alt="Description"
/>

CSS Architecture
/* Critical CSS inlined */
/* Non-critical CSS loaded asynchronously */
/* Component-specific CSS code-split */

Animation Performance
.will-animate {
  will-change: transform, opacity;
}

.gpu-accelerated {
  transform: translateZ(0);
  backface-visibility: hidden;
}`
    },
    {
      id: 'airbnb',
      name: 'Airbnb',
      image: '/designinspoimages/airbnb1.png',
      description: 'Warm, welcoming design with seamless booking experience',
      prompt: `Transform my current website design, CSS styling and overall frontend into the following strict and descriptive design and frontend specification document. Make sure to not change any copy, context or functionality we currently have in the website, this is solely a design UI/UX and frontend redesign.

IMPORTANT COMPONENT LIBRARY REQUIREMENT:
You MUST use shadcn/ui for all UI components to ensure accessibility, consistency, and modern design patterns. shadcn/ui is a collection of copy-and-paste components built on top of Radix UI primitives and styled with version 3 of Tailwind CSS.

Installation & Setup:
1. Install shadcn/ui CLI: npx shadcn-ui@latest init
2. Configure your components.json when prompted
3. Add components as needed: npx shadcn-ui@latest add [component-name]

Key Benefits:
- Fully accessible components built on Radix UI primitives
- Beautifully designed with consistent styling
- Customizable and themeable
- TypeScript support out of the box
- Copy-paste friendly - you own the code
- Works seamlessly with Tailwind CSS

Usage Guidelines:
- Use shadcn/ui components for buttons, forms, modals, navigation, cards, etc.
- Examples: npx shadcn-ui@latest add button card form dialog navigation-menu
- Import components from your components/ui directory
- Customize component variants using the built-in variant system
- Leverage the built-in dark mode support
- Follow the component composition patterns for complex UI elements

Always prioritize shadcn/ui components over custom implementations to maintain design consistency and accessibility standards.

Executive Summary
This document provides a comprehensive analysis of a modern web application's design system, focusing on technical implementation details, visual standards, and component architecture. The design demonstrates a sophisticated approach to contemporary web design with emphasis on clean aesthetics, seamless user experience, and responsive functionality.
High-Level Design Overview
Design Philosophy
The website employs a minimalist, content-first design approach with sophisticated use of whitespace, subtle animations, and carefully crafted micro-interactions. The overall aesthetic prioritizes clarity, accessibility, and modern web standards.
Core Visual Principles

Clean, geometric layouts with consistent grid systems
Subtle elevation and depth through strategic use of shadows and borders
Progressive disclosure of information with smooth transitions
Mobile-first responsive design with adaptive breakpoints
Accessibility-focused color contrast and semantic structure

Technology Stack Evidence

CSS-in-JS implementation using Linaria styling system
Component-based architecture with data attributes for testing
Advanced CSS Grid and Flexbox for layouts
Custom CSS properties for design tokens and theming
Sophisticated animation system with cubic-bezier timing functions


Detailed Design System Analysis
Typography System
Primary Font Stack
cssfont-family: 'Airbnb Cereal VF', Circular, -apple-system, 'system-ui', Roboto, 'Helvetica Neue', sans-serif
Font Smoothing & Rendering
css-webkit-font-smoothing: antialiased
font-synthesis: none
Typography Scale

Body Text: 14px (base) with 20.02px line-height
Small Text: 12px with 16px line-height
Medium Text: 16px with 20px line-height
Large Text: 18px with 24px/28px line-height
Headings: Range from 22px to 72px with negative letter-spacing

Letter Spacing System
css/* Negative letter spacing for larger text */
--typography-title-small22px-letter-spacing: -0.01375rem
--typography-title-medium26px-letter-spacing: -0.01625rem
--typography-title-large32px-letter-spacing: -0.04rem
--typography-special-display-medium_60_68-letter-spacing: -0.15rem
--typography-special-display-medium_72_74-letter-spacing: -0.18rem
Font Weight Hierarchy

Regular: 400 (body text, descriptions)
Medium: 500 (buttons, emphasis)
Semibold: 600 (subheadings)
Bold: 700 (main headings, CTA buttons)

Color System
Primary Color Palette
css/* Base Colors */
--primary-text: rgb(34, 34, 34)    /* #222222 */
--background-primary: rgb(255, 255, 255)    /* #FFFFFF */
--background-secondary: rgb(247, 247, 247)  /* #F7F7F7 */

/* Brand Colors */
--brand-primary: rgb(255, 56, 92)     /* #FF385C - Main brand color */
--brand-secondary: rgb(230, 30, 77)   /* #E61E4D */
--brand-gradient-start: rgb(230, 30, 77)
--brand-gradient-middle: rgb(227, 28, 95)
--brand-gradient-end: rgb(215, 4, 102)
Extended Color System
css/* Rausch Color Scale (Brand Reds) */
--rausch-100: #FFF8F9
--rausch-200: #FFEEF0
--rausch-300: #FEE5E7
--rausch-400: #FFD2D7
--rausch-500: #FFABB6
--rausch-600: #FF385C  /* Primary brand */
--rausch-700: #DA1249
--rausch-800: #A21039
--rausch-900: #732139
--rausch-1000: #361A21

/* Beige/Neutral Scale */
--beige-100: #F7F6F2
--beige-200: #F4F2EC
--beige-300: #EEEBE5
--beige-400: #DFDCD6
--beige-500: #C5C1BB
--beige-600: #8F8B87
--beige-700: #6E6A66
--beige-800: #53514E
--beige-900: #413F3D
--beige-1000: #232221
Border Colors
css--border-light: rgba(235, 235, 235, 0.8)  /* Subtle borders */
--border-standard: rgba(0, 0, 0, 0.08)    /* Standard borders */
Spacing & Layout System
Base Spacing Scale

4px: Micro-spacing for tight elements
8px: Small spacing between related elements
12px: Standard spacing for form elements
16px: Medium spacing for component separation
24px: Large spacing for section separation
32px: Extra large spacing
48px: Section padding
80px: Major section padding on desktop

Grid System
css/* 12-Column Grid */
grid-template-columns: repeat(12, 1fr)
column-gap: 16px

/* Content Max-Width Constraints */
max-width: 1440px  /* Standard content max-width */
max-width: 1920px  /* Footer max-width */
Responsive Breakpoints

Baseline (Mobile): 0-768px
Medium (Tablet): 768px-1024px
Large (Desktop): 1024px+

Border Radius System
css/* Subtle rounding for small elements */
border-radius: 2px   /* Links, small buttons */
border-radius: 8px   /* Standard buttons, cards */
border-radius: 12px  /* Icon containers */

/* Pills and rounded elements */
border-radius: 40px   /* Pill buttons */
border-radius: 1584px /* Full pill buttons */
border-radius: 1782px /* Large pill buttons */
Animation & Transition System
Standard Transitions
css/* Micro-interactions */
transition: transform 0.1s cubic-bezier(0.2, 0, 0, 1)
transition: box-shadow 0.2s cubic-bezier(0.2, 0, 0, 1)
transition: opacity 0.2s cubic-bezier(0.2, 0, 0, 1)

/* Smooth content transitions */
transition: all 0.5s ease
transition: opacity 1.25s  /* Gradient animations */
Complex Animations
css/* Radial progress animations */
animation-duration: 0.7s
animation-iteration-count: infinite
animation-timing-function: cubic-bezier(0.1, 0.9, 0.2, 1)

/* Timeline-based animations */
animation-timeline: --app
animation-range: contain 0px contain 100px
Material Design Elements
Backdrop Filters
css/* Glassmorphism effects */
backdrop-filter: blur(10px)
background: rgba(255, 255, 255, 0.9)  /* Transparent white overlay */

/* Material background variants */
--material-extra-thin: rgba(218,218,218,0.40) blur(8px) saturate(1)
--material-thin: rgba(240,240,240,0.50) blur(36px) saturate(1.6)  
--material-regular: rgba(250,250,250,0.72) blur(24px) saturate(1.6)
--material-thick: rgba(240,240,240,0.86) blur(12px) saturate(3.00)
--material-extra-thick: rgba(255,255,255,0.925) blur(16px) saturate(1.6)

Section-by-Section Breakdown
Navigation Header
Structure & Layout
css/* Fixed header with glassmorphism */
position: fixed
top: 0px
left: 0px
width: 100%
height: 80px
z-index: 100

/* Backdrop blur effect */
backdrop-filter: blur(10px)
background: rgba(255, 255, 255, 0.9)
border-bottom: 1px solid rgba(235, 235, 235, 0.8)
Inner Container
csspadding: 0px 80px
margin: 0px auto
max-width: 1440px
display: flex
justify-content: space-between
align-items: center
height: 100%
Logo Section
css/* Logo container */
flex: 1 0 140px
width: 32px
overflow: hidden
color: rgb(255, 56, 92)  /* Brand color */

/* Logo link styling */
display: inline-flex
align-items: center
height: 80px
transition: color 0.25s cubic-bezier(0.2, 0, 0, 1)
Hero Section
Main Container
css/* Hero section background */
background: rgb(255, 255, 255)  /* Clean white background */
padding: 0px 80px 120px 80px    /* Large bottom padding */
margin: 0px auto
max-width: 1440px
Hero Title Styling
css/* Large display heading */
font-size: 60px
line-height: 64px
letter-spacing: -1.8px  /* Tight letter spacing */
font-weight: 700
color: rgb(34, 34, 34)
padding: 0px 0px 56px   /* Large bottom margin */
margin: 0px
Hero Description
cssfont-size: 18px
line-height: 28px
font-weight: 400
color: rgb(34, 34, 34)
max-width: 240px  /* Constrained width for readability */
Button System
Primary Button (CTA)
css/* Gradient button styling */
background: linear-gradient(to right, 
  rgb(230, 30, 77) 0%, 
  rgb(227, 28, 95) 50%, 
  rgb(215, 4, 102) 100%)
color: rgb(255, 255, 255)
border: 0px
border-radius: 8px
padding: 14px 24px
font-size: 16px
font-weight: 500
line-height: 20px

/* Interactive states */
transition: box-shadow 0.2s cubic-bezier(0.2, 0, 0, 1), 
           transform 0.1s cubic-bezier(0.2, 0, 0, 1)
cursor: pointer
touch-action: manipulation
Secondary Buttons
css/* Transparent buttons with hover states */
background: transparent
color: rgb(34, 34, 34)
border: 0px
border-radius: 8px
padding: 6px 8px
font-size: 14px
font-weight: 500

/* Hover state */
background-hover: rgb(247, 247, 247)  /* Light gray background */
Pill Buttons
css/* Fully rounded buttons */
border-radius: 1584px  /* Very large radius for pill effect */
padding: 14px 24px
background: rgb(34, 34, 34)  /* Dark background */
color: rgb(255, 255, 255)
Feature Cards Section
Container Structure
css/* Three-column grid on desktop */
display: grid
grid-template-columns: repeat(3, 1fr)
gap: 34px  /* Large gap between cards */
max-width: 968px
margin: 0px auto
padding: 0px 80px 120px
Individual Card Styling
css/* Card layout */
display: grid
grid-template-rows: 64px 56px  /* Icon area + text area */
align-items: flex-start
text-align: center
Icon Containers
css/* Circular icon backgrounds */
width: 48px
height: 48px
display: grid
place-items: center
background: rgb(247, 247, 247)
border-radius: 12px
color: rgb(34, 34, 34)
SVG Icon Styling
css/* Consistent icon sizing */
display: block
height: 32px
width: 32px
fill: currentColor  /* Inherits text color */
Footer Section
Main Footer Container
cssbackground: rgb(247, 247, 247)  /* Light gray background */
padding-left: 48px
padding-right: 48px
max-width: 1920px  /* Wider than content max-width */
margin: 0px auto
Footer Grid System
css/* 12-column grid system */
padding: 48px 0px
display: grid
grid-template-columns: repeat(12, minmax(0, 1fr))
column-gap: 16px
Footer Sections
css/* Each footer section spans 4 columns */
grid-column: span 4
padding-block: 0px
Footer Headings
cssmargin-top: 0px
margin-bottom: 16px
color: rgb(34, 34, 34)
font-size: 14px
line-height: 18px
font-weight: 500
Footer Links
css/* Clean link styling */
color: rgb(34, 34, 34)
text-decoration: none
font-size: 14px
background: transparent
border: 0px
border-radius: 2px  /* Subtle rounding */
font-weight: 400
line-height: 18px

/* Hover states */
cursor: pointer
transition: background-color 0.2s ease
Footer Lists
css/* Link list styling */
margin: 0px
padding: 0px
list-style: none
display: grid
gap: 16px  /* Consistent spacing between links */
Mobile Navigation Footer
Sticky Bottom Navigation
css/* Fixed bottom positioning */
position: fixed
bottom: 0px
left: 0px
right: 0px
z-index: 4

/* Glassmorphism effect */
background: rgba(255, 255, 255, 0.5)
backdrop-filter: blur(10px)
border-top: 1px solid rgba(235, 235, 235, 0.8)

/* Animation */
animation-name: slide-up
animation-duration: 0.5s
animation-timing-function: cubic-bezier(0.2, 0, 0, 1)
animation-delay: 2s
animation-fill-mode: backwards
Interactive Elements
Hover Effects
css/* Subtle transform on hover */
transform: scale(1.02)
transition: transform 0.1s cubic-bezier(0.2, 0, 0, 1)

/* Box shadow elevation */
box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15)
transition: box-shadow 0.2s cubic-bezier(0.2, 0, 0, 1)
Progress Indicators
css/* Circular progress bars */
transform: matrix(0, -1, 1, 0, 0, 0)  /* Rotation transform */
transition: stroke-dashoffset 0.25s cubic-bezier(0, 0, 1, 1)
stroke-width: 2px
stroke-dasharray: 87.96459430051421
stroke-linecap: round
Accessibility Features
Screen Reader Support
css/* Visually hidden but accessible to screen readers */
clip: rect(0px, 0px, 0px, 0px)
clip-path: inset(100%)
height: 1px
overflow: clip
position: absolute
white-space: nowrap
width: 1px
Focus States
css/* High contrast focus indicators */
outline: rgb(34, 34, 34) none 0px
/* Custom focus styles implemented via :focus-visible */
Responsive Design Patterns
Mobile-First Approach
css/* Base styles for mobile */
padding: 16px 24px

/* Medium screens */
@media (min-width: 768px) {
  padding: 0px 40px 88px 40px
}

/* Large screens */
@media (min-width: 1024px) {
  padding: 0px 80px 120px 80px
}
Adaptive Grid Systems
css/* Mobile: Single column */
grid-template-columns: 1fr

/* Tablet: Flexible columns */
grid-template-columns: repeat(auto-fit, minmax(280px, 1fr))

/* Desktop: Fixed 3-column */
grid-template-columns: repeat(3, 1fr)

Implementation Guidelines
CSS Custom Properties Usage
The design system extensively uses CSS custom properties for consistent theming:
css/* Typography variables */
--label_font-size: 1.125rem
--label_line-height: 1.75rem
--label_font-weight: 400

/* Spacing variables */
--section_padding: 0px 80px 120px 80px
--section_max-width: 1440px
--section_margin: 0 auto
Component Architecture
Each component follows a consistent data attribute pattern:
html<div data-element-name="component-name" data-variant="style-variant">
<div data-section-name="section-identifier">
<div data-static-id="unique-identifier">
Performance Optimizations

contain: strict for performance-critical elements
will-change: transform for animated elements
touch-action: manipulation for mobile optimization
content-visibility for large content areas

Testing & QA Attributes
Every interactive element includes testing attributes:
htmldata-testid="specific-test-identifier"
data-hook="functionality-hook"`
    },
    {
      id: 'discord',
      name: 'Discord',
      image: '/designinspoimages/discord1.png',
      description: 'Modern communication platform with dark theme aesthetics',
      prompt: `Transform my current website design, CSS styling and overall frontend into the following strict and descriptive design and frontend specification document. Make sure to not change any copy, context or functionality we currently have in the website, this is solely a design UI/UX and frontend redesign.

IMPORTANT COMPONENT LIBRARY REQUIREMENT:
You MUST use shadcn/ui for all UI components to ensure accessibility, consistency, and modern design patterns. shadcn/ui is a collection of copy-and-paste components built on top of Radix UI primitives and styled with version 3 of Tailwind CSS.

Installation & Setup:
1. Install shadcn/ui CLI: npx shadcn-ui@latest init
2. Configure your components.json when prompted
3. Add components as needed: npx shadcn-ui@latest add [component-name]

Key Benefits:
- Fully accessible components built on Radix UI primitives
- Beautifully designed with consistent styling
- Customizable and themeable
- TypeScript support out of the box
- Copy-paste friendly - you own the code
- Works seamlessly with Tailwind CSS

Usage Guidelines:
- Use shadcn/ui components for buttons, forms, modals, navigation, cards, etc.
- Examples: npx shadcn-ui@latest add button card form dialog navigation-menu
- Import components from your components/ui directory
- Customize component variants using the built-in variant system
- Leverage the built-in dark mode support
- Follow the component composition patterns for complex UI elements

Always prioritize shadcn/ui components over custom implementations to maintain design consistency and accessibility standards.

Visual Identity

Dark-themed interface with a deep blue-purple gradient background (#1a1625 to #2B2D31)
High contrast design using white text on dark backgrounds for maximum readability
Playful yet professional aesthetic combining rounded corners, animated elements, and bold typography

Design Language
- Neumorphic and glassmorphic elements with subtle shadows and transparency effects
- Extensive use of border-radius (16px-40px) creating a soft, approachable interface
- Dynamic depth layering through strategic z-index management and backdrop filters

Technical Excellence
- Performance-focused implementation with lazy loading and optimized assets
- Responsive fluid design adapting seamlessly across breakpoints
- Sophisticated animation system using CSS transforms and Web Animations API

Section-by-Section Breakdown
1. Navigation Header
Structure & Layout
header {
  position: fixed;
  z-index: 99;
  backdrop-filter: blur(20px);
  background-color: rgba(0, 0, 0, 0.1);
  height: 80px;
}

.nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 40px;
  max-width: 1300px;
  margin: 0 auto;
}

Key Design Elements
- Fixed positioning with glassmorphic backdrop blur effect
- Horizontal navigation with dropdown mega-menus
- Logo placement: Fixed left position at 40px
- Typography:
  - Font: "ABC Ginto Normal" for nav items
  - Size: 16px base
  - Weight: 500 for links
  - Color: White with 0.8 opacity for inactive states

Interactive States
.nav-link {
  padding: 9px 16px;
  border-radius: 16px;
  transition: background-color 0.3s ease;
}

.nav-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Dropdown Animation */
.dropdown-menu {
  opacity: 0;
  transform: translateY(-10px);
  transition: opacity 0.4s, transform 0.4s;
}

Dropdown Mega-Menu Design
- Background: Solid #5865F2 (brand purple)
- Border-radius: 40px for soft appearance
- Padding: 40px internal spacing
- Grid layout: Multi-column structure for organized content
- Animation: Fade in with subtle slide effect

2. Hero Section
Layout Architecture
.hero-section {
  padding-top: 144px;
  padding-bottom: 125px;
  background-image: url('texture-headline.webp');
  background-position: 100% -33%;
  background-size: 95.5%;
}

.hero-content {
  display: flex;
  gap: 34.6px;
  max-width: 1240px;
  margin: 0 auto;
}`
    },
    {
      id: 'notion',
      name: 'Notion',
      image: '/designinspoimages/notion1.png',
      description: 'Clean productivity interface with minimalist design principles',
      prompt: `Transform my current website design, CSS styling and overall frontend into the following strict and descriptive design and frontend specification document. Make sure to not change any copy, context or functionality we currently have in the website, this is solely a design UI/UX and frontend redesign.

IMPORTANT COMPONENT LIBRARY REQUIREMENT:
You MUST use shadcn/ui for all UI components to ensure accessibility, consistency, and modern design patterns. shadcn/ui is a collection of copy-and-paste components built on top of Radix UI primitives and styled with version 3 of Tailwind CSS.

Installation & Setup:
1. Install shadcn/ui CLI: npx shadcn-ui@latest init
2. Configure your components.json when prompted
3. Add components as needed: npx shadcn-ui@latest add [component-name]

Key Benefits:
- Fully accessible components built on Radix UI primitives
- Beautifully designed with consistent styling
- Customizable and themeable
- TypeScript support out of the box
- Copy-paste friendly - you own the code
- Works seamlessly with Tailwind CSS

Usage Guidelines:
- Use shadcn/ui components for buttons, forms, modals, navigation, cards, etc.
- Examples: npx shadcn-ui@latest add button card form dialog navigation-menu
- Import components from your components/ui directory
- Customize component variants using the built-in variant system
- Leverage the built-in dark mode support
- Follow the component composition patterns for complex UI elements

Always prioritize shadcn/ui components over custom implementations to maintain design consistency and accessibility standards.
Executive Summary: Design Philosophy & High-Level Overview
This design system represents a modern, clean, and sophisticated approach to web design that emphasizes clarity, functionality, and visual hierarchy. The overall aesthetic is characterized by:
Core Design Principles

Minimalist Foundation: Clean, uncluttered interfaces with generous whitespace
Typography-First Approach: Strong emphasis on readable, hierarchical text systems
Subtle Sophistication: Refined color palette with strategic accent usage
Smooth Interactions: Thoughtful animations and hover states enhance user experience
Component Consistency: Modular design system ensuring cohesive visual language

Visual Language Characteristics

Color Strategy: Predominantly monochromatic with strategic blue accents (#0075de primary)
Typography Hierarchy: Multi-level system using Inter/NotionInter font family
Spacing System: Consistent mathematical spacing units (8px base grid)
Border Radius: Soft, modern corners (8px standard, 12px for larger elements)
Shadow Usage: Minimal, purposeful shadows for depth and hierarchy


Technical Design System Analysis
1. Typography System
Font Families
css--typography-font-family: 'NotionInter, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"';
--typography-serif-family: '"Lyon Text", Georgia, YuMincho, "Yu Mincho", "Hiragino Mincho ProN", "Hiragino Mincho Pro", "Songti TC", "Songti SC", SimSun, "Nanum Myeongjo", NanumMyeongjo, Batang, serif';
Typography Scale & Specifications
Title Typography

Font Size: 2.5rem (40px) mobile / 3rem (48px) desktop
Font Weight: 800
Line Height: 3rem (48px) mobile / 4rem (64px) desktop
Letter Spacing: -0.09375rem (-1.5px) mobile / -0.1328125rem (-2.125px) desktop

Heading Typography

Font Size: 2rem (32px) mobile / 2.375rem (38px) desktop
Font Weight: 700
Line Height: 2.5rem (40px) mobile / 3rem (48px) desktop
Letter Spacing: -0.046875rem (-0.75px) mobile / -0.09375rem (-1.5px) desktop

Deck Typography (Large body text)

Font Size: 1.125rem (18px) mobile / 1.25rem (20px) desktop
Font Weight: 400
Line Height: 1.5rem (24px) mobile / 1.75rem (28px) desktop

Body Typography

Font Size: 1rem (16px)
Font Weight: 400
Line Height: 1.5rem (24px)
Letter Spacing: 0

Caption Typography

Font Size: 0.875rem (14px)
Font Weight: 400
Line Height: 1.25rem (20px)
Letter Spacing: 0.0078125rem (0.125px)

2. Color System
Primary Colors
css--color-text-primary: #191918;
--color-text-secondary: #31302e;
--color-text-muted: #00000096;
--color-background: #ffffff;
--color-background-secondary: #f6f5f4;
Interactive Colors
css--color-button-primary: #0075de;
--color-button-primary-hover: #005bab;
--color-button-primary-active: #005bab;
--color-button-secondary: #f2f9ff;
--color-button-secondary-text: #005bab;
--color-button-tertiary: #ffffff;
--color-button-tertiary-border: #0000001a;
System Colors
css--color-border: #0000001a;
--color-border-hover: #0000001a;
--color-badge-primary: #31302e;
--color-badge-secondary: #ffffff;
--color-badge-tertiary: #f2f9ff;
3. Spacing System
The design uses a consistent 8px base grid system:
css--block-spacing-padding-inline: 2rem (32px);
--block-spacing-padding-block: 1.75rem (28px);
--block-spacing-gap: 1.75rem (28px);
--block-spacing-text-gap: 0.25rem (4px);
Standard Padding Values:

Small: 8px
Medium: 16px
Large: 24px
Extra Large: 32px
Container: 40px

4. Component Specifications
Buttons
css/* Primary Button */
.button-primary {
    background: #0075de;
    color: #ffffff;
    border-radius: 0.5rem (8px);
    padding: 4px 14px;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    min-height: 36px;
    transition: background-color 0.15s;
}

/* Secondary Button */
.button-secondary {
    background: #f2f9ff;
    color: #005bab;
    border-radius: 0.5rem (8px);
    padding: 4px 14px;
}

/* Tertiary Button */
.button-tertiary {
    background: #ffffff;
    color: #191918;
    border: 1px solid #0000001a;
    border-radius: 0.5rem (8px);
}
Cards/Blocks
css.block-component {
    background: #f6f5f4;
    border-radius: 0.75rem (12px);
    padding: 1.75rem (28px);
    border: 2px solid #f6f5f4;
    transition: border-color 0.2s;
}

.block-component:hover {
    border-color: #dfdcd9;
}

Section-by-Section Landing Page Breakdown
1. Navigation Bar
Structure:

Grid-based layout: grid-template-columns: 1fr auto 1fr
Height: 64px minimum
Padding: 15px 25px
Background: #ffffff
Position: Sticky top with z-index: 100

Components:

Logo (left): SVG-based, linked to home
Center Navigation: Dropdown menus with hover states
Right Actions: "Log in" link and "Get Started Free" CTA button

Dropdown Specifications:
css.nav-dropdown {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.1);
    padding: 24px;
    min-width: 600px;
}
Typography in Navigation:

Nav items: 16px, medium weight (500)
Dropdown headers: 14px, regular weight (400), color: #00000096
Dropdown links: 18px, semibold (600)

2. Hero Section
Layout Structure:

Full-width container with responsive padding
Two-column grid on desktop, stacked on mobile
Left: Text content
Right: Animated illustrations

Hero Typography:
css.hero-title {
    font-size: 3rem; /* 48px desktop */
    font-weight: 800;
    line-height: 4rem;
    letter-spacing: -0.1328125rem;
    color: #191918;
}

.hero-subtitle {
    font-size: 1.25rem; /* 20px */
    font-weight: 400;
    line-height: 1.75rem;
    color: #191918;
    margin-top: 16px;
}
CTA Buttons:

Primary: Blue (#0075de) background, white text
Secondary: Transparent with text link style
Spacing: 16px gap between buttons

Animated Elements:

Custom animated character illustrations
SVG-based with CSS animations
Smooth transitions on hover/interaction

3. Social Proof Section
Layout:

Centered container with max-width constraint
Horizontal scrolling marquee for logos
Logo specifications:

Height: 32px
Grayscale filter with opacity
Spacing: 36px between logos



Marquee Animation:
css.marquee {
    animation: marqueeFrames 60s linear infinite;
    mask-image: linear-gradient(
        to right, 
        rgba(0, 0, 0, 0), 
        rgb(0, 0, 0) 15%, 
        rgb(0, 0, 0) 85%, 
        rgba(0, 0, 0, 0)
    );
}
4. Feature Cards Section
Grid Layout:

2x2 grid on desktop
Single column on mobile
Gap: 24px between cards

Card Specifications:
css.feature-card {
    background: #f6f5f4;
    border-radius: 12px;
    padding: 28px;
    display: flex;
    align-items: center;
    gap: 16px;
    transition: transform 0.2s, box-shadow 0.2s;
}

.feature-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
}
Icon Specifications:

Size: 24px × 24px
Style: Outlined, stroke-width: 2px
Color: #097fe8

5. Quote/Testimonial Section
Layout Structure:

Centered content with max-width: 800px
Video thumbnail with play button overlay
Quote text with attribution

Typography:
css.quote-text {
    font-family: "Lyon Text", Georgia, serif;
    font-size: 1.625rem;
    font-weight: 400;
    line-height: 2rem;
    color: #191918;
}

.quote-attribution {
    font-size: 0.875rem;
    color: #00000096;
    margin-top: 16px;
}
Video Player Overlay:
css.video-overlay {
    position: absolute;
    bottom: 16px;
    right: 16px;
    width: 40px;
    height: 40px;
    background: #ffffff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}
6. Product Showcase Section
Carousel Implementation:

Tab-based navigation
Smooth transitions between views
Active tab indicator with underline

Tab Specifications:
css.tab-button {
    padding: 8px 12px;
    color: #00000096;
    font-weight: 500;
    transition: color 0.2s;
}

.tab-button.active {
    color: #191918;
    position: relative;
}

.tab-button.active::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: #191918;
}
7. CTA Section
Layout:

Centered text alignment
Maximum width: 600px
Vertical spacing: 48px padding

Design Elements:

Large headline (2.375rem)
Descriptive text (1.125rem)
Primary CTA button centered

8. Footer
Structure:

Multi-column layout (4 columns desktop, 2 mobile)
Column categories with link lists
Bottom bar with legal/social links

Typography:
css.footer-heading {
    font-size: 15px;
    font-weight: 600;
    color: #191918;
    margin-bottom: 12px;
}

.footer-link {
    font-size: 14px;
    color: #31302e;
    line-height: 21.6px;
    margin-top: 6.4px;
}

Animation & Interaction Patterns
Hover Effects
css/* Link hover */
a {
    transition: color 0.15s, background-color 0.15s;
    text-underline-offset: 1.6px;
}

/* Card hover */
.card:hover {
    transform: translateY(-2px);
    transition: transform 0.2s ease-out;
}

/* Button hover */
.button:hover {
    background-color: var(--color-button-primary-hover);
    transition: background-color 0.15s;
}
Scroll Animations

Fade-in on scroll for sections
Parallax effects on hero illustrations
Smooth scroll behavior enabled globally

Loading States

Skeleton screens for content loading
Progressive image loading with blur-up effect
Smooth transitions between states


Responsive Design Breakpoints
css/* Mobile First Approach */
/* Base: 0-740px */
/* Tablet: 741px-1023px */
/* Desktop: 1024px+ */

@media (min-width: 741px) {
    /* Tablet styles */
}

@media (min-width: 1024px) {
    /* Desktop styles */
}
Container Widths

Mobile: 100% - 40px padding
Tablet: 100% - 80px padding
Desktop: max-width: 1200px, centered


Advanced Implementation Details from Code Analysis
CSS Custom Properties Structure
The design system uses an extensive custom property system for maintainability:
css/* Base Typography Variables */
--typography-title-font-family: NotionInter, Inter, -apple-system, BlinkMacSystemFont;
--typography-title-font-size: 2.5rem;
--typography-title-font-weight: 800;
--typography-title-line-height: 3rem;
--typography-title-letter-spacing: -0.09375rem;

/* Responsive Typography */
--typography-title-sm-font-size: 3rem;
--typography-title-sm-line-height: 4rem;
--typography-title-sm-letter-spacing: -0.1328125rem;

/* Component-Specific Variables */
--block-border-radius: 0.75rem;
--block-border-width: 2px;
--block-color-background-default: #f6f5f4;
--block-color-background-hover: #f6f5f4;
--block-color-border-hover: #dfdcd9;
Navigation Implementation Details
Dropdown Menu Structure (from JSX):
jsx<div style={{
    boxSizing: 'border-box',
    borderRadius: '8px',
    padding: '8px',
    marginBottom: '-4px'
}}>
    <h3 style={{
        font: 'var(--typography-sans-400-semibold-font-weight) var(--typography-sans-400-semibold-font-size) / var(--typography-sans-400-semibold-line-height) var(--typography-sans-400-semibold-font-family)',
        textWrap: 'balance'
    }}>Menu Item</h3>
    <span style={{
        font: 'var(--typography-sans-50-regular-font-weight) var(--typography-sans-50-regular-font-size) / var(--typography-sans-50-regular-line-height) var(--typography-sans-50-regular-font-family)',
        color: 'var(--color-gray-500)'
    }}>Description text</span>
</div>
Hero Section Advanced Styling
Animated Character Implementation:
css.hero-illustration {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.animated-character {
    width: 128px;
    height: 128px;
    animation: float 3s ease-in-out infinite;
}

@keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
}
Marquee Implementation
Complete Marquee Structure:
css.marquee-container {
    --marquee-margin-top: 16px;
    --marquee-item-gap: 36px;
    --marquee-margin-inline: -40px;
    --marquee-gradient-percent: 15%;
    --marquee-animation-duration: 60s;
    --marquee-animation: marqueeFrames 60s linear infinite;
    
    contain: paint;
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-inline: -40px;
    mask-image: linear-gradient(
        to right, 
        rgba(0, 0, 0, 0), 
        rgb(0, 0, 0) 15%, 
        rgb(0, 0, 0) 85%, 
        rgba(0, 0, 0, 0)
    );
}

@keyframes marqueeFrames {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
}
Interactive Component States
Button State Variations:
css/* Base Button Reset */
button {
    border: 0;
    margin: 0;
    padding: 0;
    cursor: pointer;
    font-family: inherit;
    -webkit-font-smoothing: antialiased;
}

/* Primary Button States */
.btn-primary {
    background: rgb(0, 117, 222);
    border-color: rgba(255, 255, 255, 0);
    color: rgb(255, 255, 255);
    font-weight: 500;
    min-height: 36px;
    padding: 4px 14px;
    border-radius: 8px;
    transition: all 0.15s ease;
}

.btn-primary:hover {
    background: rgb(0, 91, 171);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 117, 222, 0.2);
}

.btn-primary:active {
    transform: translateY(0);
    box-shadow: none;
}
Grid System Implementation
Responsive Grid Classes:
css.grid-container {
    display: grid;
    gap: 24px;
    padding: 0 20px;
}

/* Mobile First */
.grid-cols-1 { grid-template-columns: 1fr; }

/* Tablet */
@media (min-width: 741px) {
    .grid-cols-md-2 { grid-template-columns: repeat(2, 1fr); }
    .grid-container { padding: 0 40px; }
}

/* Desktop */
@media (min-width: 1024px) {
    .grid-cols-lg-3 { grid-template-columns: repeat(3, 1fr); }
    .grid-cols-lg-4 { grid-template-columns: repeat(4, 1fr); }
}
Image Optimization Patterns
Responsive Image Implementation:
html<picture>
    <source 
        media="(min-width: 741px)" 
        srcset="image-desktop.jpg 1x, image-desktop@2x.jpg 2x"
    />
    <source 
        media="(max-width: 740px)" 
        srcset="image-mobile.jpg 1x, image-mobile@2x.jpg 2x"
    />
    <img 
        src="image-fallback.jpg" 
        alt="Description"
        loading="lazy"
        decoding="async"
        style="width: 100%; height: auto; object-fit: cover;"
    />
</picture>
Advanced Typography Utilities
Text Truncation and Wrapping:
css.text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.text-balance {
    text-wrap: balance; /* Modern browsers */
    word-break: break-word; /* Fallback */
}

.text-pretty {
    text-wrap: pretty;
    hyphens: auto;
}
Form Element Styling
Input Field Specifications:
css.input-field {
    background: #ffffff;
    border: 1px solid #0000001a;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 16px;
    line-height: 24px;
    transition: border-color 0.15s;
}

.input-field:focus {
    outline: none;
    border-color: #0075de;
    box-shadow: 0 0 0 3px rgba(0, 117, 222, 0.1);
}

.input-field::placeholder {
    color: #00000096;
}
Dialog/Modal Implementation
Modal Structure and Styling:
css.modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    z-index: 999;
}

.modal-content {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #ffffff;
    border-radius: 12px;
    padding: 24px;
    max-width: 90vw;
    max-height: 90vh;
    overflow: auto;
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.15);
}
Performance-Optimized Animations
GPU-Accelerated Transitions:
css.transition-transform {
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
}

.transition-opacity {
    transition: opacity 0.15s ease-out;
    will-change: opacity;
}

/* Reduce motion for accessibility */
@media (prefers-reduced-motion: reduce) {
    * {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
    }
}

Accessibility Considerations

Color Contrast: All text meets WCAG AA standards
Focus States: Visible focus indicators on all interactive elements
Semantic HTML: Proper heading hierarchy and ARIA labels
Keyboard Navigation: Full keyboard support with logical tab order
Screen Reader Support: Descriptive alt text and ARIA attributes


Implementation Guidelines
CSS Architecture

Use CSS custom properties for all design tokens
Component-based styling with BEM methodology
Utility classes for common patterns
Mobile-first responsive approach

Performance Optimizations

Lazy loading for images below the fold
CSS containment for expensive operations
GPU-accelerated animations using transform/opacity
Critical CSS inlined for above-the-fold content
`
    },
    {
      id: 'figma',
      name: 'Figma',
      image: '/designinspoimages/figma1.png',
      description: 'Professional design tool interface with collaborative features',
      prompt: `Transform my current website design, CSS styling and overall frontend into the following strict and descriptive design and frontend specification document. Make sure to not change any copy, context or functionality we currently have in the website, this is solely a design UI/UX and frontend redesign.

IMPORTANT COMPONENT LIBRARY REQUIREMENT:
You MUST use shadcn/ui for all UI components to ensure accessibility, consistency, and modern design patterns. shadcn/ui is a collection of copy-and-paste components built on top of Radix UI primitives and styled with version 3 of Tailwind CSS.

Installation & Setup:
1. Install shadcn/ui CLI: npx shadcn-ui@latest init
2. Configure your components.json when prompted
3. Add components as needed: npx shadcn-ui@latest add [component-name]

Key Benefits:
- Fully accessible components built on Radix UI primitives
- Beautifully designed with consistent styling
- Customizable and themeable
- TypeScript support out of the box
- Copy-paste friendly - you own the code
- Works seamlessly with Tailwind CSS

Usage Guidelines:
- Use shadcn/ui components for buttons, forms, modals, navigation, cards, etc.
- Examples: npx shadcn-ui@latest add button card form dialog navigation-menu
- Import components from your components/ui directory
- Customize component variants using the built-in variant system
- Leverage the built-in dark mode support
- Follow the component composition patterns for complex UI elements

Always prioritize shadcn/ui components over custom implementations to maintain design consistency and accessibility standards.
## Part 1: High-Level Design Overview

### Visual Design Philosophy

**Modern Clean Design Approach**
- Minimalist aesthetic with generous whitespace usage
- Bold typography hierarchy with strong contrast
- Vibrant color accents against neutral backgrounds
- Playful geometric illustrations and abstract shapes
- Professional yet approachable visual identity

**Color Palette Strategy**
- **Primary Colors**: Black (\`#000000\`) for text and UI elements
- **Background**: Clean white (\`#FFFFFF\`) as primary surface
- **Accent Colors**: 
  - Bright teal/cyan (\`#00D4FF\`) for quotation marks and highlights
  - Orange (\`#FF6900\`) for interactive elements
  - Purple/pink gradients in feature sections
  - Lime green backgrounds (\`#C4FF61\`) for content areas
- **Semantic Colors**: Blue (\`#0000FF\`) for primary actions
- **Neutral Grays**: Various opacity levels of black for secondary text

**Typography Hierarchy**
- **Primary Font**: \`__figmaSans_a26a19\` (Figma's custom typeface)
- **Fallback Stack**: \`'SF Pro Display', system-ui, helvetica, sans-serif\`
- **Font Weights**: Ranges from 340 (light) to bold variations
- **Font Features**: 
  - Font optical sizing: auto
  - Font variation settings: \`'wdth' 98, 'wght' 340\`
  - Responsive font scaling across breakpoints

**Spacing and Layout Principles**
- **Grid System**: Flexbox-based layouts with consistent gaps
- **Container Strategy**: Centered content with \`margin-inline: auto\`
- **Spacing Scale**: 
  - Small gaps: 8px, 12px
  - Medium spacing: 16px, 24px
  - Large sections: 64px margins
- **Border Radius**: Consistent 12px for cards and components
- **Scroll Margin**: Universal 80px top margin for navigation offset

### User Experience Patterns

**Navigation Structure**
- **Header Navigation**: Horizontal menu with dropdown indicators
- **Primary Actions**: "Log in", "Contact sales", "Get started for free"
- **Navigation Items**: Products, Solutions, Community, Resources, Pricing
- **Button Hierarchy**: Primary (black), Secondary (outlined), Text links

**Interaction Patterns**
- **Custom Cursors**: SVG-based cursor graphics for different interaction states
- **Hover Effects**: 0.16s ease-out transitions
- **Button States**: Multiple visual states (default, hover, active)
- **Smooth Scrolling**: Scroll-margin-top: 80px for anchor navigation

**Content Organization**
- **Hero Section**: Large headlines with supporting graphics
- **Feature Showcase**: Tabbed interface (Design, Draw, Build, Publish, etc.)
- **Social Proof**: User testimonials with profile images
- **Product Gallery**: Carousel/slider components for multiple views
- **Footer**: Comprehensive link organization with multiple columns

**Mobile Responsiveness**
- **Responsive Images**: \`object-fit: cover\` for consistent aspect ratios
- **Flexible Layouts**: Flexbox with \`flex-flow: row wrap\`
- **Touch Interactions**: Optimized for mobile cursor patterns
- **Adaptive Typography**: Font size scaling across viewport sizes

### Technical Implementation Summary

**Frontend Architecture**
- **Framework**: React-based implementation (evidenced by JSX structure)
- **Styling Approach**: CSS-in-JS or styled-components with utility classes
- **Component Library**: Custom design system with reusable components

**CSS Methodologies**
- **Utility-First Approach**: Atomic class naming (style-0, style-1, etc.)
- **CSS Custom Properties**: Color variables with \`--f-\` prefix
- **Component Scoping**: Unique class identifiers per component
- **Box Model**: Universal \`box-sizing: border-box\`

**Animation Technologies**
- **CSS Transforms**: \`translateY()\`, \`translateZ()\` for positioning
- **Transitions**: Consistent 0.16s ease-out timing
- **Position**: Fixed positioning for floating elements
- **Z-index**: Layering system (z-index: 10 for fixed elements)

**Responsive Design Implementation**
- **Mobile-First**: Progressive enhancement approach
- **Flexbox Grid**: Modern layout system
- **Container Queries**: Max-width constraints with auto margins
- **Viewport Units**: Responsive sizing with rem/px combinations

**Component Architecture**
- **Atomic Design**: Button, Avatar, Card, Navigation components
- **Composition**: Higher-order components for complex layouts
- **State Management**: Interactive states for UI feedback
- **Accessibility**: Semantic HTML structure with proper landmarks

## Part 2: Detailed Section-by-Section Breakdown

### Navigation Header

**Structure & Layout**
\`\`\`html
<header class="navigation-header">
  <div class="nav-container">
    <div class="logo-section">
      <!-- Figma logo -->
    </div>
    <nav class="main-navigation">
      <!-- Navigation items -->
    </nav>
    <div class="action-buttons">
      <!-- CTA buttons -->
    </div>
  </div>
</header>
\`\`\`

**Typography System**
- **Logo**: SVG-based logo with consistent brand colors
- **Navigation Links**: 
  - Font size: 14-16px
  - Font weight: 340-400
  - Color: \`rgb(0, 0, 0)\`
  - Hover states with underline decoration

**Color System**
- **Background**: White (\`#FFFFFF\`)
- **Text**: Black (\`#000000\`)
- **Buttons**: 
  - Primary: Black background (\`#000000\`), white text
  - Secondary: Transparent background with black border
- **Hover States**: Subtle opacity changes and color transitions

**Component Styling**

**Button Variations**
\`\`\`css
.primary-button {
  background: #000000;
  color: #FFFFFF;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 500;
  transition: 0.16s ease-out;
}

.secondary-button {
  background: transparent;
  color: #000000;
  border: 1px solid #000000;
  border-radius: 6px;
  padding: 8px 16px;
}
\`\`\`

**Interactive Elements**
- **Dropdown Indicators**: SVG chevron icons
- **Custom Cursors**: Hand pointer for interactive elements
- **Transition Properties**: 
  - Duration: 0.16s
  - Timing: ease-out
  - Properties: background, color, transform

**Responsive Design**
- **Desktop**: Horizontal layout with full navigation
- **Mobile**: Collapsible hamburger menu (inferred)
- **Breakpoints**: Standard responsive breakpoints
- **Touch Targets**: Minimum 44px for mobile accessibility

### Hero Section

**Structure & Layout**
\`\`\`css
.hero-section {
  padding: 120px 24px 80px;
  display: flex;
  align-items: center;
  gap: 64px;
}

.hero-content {
  flex: 1;
  max-width: 600px;
}

.hero-visual {
  flex: 1;
  max-width: 700px;
}
\`\`\`

**Typography System**
- **Main Headline**: 
  - Font size: 72px (desktop), scales down responsively
  - Font weight: 700
  - Line height: 1.1
  - Color: \`#000000\`
  - Font family: Figma Sans

- **Supporting Text**:
  - Font size: 20px
  - Font weight: 400
  - Line height: 1.4
  - Color: \`rgba(0, 0, 0, 0.7)\`
  - Max width: 500px for readability

**Color System**
- **Background**: White (\`#FFFFFF\`)
- **Headlines**: Pure black (\`#000000\`)
- **Body Text**: 70% opacity black (\`rgba(0, 0, 0, 0.7)\`)
- **Visual Elements**: Multi-colored geometric shapes with vibrant hues

**Component Styling**

**CTA Button Group**
\`\`\`css
.cta-group {
  display: flex;
  gap: 16px;
  margin-top: 32px;
  flex-wrap: wrap;
}

.primary-cta {
  background: #000000;
  color: #FFFFFF;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  transition: transform 0.16s ease-out;
}

.primary-cta:hover {
  transform: translateY(-2px);
}
\`\`\`

**Visual Elements**
- **Geometric Shapes**: Colorful abstract composition
- **Colors Used**: Orange, purple, green, yellow, red, blue
- **Layout**: Overlapping rectangles and organic shapes
- **Animation**: Subtle hover effects and micro-interactions

### Product Showcase Section

**Structure & Layout**
\`\`\`css
.product-showcase {
  background: #F8F9FA;
  padding: 80px 24px;
}

.showcase-tabs {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 48px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 24px;
  padding: 4px;
}

.tab-button {
  padding: 12px 24px;
  border-radius: 20px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.16s ease-out;
}

.tab-button.active {
  background: #FFFFFF;
  color: #000000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
\`\`\`

**Typography System**
- **Section Headline**: 
  - Font size: 48px
  - Font weight: 600
  - Text align: center
  - Color: \`#000000\`

- **Tab Labels**:
  - Font size: 14px
  - Font weight: 500
  - Active state: \`#000000\`
  - Inactive state: \`rgba(0, 0, 0, 0.6)\`

**Interactive Elements**
- **Tab Navigation**: Pill-style toggle buttons
- **Hover States**: Background color changes and shadow effects
- **Active States**: White background with subtle shadow
- **Transition**: 0.16s ease-out for smooth state changes

### Testimonial Cards

**Structure & Layout**
\`\`\`css
.testimonial-card {
  background: #FFFFFF;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  max-width: 400px;
}

.testimonial-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.user-info {
  flex: 1;
}
\`\`\`

**Typography System**
- **Quote Text**:
  - Font size: 18px
  - Font weight: 400
  - Line height: 1.5
  - Color: \`#000000\`

- **User Name**:
  - Font size: 16px
  - Font weight: 600
  - Color: \`#000000\`

- **User Title**:
  - Font size: 14px
  - Font weight: 400
  - Color: \`rgba(0, 0, 0, 0.6)\`

**Color System**
- **Card Background**: White (\`#FFFFFF\`)
- **Text**: Black (\`#000000\`)
- **Secondary Text**: 60% opacity black
- **Shadow**: \`rgba(0, 0, 0, 0.06)\` for subtle depth

**Component Elements**
- **Quote Mark**: Large cyan quotation marks (\`#00D4FF\`)
- **Avatar Images**: Circular profile pictures with proper aspect ratios
- **Company Logos**: Monochrome or brand-colored logos

### Feature Highlight Sections

**Structure & Layout**
\`\`\`css
.feature-section {
  padding: 100px 24px;
  background: linear-gradient(135deg, #E8B4FF 0%, #C89FFF 100%);
}

.feature-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}

.feature-text {
  max-width: 500px;
}

.feature-visual {
  position: relative;
}
\`\`\`

**Typography System**
- **Feature Headlines**:
  - Font size: 56px
  - Font weight: 700
  - Line height: 1.1
  - Color: \`#000000\`

- **Feature Descriptions**:
  - Font size: 20px
  - Font weight: 400
  - Line height: 1.4
  - Color: \`rgba(0, 0, 0, 0.8)\`

**Color System**
- **Purple Section**: Gradient from \`#E8B4FF\` to \`#C89FFF\`
- **Green Section**: Lime green background \`#C4FF61\`
- **Text Overlays**: Black text with sufficient contrast
- **Interactive Elements**: Blue highlights and orange accents

**Interactive Components**

**Demo Interface Cards**
\`\`\`css
.demo-card {
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transform: rotate(-2deg);
  transition: transform 0.3s ease-out;
}

.demo-card:hover {
  transform: rotate(0deg) scale(1.02);
}

.card-header {
  background: #000000;
  color: #FFFFFF;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-content {
  padding: 24px;
  background: #FFFFFF;
}
\`\`\`

### Footer Section

**Structure & Layout**
\`\`\`css
.footer {
  background: #000000;
  color: #FFFFFF;
  padding: 64px 24px 32px;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr repeat(4, 1fr);
  gap: 48px;
}

.footer-brand {
  max-width: 300px;
}

.footer-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
\`\`\`

**Typography System**
- **Footer Headlines**:
  - Font size: 14px
  - Font weight: 600
  - Color: \`rgba(255, 255, 255, 0.9)\`
  - Text transform: uppercase
  - Letter spacing: 0.5px

- **Footer Links**:
  - Font size: 14px
  - Font weight: 400
  - Color: \`rgba(255, 255, 255, 0.7)\`
  - Hover: \`#FFFFFF\`

**Color System**
- **Background**: Pure black (\`#000000\`)
- **Primary Text**: 90% opacity white (\`rgba(255, 255, 255, 0.9)\`)
- **Secondary Text**: 70% opacity white (\`rgba(255, 255, 255, 0.7)\`)
- **Hover States**: Pure white (\`#FFFFFF\`)

**Social Media Links**
\`\`\`css
.social-links {
  display: flex;
  gap: 16px;
  margin-top: 24px;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.16s ease-out;
}

.social-link:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
  transform: translateY(-2px);
}
\`\`\`

### Design Token Documentation

**Spacing Scale**
\`\`\`css
:root {
  --spacing-xs: 4px;
  --spacing-sm: 8px;
  --spacing-md: 12px;
  --spacing-lg: 16px;
  --spacing-xl: 24px;
  --spacing-2xl: 32px;
  --spacing-3xl: 48px;
  --spacing-4xl: 64px;
  --spacing-5xl: 80px;
  --spacing-6xl: 120px;
}
\`\`\`

**Color Palette**
\`\`\`css
:root {
  /* Primary Colors */
  --color-black: #000000;
  --color-white: #FFFFFF;
  
  /* Accent Colors */
  --color-blue: #0000FF;
  --color-cyan: #00D4FF;
  --color-orange: #FF6900;
  --color-purple: #E8B4FF;
  --color-green: #C4FF61;
  
  /* Semantic Colors */
  --color-text-primary: rgba(0, 0, 0, 1);
  --color-text-secondary: rgba(0, 0, 0, 0.7);
  --color-text-tertiary: rgba(0, 0, 0, 0.5);
  
  /* Component Colors */
  --f-bg-color: #FFFFFF;
  --f-text-color: #000000;
  --f-primary-btn-bg-color: #000000;
  --f-primary-btn-text-color: #FFFFFF;
  --f-form-input-bg-color: rgba(0, 0, 0, 0.08);
}
\`\`\`

**Typography Scale**
\`\`\`css
:root {
  /* Font Sizes */
  --font-xs: 12px;
  --font-sm: 14px;
  --font-base: 16px;
  --font-lg: 18px;
  --font-xl: 20px;
  --font-2xl: 24px;
  --font-3xl: 32px;
  --font-4xl: 48px;
  --font-5xl: 56px;
  --font-6xl: 72px;
  
  /* Font Weights */
  --font-light: 340;
  --font-normal: 400;
  --font-medium: 500;
  --font-semibold: 600;
  --font-bold: 700;
  
  /* Line Heights */
  --line-tight: 1.1;
  --line-normal: 1.4;
  --line-relaxed: 1.5;
}
\`\`\`

**Shadow System**
\`\`\`css
:root {
  --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.1);
  --shadow-md: 0 4px 24px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 8px 32px rgba(0, 0, 0, 0.1);
  --shadow-xl: rgba(0, 0, 0, 0.15) 0px 0px 0.5px 0px, 
               rgba(0, 0, 0, 0.13) 0px 5px 12px 0px, 
               rgba(0, 0, 0, 0.1) 0px 1px 3px 0px;
}
\`\`\`

**Border Radius Standards**
\`\`\`css
:root {
  --radius-sm: 4px;
  --radius-md: 6px;
  --radius-lg: 8px;
  --radius-xl: 12px;
  --radius-2xl: 16px;
  --radius-3xl: 24px;
  --radius-full: 50%;
}
\`\`\`

**Animation Timing**
\`\`\`css
:root {
  --duration-fast: 0.16s;
  --duration-normal: 0.3s;
  --duration-slow: 0.5s;
  
  --easing-ease-out: cubic-bezier(0, 0, 0.2, 1);
  --easing-ease-in-out: cubic-bezier(0.4, 0, 0.2, 1);
}
\`\`\`

### Implementation Guidelines

**CSS Architecture**
- Utility-first CSS with component-scoped styles
- CSS custom properties for theme consistency
- BEM-like naming for component variants
- Responsive design with mobile-first approach

**Component Library Structure**
\`\`\`
components/
├── atoms/
│   ├── Button/
│   ├── Input/
│   ├── Avatar/
│   └── Icon/
├── molecules/
│   ├── Navigation/
│   ├── Card/
│   └── Testimonial/
└── organisms/
    ├── Header/
    ├── Hero/
    ├── Footer/
    └── ProductShowcase/
\`\`\`

**Responsive Breakpoints**
\`\`\`css
/* Mobile First Approach */
@media (min-width: 640px) { /* sm */ }
@media (min-width: 768px) { /* md */ }
@media (min-width: 1024px) { /* lg */ }
@media (min-width: 1280px) { /* xl */ }
@media (min-width: 1536px) { /* 2xl */ }
\`\`\`

## Footer Component Implementation

The codebase should support:
- shadcn project structure  
- Tailwind CSS
- Typescript

If it doesn't, provide instructions on how to setup project via shadcn CLI, install Tailwind or Typescript.

Determine the default path for components and styles. 
If default path for components is not /components/ui, provide instructions on why it's important to create this folder
Copy-paste this component to /components/ui folder:

\`\`\`tsx
animated-footer.tsx
"use client"
import React, { useEffect, useRef, useState } from "react";

interface LinkItem {
  href: string;
  label: string;
}

interface FooterProps {
  leftLinks: LinkItem[];
  rightLinks: LinkItem[];
  copyrightText: string;
  barCount?: number; 
}

const Footer: React.FC<FooterProps> = ({
  leftLinks,
  rightLinks,
  copyrightText,
  barCount = 23, 
}) => {
  const waveRefs = useRef<(HTMLDivElement | null)[]>([]);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const animationFrameRef = useRef<number | null>(null);

  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 } 
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      if (footerRef.current) {
        observer.unobserve(footerRef.current);
      }
    };
  }, []);


  useEffect(() => {
    let t = 0; 

    const animateWave = () => {
      const waveElements = waveRefs.current;
      let offset = 0;

      waveElements.forEach((element, index) => {
        if (element) {
          offset += Math.max(0, 20 * Math.sin((t + index) * 0.3)); 
          element.style.transform = \`translateY(\${index + offset}px)\`;
        }
      });

      t += 0.1;
      animationFrameRef.current = requestAnimationFrame(animateWave);
    };

    if (isVisible) {
      animateWave();
    } else if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isVisible]);

  return (
    <footer
      ref={footerRef}
      className="bg-black text-white relative flex flex-col w-full h-full justify-between lg:h-screen select-none"
    >
      <div className="container mx-auto flex flex-col md:flex-row justify-between w-full gap-4 pb-24 pt-8 px-4">
        <div className="space-y-2">
          <ul className="flex flex-wrap gap-4">
            {leftLinks.map((link, index) => (
              <li key={index}>
                <a href={link.href} className="text-sm hover:text-sky-400">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <p className="text-sm mt-4 flex items-center gap-x-1">
            <svg className="size-3" viewBox="0 0 80 80">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                fill="currentColor"
                d="M67.4307 11.5693C52.005 -3.85643 26.995 -3.85643 11.5693 11.5693C-3.85643 26.995 -3.85643 52.005 11.5693 67.4307C26.995 82.8564 52.005 82.8564 67.4307 67.4307C82.8564 52.005 82.8564 26.995 67.4307 11.5693ZM17.9332 17.9332C29.8442 6.02225 49.1558 6.02225 61.0668 17.9332C72.9777 29.8442 72.9777 49.1558 61.0668 61.0668C59.7316 62.4019 58.3035 63.5874 56.8032 64.6232L56.8241 64.6023C46.8657 54.6439 46.8657 38.4982 56.8241 28.5398L58.2383 27.1256L51.8744 20.7617L50.4602 22.1759C40.5018 32.1343 24.3561 32.1343 14.3977 22.1759L14.3768 22.1968C15.4126 20.6965 16.5981 19.2684 17.9332 17.9332ZM34.0282 38.6078C25.6372 38.9948 17.1318 36.3344 10.3131 30.6265C7.56889 39.6809 9.12599 49.76 14.9844 57.6517L34.0282 38.6078ZM21.3483 64.0156C29.24 69.874 39.3191 71.4311 48.3735 68.6869C42.6656 61.8682 40.0052 53.3628 40.3922 44.9718L21.3483 64.0156Z"
              />
            </svg>
            {copyrightText}
          </p>
        </div>
        <div className="space-y-4">
          <ul className="flex flex-wrap gap-4">
            {rightLinks.map((link, index) => (
              <li key={index}>
                <a href={link.href} className="text-sm hover:text-sky-400">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <div className="text-right mt-4">
            <button className="text-sm hover:underline inline-flex items-center">
              Back to top
            </button>
          </div>
        </div>
      </div>
      <div
        id="waveContainer"
        aria-hidden="true"
        style={{ overflow: "hidden", height: 200 }}
      >
        <div style={{ marginTop: 0 }}>
          {Array.from({ length: barCount }).map((_, index) => (
            <div
              key={index}
              ref={(el) => { waveRefs.current[index] = el; }}
              className="wave-segment"
              style={{
                height: \`\${index + 1}px\`,
                backgroundColor: "rgb(255, 255, 255)",
                transition: "transform 0.1s ease",
                willChange: "transform",
                marginTop: "-2px",
              }}
            />
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;

demo.tsx
import Footer from "@/components/ui/animated-footer";

const DemoOne = () => {
  return  <Footer
          leftLinks={[
            { href: "/terms", label: "Terms & policies" },
            { href: "/privacy-policy", label: "Privacy policy" },
          ]}
          rightLinks={[
            { href: "/careers", label: "Careers" },
            { href: "/about", label: "About" },
            { href: "/help-center", label: "Help Center" },
            { href: "https://x.com/taher_max_", label: "Twitter" },
            { href: "https://www.instagram.com/taher_max_", label: "Instagram" },
            { href: "https://github.com/tahermaxse", label: "GitHub" },
          ]}
          copyrightText="Cluely 2025. All Rights Reserved"
          barCount={23}
        />;
};

export { DemoOne };

\`\`\`

Implementation Guidelines
 1. Analyze the component structure and identify all required dependencies
 2. Review the component's argumens and state
 3. Identify any required context providers or hooks and install them
 4. Questions to Ask
 - What data/props will be passed to this component?
 - Are there any specific state management requirements?
 - Are there any required assets (images, icons, etc.)?
 - What is the expected responsive behavior?
 - What is the best place to use this component in the app?

Steps to integrate
 0. Copy paste all the code above in the correct directories
 1. Install external dependencies
 2. Fill image assets with Unsplash stock images you know exist
 3. Use lucide-react icons for svgs or logos if component requires them
`
    },
    {
      id: 'cursor',
      name: 'Cursor',
      image: '/designinspoimages/cursor1.png',
      description: 'Modern code editor interface with AI-powered features',
      prompt: `Transform my current website design, CSS styling and overall frontend into the following strict and descriptive design and frontend specification document. Make sure to not change any copy, context or functionality we currently have in the website, this is solely a design UI/UX and frontend redesign.

IMPORTANT COMPONENT LIBRARY REQUIREMENT:
You MUST use shadcn/ui for all UI components to ensure accessibility, consistency, and modern design patterns. shadcn/ui is a collection of copy-and-paste components built on top of Radix UI primitives and styled with version 3 of Tailwind CSS.

Installation & Setup:
1. Install shadcn-ui CLI: npx shadcn-ui@latest init
2. Configure your components.json when prompted
3. Add components as needed: npx shadcn-ui@latest add [component-name]

Key Benefits:
- Fully accessible components built on Radix UI primitives
- Beautifully designed with consistent styling
- Customizable and themeable
- TypeScript support out of the box
- Copy-paste friendly - you own the code
- Works seamlessly with Tailwind CSS

Usage Guidelines:
- Use shadcn/ui components for buttons, forms, modals, navigation, cards, etc.
- Examples: npx shadcn-ui@latest add button card form dialog navigation-menu
- Import components from your components/ui directory
- Customize component variants using the built-in variant system
- Leverage the built-in dark mode support
- Follow the component composition patterns for complex UI elements

Always prioritize shadcn/ui components over custom implementations to maintain design consistency and accessibility standards.

## Part 1: High-Level Design Overview

### Visual Design Philosophy

**Modern Developer-Focused Aesthetic**
- Clean, professional interface with emphasis on code display
- Dark-to-light gradients creating depth and visual interest
- Typography-centric design with code as visual hero
- Geometric shapes and isometric illustrations
- Technical sophistication balanced with accessibility

**Color Palette Strategy**
- **Primary Background**: Light gray (\`#F5F5F5\`) for main surfaces
- **Navigation**: Matching light gray with subtle transparency
- **Gradient Themes**: 
  - Purple-to-blue gradients (\`#6B46C1\` to \`#3B82F6\`)
  - Purple-to-orange gradients (\`#8B5CF6\` to \`#F59E0B\`)
  - Teal-to-green combinations (\`#06B6D4\` to \`#10B981\`)
- **Text Colors**:
  - Primary: Pure black (\`#000000\`)
  - Secondary: Medium gray (\`#4D4D4D\`)
  - Tertiary: Light gray (\`rgba(0,0,0,0.6)\`)
- **Accent Elements**: Multi-colored geometric shapes with rainbow gradients

**Typography Hierarchy**
- **Primary Font**: \`GeistSans\` with fallback to system fonts
- **Monospace Font**: \`GeistMono\` for code display
- **Font Smoothing**: \`-webkit-font-smoothing: antialiased\`
- **Text Rendering**: \`optimizelegibility\` for crisp display
- **Weight System**: 
  - Regular (400) for body text
  - Medium (500) for navigation
  - Bold (600-700) for headlines
- **Line Height**: Consistent 24px base line-height

**Spacing and Layout Principles**
- **Container Strategy**: Max-width 1672px with auto centering
- **Grid Systems**: CSS Grid for complex layouts, Flexbox for components
- **Spacing Scale**: 
  - Micro: 2px, 4px for tight elements
  - Small: 8px, 12px for component padding
  - Medium: 24px, 36px for section spacing
  - Large: 48px, 64px for major layout breaks
- **Border Radius**: Consistent 16px for cards, 4px for buttons
- **Responsive Padding**: 36px horizontal on large screens

### User Experience Patterns

**Navigation Structure**
- **Fixed Header**: Positioned 24px from top with high z-index (400)
- **Grid Layout**: Three-column grid (logo, navigation, actions)
- **Menu Items**: Pricing, Features, Enterprise, Blog, Forum, Careers
- **Primary Action**: Black "Download" button with white text
- **Responsive Behavior**: Collapsible menu for mobile devices

**Content Organization**
- **Hero Sections**: Large headlines with gradient backgrounds
- **Feature Showcase**: Code editor mockups as primary visuals
- **Social Proof**: Company logo grid with trusted brands
- **Testimonial Format**: User cards with avatars and quotes
- **CTA Strategy**: Prominent "Download for free" buttons throughout

**Interaction Patterns**
- **Smooth Transitions**: 0.3s cubic-bezier easing functions
- **Hover Effects**: Subtle opacity and transform changes
- **Code Display**: Syntax-highlighted code blocks with line numbers
- **Progressive Disclosure**: Tabbed interfaces and expandable sections
- **Loading States**: Animation durations of 0.5s for content transitions

**Mobile Responsiveness**
- **Breakpoint Strategy**: Mobile-first responsive design
- **Touch Targets**: Adequate spacing for mobile interactions
- **Content Reflow**: Grid layouts collapse to single columns
- **Typography Scaling**: Responsive font sizes across viewports

### Technical Implementation Summary

**Frontend Architecture**
- **Framework**: React-based with modern JavaScript
- **Styling Approach**: CSS-in-JS with utility classes
- **Animation System**: CSS transitions with cubic-bezier timing
- **Performance**: Optimized font loading and image delivery

**CSS Methodologies**
- **Utility Classes**: Numbered class system (style-0, style-1, etc.)
- **Custom Properties**: CSS variables for consistent theming
- **Layout Systems**: CSS Grid and Flexbox combinations
- **Cross-browser**: Webkit prefixes for optimal compatibility

**Component Architecture**
- **Atomic Design**: Reusable button, card, and navigation components
- **Layout Components**: Container, grid, and section wrappers
- **Interactive Elements**: Form controls and CTA buttons
- **Media Components**: Responsive images and video elements

## Part 2: Detailed Section-by-Section Breakdown

### Navigation Header

**Structure & Layout**
\`\`\`html
<header class="fixed-nav">
  <div class="nav-container">
    <div class="nav-grid">
      <div class="logo-section">
        <img src="cursor-logo.svg" alt="Cursor" />
      </div>
      <nav class="main-navigation">
        <!-- Navigation links -->
      </nav>
      <div class="action-section">
        <button class="download-btn">Download</button>
      </div>
    </div>
  </div>
</header>
\`\`\`

**Typography System**
- **Logo**: SVG-based with mix-blend-mode multiply
- **Navigation Links**: 
  - Font weight: 500 (medium)
  - Color: \`#4D4D4D\` (medium gray)
  - Font size: 14-16px
  - Gap: 24px between items

**Color System**
\`\`\`css
.fixed-nav {
  background-color: #F5F5F5;
  border: 0.625px solid transparent;
  z-index: 400;
}

.nav-link {
  color: #4D4D4D;
  text-decoration: none;
  transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-link:hover {
  color: #000000;
}
\`\`\`

**Component Styling**

**Download Button**
\`\`\`css
.download-btn {
  background: #000000;
  color: #FFFFFF;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.download-btn:hover {
  transform: translateY(-1px);
}
\`\`\`

**Responsive Design**
- **Desktop**: Full horizontal layout with spacing
- **Mobile**: Collapsible hamburger menu
- **Container**: Max-width 1672px with 36px horizontal padding
- **Grid**: \`grid-template-columns: 1fr auto 1fr\`

### Hero Section

**Structure & Layout**
\`\`\`css
.hero-section {
  background: linear-gradient(135deg, #6B46C1 0%, #3B82F6 100%);
  padding: 120px 36px 80px;
  text-align: center;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
\`\`\`

**Typography System**
- **Main Headline**: 
  - Font size: 64px (desktop), scales down responsively
  - Font weight: 700
  - Color: \`#FFFFFF\`
  - Line height: 1.1
  - Text align: center

- **Subtitle**:
  - Font size: 18px
  - Font weight: 400
  - Color: \`rgba(255, 255, 255, 0.9)\`
  - Max width: 600px
  - Line height: 1.5

**Color System**
- **Background**: Purple to blue gradient
- **Text**: White with varying opacity levels
- **CTA Buttons**: 
  - Primary: Black background with white text
  - Secondary: White background with black text

**Interactive Elements**
\`\`\`css
.hero-cta {
  background: #000000;
  color: #FFFFFF;
  border-radius: 12px;
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 500;
  margin-top: 32px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.hero-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}
\`\`\`

**Code Editor Visual**
- **Mock Interface**: Simulated VS Code interface
- **Syntax Highlighting**: Multi-colored code display
- **Line Numbers**: Subtle gray numbering system
- **Chat Integration**: Side panel with AI conversation

### Company Logos Section

**Structure & Layout**
\`\`\`css
.logos-section {
  padding: 80px 36px;
  background: #FFFFFF;
  text-align: center;
}

.logos-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 48px;
  max-width: 1200px;
  margin: 0 auto;
  align-items: center;
}

@media (max-width: 768px) {
  .logos-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }
}
\`\`\`

**Typography System**
- **Section Header**: "TRUSTED BY ENGINEERS AT"
  - Font size: 12px
  - Font weight: 500
  - Color: \`rgba(0, 0, 0, 0.6)\`
  - Letter spacing: 1px
  - Text transform: uppercase

**Logo Styling**
\`\`\`css
.company-logo {
  height: 32px;
  width: auto;
  opacity: 0.7;
  transition: opacity 0.3s ease;
  filter: grayscale(100%);
}

.company-logo:hover {
  opacity: 1;
  filter: grayscale(0%);
}
\`\`\`

**Responsive Behavior**
- **Desktop**: 5-column grid layout
- **Tablet**: 3-column grid
- **Mobile**: 2-column grid
- **Logo Sizing**: Consistent height with auto width

### Feature Sections

**"Tab, tab, tab" Section**

**Structure & Layout**
\`\`\`css
.tab-feature-section {
  padding: 100px 36px;
  background: #FFFFFF;
  text-align: center;
}

.feature-content {
  max-width: 1200px;
  margin: 0 auto;
}

.feature-visual {
  background: linear-gradient(135deg, #8B5CF6 0%, #F59E0B 100%);
  border-radius: 24px;
  padding: 48px;
  margin-top: 48px;
  position: relative;
  overflow: hidden;
}
\`\`\`

**Typography System**
- **Feature Headline**:
  - Font size: 48px
  - Font weight: 700
  - Color: \`#000000\`
  - Line height: 1.2

- **Feature Description**:
  - Font size: 18px
  - Font weight: 400
  - Color: \`rgba(0, 0, 0, 0.7)\`
  - Max width: 600px
  - Margin: 0 auto

**Code Interface Styling**
\`\`\`css
.code-mockup {
  background: #1E1E1E;
  border-radius: 12px;
  padding: 24px;
  font-family: 'GeistMono', monospace;
  font-size: 14px;
  line-height: 20px;
  color: #FFFFFF;
  text-align: left;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3);
}

.line-number {
  color: rgba(255, 255, 255, 0.4);
  margin-right: 16px;
  user-select: none;
}

.syntax-keyword {
  color: #569CD6;
}

.syntax-string {
  color: #CE9178;
}

.syntax-function {
  color: #DCDCAA;
}
\`\`\`

**"Knows your codebase" Section**

**Layout Structure**
\`\`\`css
.codebase-section {
  padding: 100px 36px;
  background: #F8F9FA;
}

.codebase-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  max-width: 1400px;
  margin: 0 auto;
  align-items: center;
}

@media (max-width: 1024px) {
  .codebase-layout {
    grid-template-columns: 1fr;
    gap: 48px;
  }
}
\`\`\`

**Chat Interface Styling**
\`\`\`css
.ai-chat-panel {
  background: #FFFFFF;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  height: fit-content;
}

.chat-message {
  padding: 12px 16px;
  margin: 8px 0;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.4;
}

.user-message {
  background: #F3F4F6;
  color: #111827;
  text-align: right;
}

.ai-message {
  background: #EFF6FF;
  color: #1F2937;
}
\`\`\`

### Feature Cards Section

**Structure & Layout**
\`\`\`css
.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  max-width: 1200px;
  margin: 80px auto 0;
  padding: 0 36px;
}

@media (max-width: 1024px) {
  .features-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}
\`\`\`

**Card Component Styling**
\`\`\`css
.feature-card {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 32px;
  text-align: left;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #6B46C1, #3B82F6, #10B981);
}
\`\`\`

**Typography in Cards**
\`\`\`css
.card-headline {
  font-size: 24px;
  font-weight: 600;
  color: #000000;
  margin-bottom: 16px;
  line-height: 1.3;
}

.card-description {
  font-size: 16px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.5;
  margin-bottom: 24px;
}
\`\`\`

**Geometric Illustrations**
\`\`\`css
.card-visual {
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, var(--gradient-start), var(--gradient-end));
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  position: relative;
}

.geometric-shape {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

/* Individual card color variations */
.frontier-card { --gradient-start: #6B46C1; --gradient-end: #3B82F6; }
.familiar-card { --gradient-start: #10B981; --gradient-end: #06B6D4; }
.privacy-card { --gradient-start: #F59E0B; --gradient-end: #EF4444; }
\`\`\`

### Testimonials Section

**Structure & Layout**
\`\`\`css
.testimonials-section {
  padding: 100px 36px;
  background: #F8F9FA;
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  max-width: 1000px;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .testimonials-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}
\`\`\`

**Testimonial Card Styling**
\`\`\`css
.testimonial-card {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.testimonial-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.testimonial-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #E5E7EB;
}

.user-info h4 {
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  margin: 0;
}

.user-info p {
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  margin: 4px 0 0 0;
}
\`\`\`

**Quote Styling**
\`\`\`css
.testimonial-quote {
  font-size: 16px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.8);
  line-height: 1.6;
  position: relative;
  padding-left: 20px;
}

.testimonial-quote::before {
  content: '"';
  position: absolute;
  left: 0;
  top: -5px;
  font-size: 24px;
  color: #6B46C1;
  font-weight: 700;
}
\`\`\`

## Testimonials Component Implementation

The codebase should support:
- shadcn project structure  
- Tailwind CSS
- Typescript

If it doesn't, provide instructions on how to setup project via shadcn CLI, install Tailwind or Typescript.

Determine the default path for components and styles. 
If default path for components is not /components/ui, provide instructions on why it's important to create this folder
Copy-paste this component to /components/ui folder:

\`\`\`tsx
testimonials-columns-1.tsx
"use client";
import React from "react";
import { motion } from "motion/react";


export const TestimonialsColumn = (props: {
  className?: string;
  testimonials: typeof testimonials;
  duration?: number;
}) => {
  return (
    <div className={props.className}>
      <motion.div
        animate={{
          translateY: "-50%",
        }}
        transition={{
          duration: props.duration || 10,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop",
        }}
        className="flex flex-col gap-6 pb-6 bg-background"
      >
        {[
          ...new Array(2).fill(0).map((_, index) => (
            <React.Fragment key={index}>
              {props.testimonials.map(({ text, image, name, role }, i) => (
                <div className="p-10 rounded-3xl border shadow-lg shadow-primary/10 max-w-xs w-full" key={i}>
                  <div>{text}</div>
                  <div className="flex items-center gap-2 mt-5">
                    <img
                      width={40}
                      height={40}
                      src={image}
                      alt={name}
                      className="h-10 w-10 rounded-full"
                    />
                    <div className="flex flex-col">
                      <div className="font-medium tracking-tight leading-5">{name}</div>
                      <div className="leading-5 opacity-60 tracking-tight">{role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </React.Fragment>
          )),
        ]}
      </motion.div>
    </div>
  );
};

;

demo.tsx
import { TestimonialsColumn } from "@/components/ui/testimonials-columns-1";
import { motion } from "motion/react";

const testimonials = [
  {
    text: "This ERP revolutionized our operations, streamlining finance and inventory. The cloud-based platform keeps us productive, even remotely.",
    image: "https://randomuser.me/api/portraits/women/1.jpg",
    name: "Briana Patton",
    role: "Operations Manager",
  },
  {
    text: "Implementing this ERP was smooth and quick. The customizable, user-friendly interface made team training effortless.",
    image: "https://randomuser.me/api/portraits/men/2.jpg",
    name: "Bilal Ahmed",
    role: "IT Manager",
  },
  {
    text: "The support team is exceptional, guiding us through setup and providing ongoing assistance, ensuring our satisfaction.",
    image: "https://randomuser.me/api/portraits/women/3.jpg",
    name: "Saman Malik",
    role: "Customer Support Lead",
  },
  {
    text: "This ERP's seamless integration enhanced our business operations and efficiency. Highly recommend for its intuitive interface.",
    image: "https://randomuser.me/api/portraits/men/4.jpg",
    name: "Omar Raza",
    role: "CEO",
  },
  {
    text: "Its robust features and quick support have transformed our workflow, making us significantly more efficient.",
    image: "https://randomuser.me/api/portraits/women/5.jpg",
    name: "Zainab Hussain",
    role: "Project Manager",
  },
  {
    text: "The smooth implementation exceeded expectations. It streamlined processes, improving overall business performance.",
    image: "https://randomuser.me/api/portraits/women/6.jpg",
    name: "Aliza Khan",
    role: "Business Analyst",
  },
  {
    text: "Our business functions improved with a user-friendly design and positive customer feedback.",
    image: "https://randomuser.me/api/portraits/men/7.jpg",
    name: "Farhan Siddiqui",
    role: "Marketing Director",
  },
  {
    text: "They delivered a solution that exceeded expectations, understanding our needs and enhancing our operations.",
    image: "https://randomuser.me/api/portraits/women/8.jpg",
    name: "Sana Sheikh",
    role: "Sales Manager",
  },
  {
    text: "Using this ERP, our online presence and conversions significantly improved, boosting business performance.",
    image: "https://randomuser.me/api/portraits/men/9.jpg",
    name: "Hassan Ali",
    role: "E-commerce Manager",
  },
];


const firstColumn = testimonials.slice(0, 3);
const secondColumn = testimonials.slice(3, 6);
const thirdColumn = testimonials.slice(6, 9);


const Testimonials = () => {
  return (
    <section className="bg-background my-20 relative">

      <div className="container z-10 mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="flex flex-col items-center justify-center max-w-[540px] mx-auto"
        >
          <div className="flex justify-center">
            <div className="border py-1 px-4 rounded-lg">Testimonials</div>
          </div>

          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tighter mt-5">
            What our users say
          </h2>
          <p className="text-center mt-5 opacity-75">
            See what our customers have to say about us.
          </p>
        </motion.div>

        <div className="flex justify-center gap-6 mt-10 [mask-image:linear-gradient(to_bottom,transparent,black_25%,black_75%,transparent)] max-h-[740px] overflow-hidden">
          <TestimonialsColumn testimonials={firstColumn} duration={15} />
          <TestimonialsColumn testimonials={secondColumn} className="hidden md:block" duration={19} />
          <TestimonialsColumn testimonials={thirdColumn} className="hidden lg:block" duration={17} />
        </div>
      </div>
    </section>
  );
};

export default { Testimonials };

\`\`\`

Install NPM dependencies:
\`\`\`bash
motion
\`\`\`

Implementation Guidelines
 1. Analyze the component structure and identify all required dependencies
 2. Review the component's argumens and state
 3. Identify any required context providers or hooks and install them
 4. Questions to Ask
 - What data/props will be passed to this component?
 - Are there any specific state management requirements?
 - Are there any required assets (images, icons, etc.)?
 - What is the expected responsive behavior?
 - What is the best place to use this component in the app?

Steps to integrate
 0. Copy paste all the code above in the correct directories
 1. Install external dependencies
 2. Fill image assets with Unsplash stock images you know exist
 3. Use lucide-react icons for svgs or logos if component requires them

### Final CTA Section

**Structure & Layout**
\`\`\`css
.final-cta-section {
  padding: 120px 36px;
  background: #FFFFFF;
  text-align: center;
  position: relative;
}

.cta-content {
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.geometric-bg {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 400px;
  height: 400px;
  z-index: 1;
}
\`\`\`

**Typography System**
\`\`\`css
.cta-headline {
  font-size: 72px;
  font-weight: 700;
  color: #000000;
  line-height: 1.1;
  margin-bottom: 32px;
}

@media (max-width: 768px) {
  .cta-headline {
    font-size: 48px;
  }
}
\`\`\`

**Geometric Illustration**
\`\`\`css
.isometric-cube {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #06B6D4, #3B82F6, #6B46C1, #F59E0B, #10B981);
  clip-path: polygon(
    50% 0%,
    100% 38%,
    82% 100%,
    18% 100%,
    0% 38%
  );
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
}
\`\`\`

### Footer Section

**Structure & Layout**
\`\`\`css
.footer {
  background: #FFFFFF;
  padding: 64px 36px 32px;
  border-top: 1px solid #E5E7EB;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr repeat(4, auto);
  gap: 64px;
  align-items: start;
}

@media (max-width: 1024px) {
  .footer-content {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
  }
}

@media (max-width: 640px) {
  .footer-content {
    grid-template-columns: 1fr;
    gap: 32px;
  }
}
\`\`\`

**Footer Links**
\`\`\`css
.footer-column h3 {
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-column ul li {
  margin-bottom: 12px;
}

.footer-column a {
  color: rgba(0, 0, 0, 0.6);
  text-decoration: none;
  font-size: 14px;
  font-weight: 400;
  transition: color 0.3s ease;
}

.footer-column a:hover {
  color: #000000;
}
\`\`\`

**Social Links**
\`\`\`css
.social-links {
  display: flex;
  gap: 16px;
  margin-top: 24px;
}

.social-link {
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.6);
  transition: all 0.3s ease;
}

.social-link:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #000000;
  transform: translateY(-2px);
}
\`\`\`

### Design Token Documentation

**Color System**
\`\`\`css
:root {
  /* Primary Colors */
  --color-white: #FFFFFF;
  --color-black: #000000;
  --color-gray-50: #F9FAFB;
  --color-gray-100: #F3F4F6;
  --color-gray-200: #E5E7EB;
  --color-gray-300: #D1D5DB;
  --color-gray-400: #9CA3AF;
  --color-gray-500: #6B7280;
  --color-gray-600: #4B5563;
  --color-gray-700: #374151;
  --color-gray-800: #1F2937;
  --color-gray-900: #111827;
  
  /* Background Colors */
  --bg-primary: #F5F5F5;
  --bg-secondary: #FFFFFF;
  --bg-tertiary: #F8F9FA;
  
  /* Gradient Colors */
  --purple-500: #6B46C1;
  --purple-600: #8B5CF6;
  --blue-500: #3B82F6;
  --blue-600: #2563EB;
  --teal-500: #06B6D4;
  --green-500: #10B981;
  --orange-500: #F59E0B;
  --red-500: #EF4444;
  
  /* Text Colors */
  --text-primary: rgba(0, 0, 0, 1);
  --text-secondary: rgba(0, 0, 0, 0.7);
  --text-tertiary: rgba(0, 0, 0, 0.6);
  --text-quaternary: rgba(0, 0, 0, 0.4);
}
\`\`\`

**Typography Scale**
\`\`\`css
:root {
  /* Font Families */
  --font-sans: 'GeistSans', 'GeistSans Fallback', system-ui, sans-serif;
  --font-mono: 'GeistMono', ui-monospace, 'SFMono-Regular', monospace;
  
  /* Font Sizes */
  --text-xs: 12px;
  --text-sm: 14px;
  --text-base: 16px;
  --text-lg: 18px;
  --text-xl: 20px;
  --text-2xl: 24px;
  --text-3xl: 32px;
  --text-4xl: 48px;
  --text-5xl: 64px;
  --text-6xl: 72px;
  
  /* Font Weights */
  --font-normal: 400;
  --font-medium: 500;
  --font-semibold: 600;
  --font-bold: 700;
  
  /* Line Heights */
  --leading-none: 1;
  --leading-tight: 1.1;
  --leading-snug: 1.2;
  --leading-normal: 1.5;
  --leading-relaxed: 1.6;
}
\`\`\`

**Spacing Scale**
\`\`\`css
:root {
  --space-1: 4px;
  --space-2: 8px;
  --space-3: 12px;
  --space-4: 16px;
  --space-5: 20px;
  --space-6: 24px;
  --space-8: 32px;
  --space-10: 40px;
  --space-12: 48px;
  --space-16: 64px;
  --space-20: 80px;
  --space-24: 96px;
  --space-32: 128px;
}
\`\`\`

**Border Radius System**
\`\`\`css
:root {
  --radius-none: 0;
  --radius-sm: 4px;
  --radius-base: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 24px;
  --radius-full: 50%;
}
\`\`\`

**Shadow System**
\`\`\`css
:root {
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-base: 0 4px 16px rgba(0, 0, 0, 0.08);
  --shadow-md: 0 8px 24px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 16px 48px rgba(0, 0, 0, 0.15);
  --shadow-xl: 0 24px 64px rgba(0, 0, 0, 0.2);
}
\`\`\`

**Animation Timing**
\`\`\`css
:root {
  --duration-fast: 0.15s;
  --duration-normal: 0.3s;
  --duration-slow: 0.5s;
  
  --ease-in: cubic-bezier(0.4, 0, 1, 1);
  --ease-out: cubic-bezier(0, 0, 0.2, 1);
  --ease-in-out: cubic-bezier(0.4, 0, 0.2, 1);
}
\`\`\`

**Gradient Definitions**
\`\`\`css
:root {
  --gradient-purple-blue: linear-gradient(135deg, #6B46C1 0%, #3B82F6 100%);
  --gradient-purple-orange: linear-gradient(135deg, #8B5CF6 0%, #F59E0B 100%);
  --gradient-teal-green: linear-gradient(135deg, #06B6D4 0%, #10B981 100%);
  --gradient-rainbow: linear-gradient(90deg, #6B46C1, #3B82F6, #10B981, #F59E0B, #EF4444);
}
\`\`\`

### Implementation Guidelines

**Component Architecture**
\`\`\`
components/
├── atoms/
│   ├── Button/
│   ├── Input/
│   ├── Avatar/
│   ├── Logo/
│   └── Icon/
├── molecules/
│   ├── Navigation/
│   ├── FeatureCard/
│   ├── TestimonialCard/
│   └── CodeBlock/
└── organisms/
    ├── Header/
    ├── HeroSection/
    ├── FeatureGrid/
    ├── TestimonialGrid/
    └── Footer/
\`\`\`

**Responsive Breakpoints**
\`\`\`css
/* Mobile First Approach */
@media (min-width: 640px) { /* sm */ }
@media (min-width: 768px) { /* md */ }
@media (min-width: 1024px) { /* lg */ }
@media (min-width: 1280px) { /* xl */ }
@media (min-width: 1536px) { /* 2xl */ }
\`\`\`

**Performance Considerations**
- **Font Loading**: \`font-display: swap\` for web fonts
- **Image Optimization**: WebP format with fallbacks
- **Animation**: \`transform\` and \`opacity\` for GPU acceleration
- **Layout Shifts**: Explicit dimensions for dynamic content
- **Loading States**: Skeleton screens for code blocks
`
    },
    {
      id: 'netflix',
      name: 'Netflix',
      image: '/designinspoimages/netflix1.png',
      description: 'Streaming platform with immersive content discovery',
      prompt: ` Design specs for a Netflix ui inspired design.

IMPORTANT COMPONENT LIBRARY REQUIREMENT:
You MUST use shadcn/ui for all UI components to ensure accessibility, consistency, and modern design patterns. shadcn/ui is a collection of copy-and-paste components built on top of Radix UI primitives and styled with version 3 of Tailwind CSS.

Installation & Setup:
1. Install shadcn/ui CLI: npx shadcn-ui@latest init
2. Configure your components.json when prompted
3. Add components as needed: npx shadcn-ui@latest add [component-name]

Key Benefits:
- Fully accessible components built on Radix UI primitives
- Beautifully designed with consistent styling
- Customizable and themeable
- TypeScript support out of the box
- Copy-paste friendly - you own the code
- Works seamlessly with Tailwind CSS

Usage Guidelines:
- Use shadcn/ui components for buttons, forms, modals, navigation, cards, etc.
- Examples: npx shadcn-ui@latest add button card form dialog navigation-menu
- Import components from your components/ui directory
- Customize component variants using the built-in variant system
- Leverage the built-in dark mode support
- Follow the component composition patterns for complex UI elements

Always prioritize shadcn/ui components over custom implementations to maintain design consistency and accessibility standards.
## Part 1: High-Level Design Overview

### Visual Design Philosophy

**Overall Aesthetic Approach**: Bold cinematic design with dark theme dominance
- Heavy emphasis on visual hierarchy through dramatic contrast
- Content-forward approach showcasing media thumbnails prominently
- Minimalist text overlaid on rich visual backgrounds
- Premium entertainment brand aesthetic with sophisticated color treatment

**Color Palette Strategy and Psychology**:
- Primary: \`#E50914\` (Signature red) - Creates urgency and excitement
- Background: \`#000000\` (Pure black) - Cinema-like immersive experience
- Text Primary: \`#FFFFFF\` (Pure white) - Maximum contrast for readability
- Accent: \`rgba(255, 255, 255, 0.7)\` - Subtle secondary text treatment
- Gradients: Complex multi-stop linear/radial gradients for depth

**Typography Hierarchy and Personality**:
- Custom font stack: \`'Netflix Sans', 'Helvetica Neue', 'Segoe UI', Roboto, Ubuntu, sans-serif\`
- Weight range: 400 (regular) to 900 (black) for dramatic hierarchy
- Size range: 14px (small UI text) to 56px (hero headlines)
- Line height ratio: 1.25-1.4 for optimal readability
- Letter spacing: Tight (-0.5px to 0px) for modern appearance

**Spacing and Layout Principles**:
- 8px base unit grid system (8, 16, 24, 32, 48px increments)
- Golden ratio proportions in section heights
- Generous whitespace around key CTAs
- Asymmetric layouts balanced with visual weight

**Visual Rhythm and Grid Systems**:
- 12-column responsive grid with fluid containers
- Max-width constraint: 1920px for ultra-wide screens
- Consistent 16px gutters with responsive scaling
- Vertical rhythm based on 24px baseline grid

### User Experience Patterns

**Navigation Structure and Behavior**:
- Minimalist header with logo prominence and single CTA
- Fixed positioning with subtle background overlay
- Mobile-first responsive collapse patterns
- Clear visual hierarchy: Brand > Actions

**Interaction Patterns and Micro-animations**:
- 0.25s cubic-bezier(0.4, 0, 0.68, 0.06) standard easing
- Hover states with opacity and background color transitions
- Form focus states with border color animation
- Smooth scroll-triggered animations

**Content Organization and Information Architecture**:
- Above-the-fold: Value proposition + immediate action
- Progressive disclosure: FAQ accordions
- Social proof through trending content visualization
- Benefit-focused feature grid layout

**Mobile Responsiveness Approach**:
- Mobile-first CSS methodology
- Fluid typography with clamp() functions
- Touch-optimized button sizes (minimum 44px)
- Stack-first layouts with horizontal arrangement on larger screens

**Accessibility Considerations**:
- Semantic HTML structure with proper headings
- ARIA labels and hidden text for screen readers
- Sufficient color contrast ratios (4.5:1 minimum)
- Keyboard navigation support

### Technical Implementation Summary

**Frontend Frameworks and Libraries**:
- Pure HTML/CSS implementation option
- React JSX component architecture option
- No external UI library dependencies
- Custom CSS properties system

**CSS Methodologies**:
- Utility-first approach with numbered classes (.style-0, .style-1)
- CSS-in-JS for React implementation
- Custom properties for consistent theming
- BEM-like naming for component variants

**Animation/Transition Technologies**:
- CSS transforms and transitions
- Keyframe animations for complex movements
- GPU-accelerated properties (transform, opacity)
- Reduced motion media query support

**Responsive Design Approach**:
- Mobile-first breakpoint strategy
- Fluid layouts with percentage-based widths
- Flexible box and CSS Grid for complex layouts
- Progressive enhancement philosophy

**Component Architecture Patterns**:
- Container/Presentational component separation
- Atomic design methodology (atoms, molecules, organisms)
- Reusable layout components with prop-based variants
- CSS modules for style encapsulation

---

## Part 2: Detailed Section-by-Section Breakdown

### Header Navigation Section

#### Structure & Layout

**HTML Structure**:
\`\`\`html
<header data-layout="container" data-uia="header+container" dir="ltr">
  <div data-layout="item"> <!-- Logo container -->
    <span data-uia="nmhp-card-header+logo">
      <!-- SVG Logo -->
    </span>
  </div>
  <div data-layout="item"> <!-- CTA container -->
    <a data-uia="header-login-link" href="/gb/login">Sign In</a>
  </div>
</header>
\`\`\`

**Layout Implementation**:
- Container: \`display: inline-flex\`
- Height: \`88px\` fixed
- Max-width: \`1920px\`
- Padding: \`0px 148px\` (horizontal only)
- Justify-content: \`space-between\`
- Align-items: \`center\`

**Flexbox Details**:
\`\`\`css
.header-container {
  display: inline-flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 88px;
  margin-left: -16px; /* Negative margin for grid alignment */
  width: calc(100% + 16px);
}
\`\`\`

#### Typography System

**Logo Typography**:
- SVG implementation: \`viewBox="0 0 111 30"\`
- Width: \`9.25rem\` (148px)
- Height: \`40px\`
- Color: \`rgb(229, 9, 20)\` (#E50914)
- Accessible text: "Netflix" (visually hidden)

**CTA Button Typography**:
- Font-family: \`'Netflix Sans', 'Helvetica Neue', 'Segoe UI', Roboto, Ubuntu, sans-serif\`
- Font-size: \`14px\`
- Font-weight: \`500\` (medium)
- Line-height: \`14px\` (1.0 ratio)
- Letter-spacing: \`normal\`

#### Color System

**Header Background**:
\`\`\`css
.header-overlay {
  background: linear-gradient(
    rgba(0, 0, 0, 0.8) 0%, 
    rgba(0, 0, 0, 0.79) 8.333%, 
    rgba(0, 0, 0, 0.757) 16.67%, 
    /* ... continues with opacity fade */
    rgba(0, 0, 0, 0) 100%
  );
}
\`\`\`

**Color Variables**:
- Primary red: \`#E50914\`
- Text primary: \`#FFFFFF\`
- Background overlay: \`rgba(0, 0, 0, 0.8)\`

#### Component Styling

**Sign In Button**:
\`\`\`css
.cta-button {
  background-color: rgb(229, 9, 20);
  border: 0px none;
  border-radius: 4px;
  color: rgb(255, 255, 255);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  min-height: 32px;
  padding: 4px 16px;
  transition-duration: 0.25s;
  transition-property: background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.68, 0.06);
}
\`\`\`

#### Interactive Elements

**Button Hover States**:
- Transition duration: \`0.25s\`
- Easing: \`cubic-bezier(0.4, 0, 0.68, 0.06)\`
- Properties: \`background-color, border-color\`
- No explicit hover color defined (browser default darkening)

#### Responsive Design

**Mobile Breakpoints**:
\`\`\`css
@media (max-width: 768px) {
  .header-container {
    padding: 0px 16px; /* Reduced from 148px */
    height: 64px; /* Reduced from 88px */
  }
  
  .logo {
    width: 7rem; /* Reduced from 9.25rem */
  }
}
\`\`\`

---

### Hero Section

#### Structure & Layout

**HTML Structure**:
\`\`\`html
<div data-uia="nmhp-hero">
  <div> <!-- Background container -->
    <div data-uia="hero-vlv">
      <!-- Background image with gradient overlay -->
    </div>
  </div>
  <div> <!-- Content container -->
    <div> <!-- Text content -->
      <h1>Unlimited films, series and more</h1>
      <p>Starts at £5.99. Cancel at any time.</p>
    </div>
    <div> <!-- Email form -->
      <form data-uia="email-form">
        <!-- Form elements -->
      </form>
    </div>
  </div>
</div>
\`\`\`

**Layout Measurements**:
- Section height: \`826.398px\`
- Content max-width: \`588px\`
- Content padding: \`0px 32px 32px\`
- Background positioning: \`50% 50%\` (center)
- Min-height content area: \`658.4px\`

#### Typography System

**Hero Headline (H1)**:
\`\`\`css
.hero-headline {
  font-family: 'Netflix Sans', 'Helvetica Neue', 'Segoe UI', Roboto, Ubuntu, sans-serif;
  font-size: 56px;
  font-weight: 900; /* Black weight */
  line-height: 70px; /* 1.25 ratio */
  color: rgb(255, 255, 255);
  margin-bottom: 16px;
  text-align: center;
}
\`\`\`

**Hero Subheadline (P)**:
\`\`\`css
.hero-subheadline {
  font-family: 'Netflix Sans', 'Helvetica Neue', 'Segoe UI', Roboto, Ubuntu, sans-serif;
  font-size: 20px;
  font-weight: 500; /* Medium weight */
  color: rgb(255, 255, 255);
  margin-bottom: 32px;
  text-align: center;
}
\`\`\`

**Form Label Typography**:
\`\`\`css
.form-label {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: rgba(255, 255, 255, 0.7); /* 70% opacity */
}
\`\`\`

#### Color System

**Background Gradient System**:
\`\`\`css
.hero-background {
  background-image: linear-gradient(
    7deg, 
    rgba(0, 0, 0, 0.85) 10%, 
    rgba(0, 0, 0, 0.847) 17.25%, 
    rgba(0, 0, 0, 0.835) 24.5%, 
    rgba(0, 0, 0, 0.82) 31.75%,
    /* ... continues with gradual opacity reduction */
    rgba(0, 0, 0, 0.6) 97%
  );
}
\`\`\`

**Radial Gradient Accents**:
\`\`\`css
.hero-accent-gradient {
  background: radial-gradient(
    11% 56% at 17% 50%, 
    rgb(70, 21, 24) 0%, 
    rgba(0, 0, 0, 0) 100%
  ), radial-gradient(
    11% 56% at 83% 50%, 
    rgb(70, 21, 24) 0%, 
    rgba(0, 0, 0, 0) 100%
  );
}
\`\`\`

#### Component Styling

**Email Input Field**:
\`\`\`css
.email-input {
  background: rgba(22, 22, 22, 0.7);
  border: 1px solid rgba(128, 128, 128, 0.7);
  border-radius: 4px;
  color: rgb(255, 255, 255);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  padding: 24px 16px 8px; /* Top padding for floating label */
  width: 100%;
  min-width: 200px;
}
\`\`\`

**Get Started CTA Button**:
\`\`\`css
.get-started-button {
  background: rgb(229, 9, 20);
  border: 0px none;
  border-radius: 4px;
  color: rgb(255, 255, 255);
  cursor: pointer;
  font-size: 24px; /* Larger than header CTA */
  font-weight: 500;
  min-height: 56px; /* Larger than header CTA */
  padding: 12px 24px;
  margin-left: 8px;
  flex: 0 0 auto; /* No flex grow/shrink */
  transition-duration: 0.25s;
  transition-property: background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.68, 0.06);
}
\`\`\`

**Chevron Icon**:
\`\`\`css
.button-icon {
  width: 1.5rem; /* 24px */
  height: 24px;
  margin-left: 12px;
  fill: currentColor; /* Inherits button text color */
}
\`\`\`

#### Interactive Elements

**Form Container Layout**:
\`\`\`css
.email-form-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  text-align: left;
  margin: 1rem auto 0px;
  padding-top: 16px;
  max-width: 783px;
}
\`\`\`

**Floating Label Animation**:
\`\`\`css
.floating-label {
  position: absolute;
  left: 16px;
  top: 16px;
  z-index: 1;
  transition-property: top, font-size, line-height;
  transition-duration: 0.25s;
  transition-timing-function: cubic-bezier(0.4, 0, 0.68, 0.06);
  pointer-events: none;
}

.floating-label.active {
  top: 8px;
  font-size: 12px;
  line-height: 16px;
}
\`\`\`

#### Responsive Design

**Mobile Hero Typography**:
\`\`\`css
@media (max-width: 768px) {
  .hero-headline {
    font-size: 32px; /* Reduced from 56px */
    line-height: 40px; /* Reduced from 70px */
  }
  
  .hero-subheadline {
    font-size: 18px; /* Reduced from 20px */
  }
}
\`\`\`

**Mobile Form Layout**:
\`\`\`css
@media (max-width: 768px) {
  .email-form-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .get-started-button {
    margin-left: 0;
    margin-top: 16px;
    width: 100%;
  }
}
\`\`\`

---

### Trending Content Section

#### Structure & Layout

**Section Container**:
\`\`\`css
.trending-section {
  padding: 80px 0; /* Vertical spacing */
  background: #000000;
}

.trending-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}
\`\`\`

**Content Grid**:
\`\`\`css
.trending-grid {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding: 0 0 16px 0; /* Bottom padding for scrollbar */
}

.trending-item {
  flex: 0 0 200px; /* Fixed width, no grow/shrink */
  scroll-snap-align: start;
  position: relative;
}
\`\`\`

#### Typography System

**Section Heading**:
\`\`\`css
.trending-heading {
  font-size: 32px;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 24px;
  line-height: 1.2;
}
\`\`\`

**Ranking Numbers**:
\`\`\`css
.ranking-number {
  position: absolute;
  left: -12px;
  bottom: -8px;
  font-size: 120px;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.1);
  line-height: 1;
  z-index: 1;
}
\`\`\`

#### Component Styling

**Content Cards**:
\`\`\`css
.trending-card {
  background: rgba(20, 20, 20, 0.8);
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 16/9;
  position: relative;
  backdrop-filter: blur(10px);
}

.trending-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.trending-card:hover img {
  transform: scale(1.05);
}
\`\`\`

**Netflix Badge**:
\`\`\`css
.netflix-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #E50914;
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 3px;
  letter-spacing: 0.5px;
}
\`\`\`

---

### Features Grid Section

#### Structure & Layout

**Grid Container**:
\`\`\`css
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 32px;
  padding: 80px 24px;
  max-width: 1200px;
  margin: 0 auto;
}
\`\`\`

#### Typography System

**Section Heading**:
\`\`\`css
.features-section-title {
  font-size: 40px;
  font-weight: 700;
  color: #FFFFFF;
  text-align: center;
  margin-bottom: 48px;
  line-height: 1.1;
}
\`\`\`

**Feature Card Titles**:
\`\`\`css
.feature-title {
  font-size: 32px;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 16px;
  line-height: 1.2;
}
\`\`\`

**Feature Descriptions**:
\`\`\`css
.feature-description {
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin-bottom: 24px;
}
\`\`\`

#### Component Styling

**Feature Cards**:
\`\`\`css
.feature-card {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 16px;
  padding: 48px 32px;
  text-align: left;
  position: relative;
  overflow: hidden;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
\`\`\`

**Feature Icons**:
\`\`\`css
.feature-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
  opacity: 0.8;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}
\`\`\`

#### Interactive Elements

**Card Hover Effects**:
\`\`\`css
.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
\`\`\`

---

### FAQ Section

#### Structure & Layout

**FAQ Container**:
\`\`\`css
.faq-section {
  background: #000000;
  padding: 80px 24px;
}

.faq-container {
  max-width: 1000px;
  margin: 0 auto;
}
\`\`\`

#### Typography System

**FAQ Section Title**:
\`\`\`css
.faq-title {
  font-size: 48px;
  font-weight: 700;
  color: #FFFFFF;
  text-align: center;
  margin-bottom: 48px;
  line-height: 1.1;
}
\`\`\`

**Question Text**:
\`\`\`css
.faq-question {
  font-size: 24px;
  font-weight: 400;
  color: #FFFFFF;
  line-height: 1.3;
  padding: 24px;
  margin: 0;
}
\`\`\`

**Answer Text**:
\`\`\`css
.faq-answer {
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  padding: 0 24px 24px 24px;
  margin: 0;
}
\`\`\`

#### Component Styling

**FAQ Items**:
\`\`\`css
.faq-item {
  background: rgba(45, 45, 45, 1);
  margin-bottom: 8px;
  border-radius: 0;
  overflow: hidden;
}

.faq-button {
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;
}

.faq-button:hover {
  background: rgba(55, 55, 55, 1);
}
\`\`\`

**Plus/Minus Icons**:
\`\`\`css
.faq-icon {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  color: #FFFFFF;
  transition: transform 0.3s ease;
}

.faq-item.active .faq-icon {
  transform: translateY(-50%) rotate(45deg);
}
\`\`\`

#### Interactive Elements

**Accordion Animation**:
\`\`\`css
.faq-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.faq-item.active .faq-content {
  max-height: 300px; /* Approximate max height */
}
\`\`\`

---

### Footer Section

#### Structure & Layout

**Footer Container**:
\`\`\`css
.footer {
  background: #000000;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 80px 24px 40px;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
}
\`\`\`

**Email Signup (Footer)**:
\`\`\`css
.footer-signup {
  text-align: center;
  margin-bottom: 64px;
}

.footer-signup-form {
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  gap: 8px;
}
\`\`\`

#### Typography System

**Footer CTA Text**:
\`\`\`css
.footer-cta-text {
  font-size: 18px;
  font-weight: 400;
  color: #FFFFFF;
  margin-bottom: 24px;
  line-height: 1.4;
}
\`\`\`

**Footer Links**:
\`\`\`css
.footer-links {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 32px 48px;
  margin-bottom: 48px;
}

.footer-link {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 400;
  text-decoration: underline;
  line-height: 1.5;
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: rgba(255, 255, 255, 0.9);
}
\`\`\`

**Footer Meta Text**:
\`\`\`css
.footer-meta {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  margin-top: 32px;
}
\`\`\`

#### Component Styling

**Language Selector**:
\`\`\`css
.language-selector {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  color: #FFFFFF;
  font-size: 14px;
  padding: 8px 32px 8px 16px;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,..."); /* Custom dropdown arrow */
  background-repeat: no-repeat;
  background-position: right 12px center;
}
\`\`\`

**Phone Number Link**:
\`\`\`css
.phone-link {
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 400;
  text-decoration: underline;
  margin-bottom: 32px;
  display: inline-block;
}
\`\`\`

---

## Code Implementation Examples

### JSX Component Structure
\`\`\`jsx
const StreamingLandingPage = () => {
  return (
    <div style={{
      background: 'rgb(0, 0, 0)',
      color: 'rgb(255, 255, 255)',
      fontFamily: '"Netflix Sans", "Helvetica Neue", "Segoe UI", Roboto, Ubuntu, sans-serif'
    }}>
      <Header />
      <HeroSection />
      <TrendingSection />
      <FeaturesGrid />
      <FAQSection />
      <Footer />
    </div>
  );
};
\`\`\`

### CSS Custom Properties System
\`\`\`css
:root {
  /* Colors */
  --primary-red: #E50914;
  --background-black: #000000;
  --text-primary: #FFFFFF;
  --text-secondary: rgba(255, 255, 255, 0.7);
  --text-muted: rgba(255, 255, 255, 0.5);
  
  /* Typography */
  --font-family-primary: 'Netflix Sans', 'Helvetica Neue', 'Segoe UI', Roboto, Ubuntu, sans-serif;
  --font-size-xs: 12px;
  --font-size-sm: 14px;
  --font-size-base: 16px;
  --font-size-lg: 18px;
  --font-size-xl: 20px;
  --font-size-2xl: 24px;
  --font-size-3xl: 32px;
  --font-size-4xl: 40px;
  --font-size-5xl: 48px;
  --font-size-6xl: 56px;
  
  /* Spacing */
  --spacing-xs: 8px;
  --spacing-sm: 16px;
  --spacing-md: 24px;
  --spacing-lg: 32px;
  --spacing-xl: 48px;
  --spacing-2xl: 64px;
  --spacing-3xl: 80px;
  
  /* Transitions */
  --transition-fast: 0.15s;
  --transition-base: 0.25s;
  --transition-slow: 0.3s;
  --ease-out: cubic-bezier(0.4, 0, 0.2, 1);
  --ease-in-out: cubic-bezier(0.4, 0, 0.68, 0.06);
}
\`\`\`

### Responsive Breakpoint System
\`\`\`css
/* Mobile First Approach */
.container {
  padding: 0 16px;
  max-width: 100%;
}

/* Tablet */
@media (min-width: 768px) {
  .container {
    padding: 0 32px;
    max-width: 768px;
  }
}

/* Desktop */
@media (min-width: 1024px) {
  .container {
    padding: 0 48px;
    max-width: 1200px;
  }
}

/* Large Desktop */
@media (min-width: 1440px) {
  .container {
    padding: 0 148px;
    max-width: 1920px;
  }
}
\`\`\`

### Animation Keyframes
\`\`\`css
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s var(--ease-out) forwards;
}

.animate-scale-in {
  animation: scaleIn 0.4s var(--ease-out) forwards;
}
\`\`\`
## Part 1: High-Level Design Overview

### Visual Design Philosophy

**Overall Aesthetic Approach**: Bold cinematic design with dark theme dominance
- Heavy emphasis on visual hierarchy through dramatic contrast
- Content-forward approach showcasing media thumbnails prominently
- Minimalist text overlaid on rich visual backgrounds
- Premium entertainment brand aesthetic with sophisticated color treatment

**Color Palette Strategy and Psychology**:
- Primary: \`#E50914\` (Signature red) - Creates urgency and excitement
- Background: \`#000000\` (Pure black) - Cinema-like immersive experience
- Text Primary: \`#FFFFFF\` (Pure white) - Maximum contrast for readability
- Accent: \`rgba(255, 255, 255, 0.7)\` - Subtle secondary text treatment
- Gradients: Complex multi-stop linear/radial gradients for depth

**Typography Hierarchy and Personality**:
- Custom font stack: \`'Netflix Sans', 'Helvetica Neue', 'Segoe UI', Roboto, Ubuntu, sans-serif\`
- Weight range: 400 (regular) to 900 (black) for dramatic hierarchy
- Size range: 14px (small UI text) to 56px (hero headlines)
- Line height ratio: 1.25-1.4 for optimal readability
- Letter spacing: Tight (-0.5px to 0px) for modern appearance

**Spacing and Layout Principles**:
- 8px base unit grid system (8, 16, 24, 32, 48px increments)
- Golden ratio proportions in section heights
- Generous whitespace around key CTAs
- Asymmetric layouts balanced with visual weight

**Visual Rhythm and Grid Systems**:
- 12-column responsive grid with fluid containers
- Max-width constraint: 1920px for ultra-wide screens
- Consistent 16px gutters with responsive scaling
- Vertical rhythm based on 24px baseline grid

### User Experience Patterns

**Navigation Structure and Behavior**:
- Minimalist header with logo prominence and single CTA
- Fixed positioning with subtle background overlay
- Mobile-first responsive collapse patterns
- Clear visual hierarchy: Brand > Actions

**Interaction Patterns and Micro-animations**:
- 0.25s cubic-bezier(0.4, 0, 0.68, 0.06) standard easing
- Hover states with opacity and background color transitions
- Form focus states with border color animation
- Smooth scroll-triggered animations

**Content Organization and Information Architecture**:
- Above-the-fold: Value proposition + immediate action
- Progressive disclosure: FAQ accordions
- Social proof through trending content visualization
- Benefit-focused feature grid layout

**Mobile Responsiveness Approach**:
- Mobile-first CSS methodology
- Fluid typography with clamp() functions
- Touch-optimized button sizes (minimum 44px)
- Stack-first layouts with horizontal arrangement on larger screens

**Accessibility Considerations**:
- Semantic HTML structure with proper headings
- ARIA labels and hidden text for screen readers
- Sufficient color contrast ratios (4.5:1 minimum)
- Keyboard navigation support

### Technical Implementation Summary

**Frontend Frameworks and Libraries**:
- Pure HTML/CSS implementation option
- React JSX component architecture option
- No external UI library dependencies
- Custom CSS properties system

**CSS Methodologies**:
- Utility-first approach with numbered classes (.style-0, .style-1)
- CSS-in-JS for React implementation
- Custom properties for consistent theming
- BEM-like naming for component variants

**Animation/Transition Technologies**:
- CSS transforms and transitions
- Keyframe animations for complex movements
- GPU-accelerated properties (transform, opacity)
- Reduced motion media query support

**Responsive Design Approach**:
- Mobile-first breakpoint strategy
- Fluid layouts with percentage-based widths
- Flexible box and CSS Grid for complex layouts
- Progressive enhancement philosophy

**Component Architecture Patterns**:
- Container/Presentational component separation
- Atomic design methodology (atoms, molecules, organisms)
- Reusable layout components with prop-based variants
- CSS modules for style encapsulation

---

## Part 2: Detailed Section-by-Section Breakdown

### Header Navigation Section

#### Structure & Layout

**HTML Structure**:
\`\`\`html
<header data-layout="container" data-uia="header+container" dir="ltr">
  <div data-layout="item"> <!-- Logo container -->
    <span data-uia="nmhp-card-header+logo">
      <!-- SVG Logo -->
    </span>
  </div>
  <div data-layout="item"> <!-- CTA container -->
    <a data-uia="header-login-link" href="/gb/login">Sign In</a>
  </div>
</header>
\`\`\`

**Layout Implementation**:
- Container: \`display: inline-flex\`
- Height: \`88px\` fixed
- Max-width: \`1920px\`
- Padding: \`0px 148px\` (horizontal only)
- Justify-content: \`space-between\`
- Align-items: \`center\`

**Flexbox Details**:
\`\`\`css
.header-container {
  display: inline-flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 88px;
  margin-left: -16px; /* Negative margin for grid alignment */
  width: calc(100% + 16px);
}
\`\`\`

#### Typography System

**Logo Typography**:
- SVG implementation: \`viewBox="0 0 111 30"\`
- Width: \`9.25rem\` (148px)
- Height: \`40px\`
- Color: \`rgb(229, 9, 20)\` (#E50914)
- Accessible text: "Netflix" (visually hidden)

**CTA Button Typography**:
- Font-family: \`'Netflix Sans', 'Helvetica Neue', 'Segoe UI', Roboto, Ubuntu, sans-serif\`
- Font-size: \`14px\`
- Font-weight: \`500\` (medium)
- Line-height: \`14px\` (1.0 ratio)
- Letter-spacing: \`normal\`

#### Color System

**Header Background**:
\`\`\`css
.header-overlay {
  background: linear-gradient(
    rgba(0, 0, 0, 0.8) 0%, 
    rgba(0, 0, 0, 0.79) 8.333%, 
    rgba(0, 0, 0, 0.757) 16.67%, 
    /* ... continues with opacity fade */
    rgba(0, 0, 0, 0) 100%
  );
}
\`\`\`

**Color Variables**:
- Primary red: \`#E50914\`
- Text primary: \`#FFFFFF\`
- Background overlay: \`rgba(0, 0, 0, 0.8)\`

#### Component Styling

**Sign In Button**:
\`\`\`css
.cta-button {
  background-color: rgb(229, 9, 20);
  border: 0px none;
  border-radius: 4px;
  color: rgb(255, 255, 255);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  min-height: 32px;
  padding: 4px 16px;
  transition-duration: 0.25s;
  transition-property: background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.68, 0.06);
}
\`\`\`

#### Interactive Elements

**Button Hover States**:
- Transition duration: \`0.25s\`
- Easing: \`cubic-bezier(0.4, 0, 0.68, 0.06)\`
- Properties: \`background-color, border-color\`
- No explicit hover color defined (browser default darkening)

#### Responsive Design

**Mobile Breakpoints**:
\`\`\`css
@media (max-width: 768px) {
  .header-container {
    padding: 0px 16px; /* Reduced from 148px */
    height: 64px; /* Reduced from 88px */
  }
  
  .logo {
    width: 7rem; /* Reduced from 9.25rem */
  }
}
\`\`\`

---

### Hero Section

#### Structure & Layout

**HTML Structure**:
\`\`\`html
<div data-uia="nmhp-hero">
  <div> <!-- Background container -->
    <div data-uia="hero-vlv">
      <!-- Background image with gradient overlay -->
    </div>
  </div>
  <div> <!-- Content container -->
    <div> <!-- Text content -->
      <h1>Unlimited films, series and more</h1>
      <p>Starts at £5.99. Cancel at any time.</p>
    </div>
    <div> <!-- Email form -->
      <form data-uia="email-form">
        <!-- Form elements -->
      </form>
    </div>
  </div>
</div>
\`\`\`

**Layout Measurements**:
- Section height: \`826.398px\`
- Content max-width: \`588px\`
- Content padding: \`0px 32px 32px\`
- Background positioning: \`50% 50%\` (center)
- Min-height content area: \`658.4px\`

#### Typography System

**Hero Headline (H1)**:
\`\`\`css
.hero-headline {
  font-family: 'Netflix Sans', 'Helvetica Neue', 'Segoe UI', Roboto, Ubuntu, sans-serif;
  font-size: 56px;
  font-weight: 900; /* Black weight */
  line-height: 70px; /* 1.25 ratio */
  color: rgb(255, 255, 255);
  margin-bottom: 16px;
  text-align: center;
}
\`\`\`

**Hero Subheadline (P)**:
\`\`\`css
.hero-subheadline {
  font-family: 'Netflix Sans', 'Helvetica Neue', 'Segoe UI', Roboto, Ubuntu, sans-serif;
  font-size: 20px;
  font-weight: 500; /* Medium weight */
  color: rgb(255, 255, 255);
  margin-bottom: 32px;
  text-align: center;
}
\`\`\`

**Form Label Typography**:
\`\`\`css
.form-label {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: rgba(255, 255, 255, 0.7); /* 70% opacity */
}
\`\`\`

#### Color System

**Background Gradient System**:
\`\`\`css
.hero-background {
  background-image: linear-gradient(
    7deg, 
    rgba(0, 0, 0, 0.85) 10%, 
    rgba(0, 0, 0, 0.847) 17.25%, 
    rgba(0, 0, 0, 0.835) 24.5%, 
    rgba(0, 0, 0, 0.82) 31.75%,
    /* ... continues with gradual opacity reduction */
    rgba(0, 0, 0, 0.6) 97%
  );
}
\`\`\`

**Radial Gradient Accents**:
\`\`\`css
.hero-accent-gradient {
  background: radial-gradient(
    11% 56% at 17% 50%, 
    rgb(70, 21, 24) 0%, 
    rgba(0, 0, 0, 0) 100%
  ), radial-gradient(
    11% 56% at 83% 50%, 
    rgb(70, 21, 24) 0%, 
    rgba(0, 0, 0, 0) 100%
  );
}
\`\`\`

#### Component Styling

**Email Input Field**:
\`\`\`css
.email-input {
  background: rgba(22, 22, 22, 0.7);
  border: 1px solid rgba(128, 128, 128, 0.7);
  border-radius: 4px;
  color: rgb(255, 255, 255);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  padding: 24px 16px 8px; /* Top padding for floating label */
  width: 100%;
  min-width: 200px;
}
\`\`\`

**Get Started CTA Button**:
\`\`\`css
.get-started-button {
  background: rgb(229, 9, 20);
  border: 0px none;
  border-radius: 4px;
  color: rgb(255, 255, 255);
  cursor: pointer;
  font-size: 24px; /* Larger than header CTA */
  font-weight: 500;
  min-height: 56px; /* Larger than header CTA */
  padding: 12px 24px;
  margin-left: 8px;
  flex: 0 0 auto; /* No flex grow/shrink */
  transition-duration: 0.25s;
  transition-property: background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.68, 0.06);
}
\`\`\`

**Chevron Icon**:
\`\`\`css
.button-icon {
  width: 1.5rem; /* 24px */
  height: 24px;
  margin-left: 12px;
  fill: currentColor; /* Inherits button text color */
}
\`\`\`

#### Interactive Elements

**Form Container Layout**:
\`\`\`css
.email-form-container {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  text-align: left;
  margin: 1rem auto 0px;
  padding-top: 16px;
  max-width: 783px;
}
\`\`\`

**Floating Label Animation**:
\`\`\`css
.floating-label {
  position: absolute;
  left: 16px;
  top: 16px;
  z-index: 1;
  transition-property: top, font-size, line-height;
  transition-duration: 0.25s;
  transition-timing-function: cubic-bezier(0.4, 0, 0.68, 0.06);
  pointer-events: none;
}

.floating-label.active {
  top: 8px;
  font-size: 12px;
  line-height: 16px;
}
\`\`\`

#### Responsive Design

**Mobile Hero Typography**:
\`\`\`css
@media (max-width: 768px) {
  .hero-headline {
    font-size: 32px; /* Reduced from 56px */
    line-height: 40px; /* Reduced from 70px */
  }
  
  .hero-subheadline {
    font-size: 18px; /* Reduced from 20px */
  }
}
\`\`\`

**Mobile Form Layout**:
\`\`\`css
@media (max-width: 768px) {
  .email-form-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .get-started-button {
    margin-left: 0;
    margin-top: 16px;
    width: 100%;
  }
}
\`\`\`

---

### Trending Content Section

#### Structure & Layout

**Section Container**:
\`\`\`css
.trending-section {
  padding: 80px 0; /* Vertical spacing */
  background: #000000;
}

.trending-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}
\`\`\`

**Content Grid**:
\`\`\`css
.trending-grid {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding: 0 0 16px 0; /* Bottom padding for scrollbar */
}

.trending-item {
  flex: 0 0 200px; /* Fixed width, no grow/shrink */
  scroll-snap-align: start;
  position: relative;
}
\`\`\`

#### Typography System

**Section Heading**:
\`\`\`css
.trending-heading {
  font-size: 32px;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 24px;
  line-height: 1.2;
}
\`\`\`

**Ranking Numbers**:
\`\`\`css
.ranking-number {
  position: absolute;
  left: -12px;
  bottom: -8px;
  font-size: 120px;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.1);
  line-height: 1;
  z-index: 1;
}
\`\`\`

#### Component Styling

**Content Cards**:
\`\`\`css
.trending-card {
  background: rgba(20, 20, 20, 0.8);
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 16/9;
  position: relative;
  backdrop-filter: blur(10px);
}

.trending-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.trending-card:hover img {
  transform: scale(1.05);
}
\`\`\`

**Netflix Badge**:
\`\`\`css
.netflix-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #E50914;
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 3px;
  letter-spacing: 0.5px;
}
\`\`\`

---

### Features Grid Section

#### Structure & Layout

**Grid Container**:
\`\`\`css
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 32px;
  padding: 80px 24px;
  max-width: 1200px;
  margin: 0 auto;
}
\`\`\`

#### Typography System

**Section Heading**:
\`\`\`css
.features-section-title {
  font-size: 40px;
  font-weight: 700;
  color: #FFFFFF;
  text-align: center;
  margin-bottom: 48px;
  line-height: 1.1;
}
\`\`\`

**Feature Card Titles**:
\`\`\`css
.feature-title {
  font-size: 32px;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 16px;
  line-height: 1.2;
}
\`\`\`

**Feature Descriptions**:
\`\`\`css
.feature-description {
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin-bottom: 24px;
}
\`\`\`

#### Component Styling

**Feature Cards**:
\`\`\`css
.feature-card {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 16px;
  padding: 48px 32px;
  text-align: left;
  position: relative;
  overflow: hidden;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
\`\`\`

**Feature Icons**:
\`\`\`css
.feature-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
  opacity: 0.8;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}
\`\`\`

#### Interactive Elements

**Card Hover Effects**:
\`\`\`css
.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
\`\`\`

---

### FAQ Section

#### Structure & Layout

**FAQ Container**:
\`\`\`css
.faq-section {
  background: #000000;
  padding: 80px 24px;
}

.faq-container {
  max-width: 1000px;
  margin: 0 auto;
}
\`\`\`

#### Typography System

**FAQ Section Title**:
\`\`\`css
.faq-title {
  font-size: 48px;
  font-weight: 700;
  color: #FFFFFF;
  text-align: center;
  margin-bottom: 48px;
  line-height: 1.1;
}
\`\`\`

**Question Text**:
\`\`\`css
.faq-question {
  font-size: 24px;
  font-weight: 400;
  color: #FFFFFF;
  line-height: 1.3;
  padding: 24px;
  margin: 0;
}
\`\`\`

**Answer Text**:
\`\`\`css
.faq-answer {
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  padding: 0 24px 24px 24px;
  margin: 0;
}
\`\`\`

#### Component Styling

**FAQ Items**:
\`\`\`css
.faq-item {
  background: rgba(45, 45, 45, 1);
  margin-bottom: 8px;
  border-radius: 0;
  overflow: hidden;
}

.faq-button {
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;
}

.faq-button:hover {
  background: rgba(55, 55, 55, 1);
}
\`\`\`

**Plus/Minus Icons**:
\`\`\`css
.faq-icon {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  color: #FFFFFF;
  transition: transform 0.3s ease;
}

.faq-item.active .faq-icon {
  transform: translateY(-50%) rotate(45deg);
}
\`\`\`

#### Interactive Elements

**Accordion Animation**:
\`\`\`css
.faq-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.faq-item.active .faq-content {
  max-height: 300px; /* Approximate max height */
}
\`\`\`

---

### Footer Section

#### Structure & Layout

**Footer Container**:
\`\`\`css
.footer {
  background: #000000;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 80px 24px 40px;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
}
\`\`\`

**Email Signup (Footer)**:
\`\`\`css
.footer-signup {
  text-align: center;
  margin-bottom: 64px;
}

.footer-signup-form {
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  gap: 8px;
}
\`\`\`

#### Typography System

**Footer CTA Text**:
\`\`\`css
.footer-cta-text {
  font-size: 18px;
  font-weight: 400;
  color: #FFFFFF;
  margin-bottom: 24px;
  line-height: 1.4;
}
\`\`\`

**Footer Links**:
\`\`\`css
.footer-links {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 32px 48px;
  margin-bottom: 48px;
}

.footer-link {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 400;
  text-decoration: underline;
  line-height: 1.5;
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: rgba(255, 255, 255, 0.9);
}
\`\`\`

**Footer Meta Text**:
\`\`\`css
.footer-meta {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  margin-top: 32px;
}
\`\`\`

#### Component Styling

**Language Selector**:
\`\`\`css
.language-selector {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  color: #FFFFFF;
  font-size: 14px;
  padding: 8px 32px 8px 16px;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,..."); /* Custom dropdown arrow */
  background-repeat: no-repeat;
  background-position: right 12px center;
}
\`\`\`

**Phone Number Link**:
\`\`\`css
.phone-link {
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 400;
  text-decoration: underline;
  margin-bottom: 32px;
  display: inline-block;
}
\`\`\`

---

## Code Implementation Examples

### JSX Component Structure
\`\`\`jsx
const StreamingLandingPage = () => {
  return (
    <div style={{
      background: 'rgb(0, 0, 0)',
      color: 'rgb(255, 255, 255)',
      fontFamily: '"Netflix Sans", "Helvetica Neue", "Segoe UI", Roboto, Ubuntu, sans-serif'
    }}>
      <Header />
      <HeroSection />
      <TrendingSection />
      <FeaturesGrid />
      <FAQSection />
      <Footer />
    </div>
  );
};
\`\`\`

### CSS Custom Properties System
\`\`\`css
:root {
  /* Colors */
  --primary-red: #E50914;
  --background-black: #000000;
  --text-primary: #FFFFFF;
  --text-secondary: rgba(255, 255, 255, 0.7);
  --text-muted: rgba(255, 255, 255, 0.5);
  
  /* Typography */
  --font-family-primary: 'Netflix Sans', 'Helvetica Neue', 'Segoe UI', Roboto, Ubuntu, sans-serif;
  --font-size-xs: 12px;
  --font-size-sm: 14px;
  --font-size-base: 16px;
  --font-size-lg: 18px;
  --font-size-xl: 20px;
  --font-size-2xl: 24px;
  --font-size-3xl: 32px;
  --font-size-4xl: 40px;
  --font-size-5xl: 48px;
  --font-size-6xl: 56px;
  
  /* Spacing */
  --spacing-xs: 8px;
  --spacing-sm: 16px;
  --spacing-md: 24px;
  --spacing-lg: 32px;
  --spacing-xl: 48px;
  --spacing-2xl: 64px;
  --spacing-3xl: 80px;
  
  /* Transitions */
  --transition-fast: 0.15s;
  --transition-base: 0.25s;
  --transition-slow: 0.3s;
  --ease-out: cubic-bezier(0.4, 0, 0.2, 1);
  --ease-in-out: cubic-bezier(0.4, 0, 0.68, 0.06);
}
\`\`\`

### Responsive Breakpoint System
\`\`\`css
/* Mobile First Approach */
.container {
  padding: 0 16px;
  max-width: 100%;
}

/* Tablet */
@media (min-width: 768px) {
  .container {
    padding: 0 32px;
    max-width: 768px;
  }
}

/* Desktop */
@media (min-width: 1024px) {
  .container {
    padding: 0 48px;
    max-width: 1200px;
  }
}

/* Large Desktop */
@media (min-width: 1440px) {
  .container {
    padding: 0 148px;
    max-width: 1920px;
  }
}
\`\`\`

### Animation Keyframes
\`\`\`css
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s var(--ease-out) forwards;
}

.animate-scale-in {
  animation: scaleIn 0.4s var(--ease-out) forwards;
}
\`\`\``
    },
    {
      id: 'jasper-ai',
      name: 'Jasper AI',
      image: '/designinspoimages/jasper1.png',
      description: 'AI-powered content creation platform with modern interface',
      prompt: `Transform my current website design, CSS styling and overall frontend into the following strict and descriptive design and frontend specification document. Make sure to not change any copy, context or functionality we currently have in the website, this is solely a design UI/UX and frontend redesign.

IMPORTANT COMPONENT LIBRARY REQUIREMENT:
You MUST use shadcn/ui for all UI components to ensure accessibility, consistency, and modern design patterns. shadcn/ui is a collection of copy-and-paste components built on top of Radix UI primitives and styled with version 3 of Tailwind CSS.

Installation & Setup:
1. Install shadcn/ui CLI: npx shadcn-ui@latest init
2. Configure your components.json when prompted
3. Add components as needed: npx shadcn-ui@latest add [component-name]

Key Benefits:
- Fully accessible components built on Radix UI primitives
- Beautifully designed with consistent styling
- Customizable and themeable
- TypeScript support out of the box
- Copy-paste friendly - you own the code
- Works seamlessly with Tailwind CSS

Usage Guidelines:
- Use shadcn/ui components for buttons, forms, modals, navigation, cards, etc.
- Examples: npx shadcn-ui@latest add button card form dialog navigation-menu
- Import components from your components/ui directory
- Customize component variants using the built-in variant system
- Leverage the built-in dark mode support
- Follow the component composition patterns for complex UI elements

Always prioritize shadcn/ui components over custom implementations to maintain design consistency and accessibility standards.
## Part 1: High-Level Design Overview

### Visual Design Philosophy

**Overall Aesthetic Approach**: Clean, modern SaaS platform design with vibrant accent system
- Minimalist layout with strategic use of whitespace and geometric elements
- Professional business aesthetic balanced with creative energy through colorful illustrations
- Grid-based modular design system with consistent component patterns
- Contemporary typography with precise spacing and clear information hierarchy

**Color Palette Strategy and Psychology**:
- Primary Brand: \`#FA4028\` (Coral Red) - Energy, innovation, and action-oriented
- Primary Text: \`#00063D\` (Deep Navy Blue) - Trust, professionalism, stability
- Background: \`#FFFFFF\` (Pure White) - Clean, spacious, modern
- Secondary Accents:
  - Blue: \`#CEEBFF\` (Light Blue) - Platform/Technology theme
  - Orange: \`#FFB3A3\` (Peach) - Solutions/Workflow theme  
  - Green: \`#D2FFC1\` (Mint Green) - Resources/Growth theme
  - Pink: \`#FFB3DE\` (Rose Pink) - Company/Team theme
  - Yellow: \`#FFE066\` (Sunshine Yellow) - Trust/Security theme

**Typography Hierarchy and Personality**:
- Primary Font: \`'ABC ROM', Arial, sans-serif\` - Modern, technical, readable
- Font Weight Range: 450 (Regular) to 900 (Ultra Bold) for strong hierarchy
- Font Variation Settings: \`'opsz' 45\` - Optical sizing for optimal readability
- Letter Spacing: \`-0.16px\` to \`-0.01em\` for tight, modern appearance
- Line Height Ratios: 1.4 standard, creating comfortable reading experience

**Spacing and Layout Principles**:
- 16px base unit grid system with consistent gaps
- Container max-width: \`1360px\` for optimal readability across devices
- 12-column responsive grid with \`minmax(0px, 1fr)\` flexibility
- Generous section padding with 80px+ vertical spacing
- Strategic use of negative space for content breathing room

**Visual Rhythm and Grid Systems**:
- Modular card-based layout system
- Consistent component sizing with aspect ratios
- Mathematical proportions in spacing (8px, 16px, 24px, 32px, 48px)
- Color-coded sections for easy visual navigation
- Grid overlay system for precise alignment

### User Experience Patterns

**Navigation Structure and Behavior**:
- Fixed header with glassmorphism backdrop blur effect
- Horizontal navigation with color-coded hover states
- Clear visual hierarchy: Logo > Navigation > Actions
- Hover animations with opacity transitions and background color changes
- Mobile-responsive collapse patterns

**Interaction Patterns and Micro-animations**:
- Hover states with 0.1s and 0.3s transition durations
- Color-coded interactive elements per navigation theme
- Smooth opacity transitions on hover (opacity 0 to visible)
- Transform animations with precise transform-origin points
- Backdrop blur effects for depth and focus

**Content Organization and Information Architecture**:
- Hero section with clear value proposition and dual CTA pattern
- Social proof through client logo grid
- Progressive disclosure through expandable sections
- Feature comparison with visual product cards
- Footer with comprehensive link organization

**Mobile Responsiveness Approach**:
- Container-based responsive design with max-width constraints
- Grid template adjustments across breakpoints
- Touch-optimized interface elements
- Flexible typography with clamp() functions for fluid scaling

**Accessibility Considerations**:
- High contrast color combinations (navy on white)
- Semantic HTML structure with proper roles and labels
- Screen reader friendly content with aria-labels
- Focus states and keyboard navigation support
- Alternative text for complex illustrations

### Technical Implementation Summary

**Frontend Frameworks and Libraries**:
- Custom CSS system with utility-first approach
- Advanced CSS Grid and Flexbox layouts
- CSS custom properties for theming consistency
- Font variation settings for optimized typography
- Backdrop filter effects for modern glassmorphism

**CSS Methodologies**:
- Numbered class system (.style-0, .style-1) for modular organization
- CSS-in-JS implementation option available
- Custom property theming system with extensive color tokens
- Container query support with \`container-type: inline-size\`
- Advanced typography with \`font-variation-settings\`

**Animation/Transition Technologies**:
- CSS transition properties with precise timing
- Transform animations with specific transform-origin points
- Opacity-based hover states and micro-interactions
- Smooth property transitions for professional feel
- Performance-optimized animations using transform and opacity

**Responsive Design Approach**:
- Mobile-first development methodology
- Fluid grid systems with minmax() functions
- Container-based responsive breakpoints
- Flexible component sizing with percentage-based widths
- Progressive enhancement for advanced features

**Component Architecture Patterns**:
- Atomic design system with reusable components
- Color-variant system for themed components
- Card-based layout patterns for content organization
- Modular navigation system with theme variants
- Grid-based layout components with responsive behavior

---

## Part 2: Detailed Section-by-Section Breakdown

### Header Navigation Section

#### Structure & Layout

**HTML Structure**:
\`\`\`html
<div style="backdrop-filter: blur(20px); height: 80px; display: grid;">
  <a aria-label="Jasper.ai Homepage" href="/">
    <!-- Logo SVG -->
  </a>
  <ul role="list">
    <li>
      <div data-nav-btn="Platform" data-wf--nav-menu-link--variant="blue">
        <!-- Navigation Link -->
      </div>
    </li>
    <!-- Additional navigation items -->
  </ul>
  <div>
    <!-- CTA Buttons -->
  </div>
</div>
\`\`\`

**Layout Implementation**:
- Container Height: \`80px\` fixed
- Display: \`grid\`
- Grid Template: \`480px 480px 480px\` (three equal columns)
- Backdrop Filter: \`blur(20px)\` for glassmorphism effect
- Background: \`rgba(255, 255, 255, 0.8)\` with transparency
- Padding: \`16px 20px\`
- Position: \`fixed\` with \`z-index: 1000\`

**Grid System Details**:
\`\`\`css
.header-container {
  display: grid;
  grid-template-columns: 480px 480px 480px;
  grid-auto-columns: minmax(0px, 1fr);
  column-gap: 16px;
  row-gap: 16px;
  align-items: center;
  position: relative;
}
\`\`\`

#### Typography System

**Logo Typography**:
- SVG implementation with \`viewBox="0 0 232 85"\`
- Width: \`6.1875rem\` (99px)
- Height: \`36.2656px\`
- Color: \`rgb(250, 64, 40)\` (#FA4028)
- Max-width: \`100%\` for responsiveness

**Navigation Link Typography**:
\`\`\`css
.nav-link {
  font-family: 'ABC ROM', Arial, sans-serif;
  font-size: 16px;
  line-height: 22.4px; /* 1.4 ratio */
  font-weight: 450;
  letter-spacing: -0.16px;
  font-variation-settings: 'opsz' 45;
  text-transform: none;
  pointer-events: none; /* For overlay text */
  position: relative;
}
\`\`\`

**Typography Hierarchy**:
- Navigation Text: 16px / 22.4px line height
- Font Weight: 450 (Medium-Light)
- Optical Size: 45 for screen optimization
- Letter Spacing: Tight (-0.16px) for modern look

#### Color System

**Header Background System**:
\`\`\`css
.header-background {
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  position: fixed;
  z-index: 1000;
}
\`\`\`

**Navigation Color Variants**:
\`\`\`css
/* Platform - Blue Theme */
.nav-variant-blue {
  --hover-color: rgb(206, 235, 255);
}

/* Solutions - Orange Theme */
.nav-variant-flame {
  --hover-color: rgb(255, 179, 163);
}

/* Resources - Green Theme */
.nav-variant-green {
  --hover-color: rgb(210, 255, 193);
}

/* Company - Pink Theme */
.nav-variant-pink {
  --hover-color: rgb(255, 179, 222);
}
\`\`\`

**Primary Color Tokens**:
- Brand Primary: \`#FA4028\`
- Text Primary: \`#00063D\`
- Background Primary: \`#FFFFFF\`
- Hover Backgrounds: Color-coded per section

#### Component Styling

**Navigation Link Hover System**:
\`\`\`css
.nav-link-container {
  padding: 8px 12px;
  position: relative;
  display: flex;
  align-items: center;
}

.nav-hover-background {
  position: absolute;
  inset: 0px;
  display: flex;
  align-items: center;
  justify-content: start;
  opacity: 0;
  transition: opacity 0.3s;
}

.nav-hover-left {
  width: 15%;
  background-color: var(--hover-color);
  transform-origin: calculated;
  transition: opacity 0.1s;
}

.nav-hover-center {
  width: 100%;
  background-color: var(--hover-color);
  transition: opacity 0.3s;
}

.nav-hover-right {
  width: 15%;
  background-color: var(--hover-color);
  transition: opacity 0.1s;
}
\`\`\`

#### Interactive Elements

**Hover Animation System**:
- Entry Animation: Left and right elements fade in at 0.1s
- Center Animation: Full background fades in at 0.3s
- Color: Section-specific theme colors
- Transform Origin: Precise positioning for smooth scaling
- Opacity: 0 (hidden) to visible on hover

**Button States**:
\`\`\`css
.nav-button {
  width: 100%;
  height: 28.1641px;
  position: absolute;
  inset: 0px;
  border-radius: 0px;
  background-color: transparent;
  border: 0px none;
  cursor: pointer;
  z-index: 3;
}
\`\`\`

#### Responsive Design

**Mobile Navigation Considerations**:
\`\`\`css
@media (max-width: 768px) {
  .header-container {
    grid-template-columns: 1fr auto;
    padding: 12px 16px;
  }
  
  .navigation-list {
    display: none; /* Hidden in mobile, replaced with hamburger */
  }
  
  .logo {
    width: 5rem; /* Smaller logo for mobile */
  }
}
\`\`\`

---

### Hero Section

#### Structure & Layout

**HTML Structure**:
\`\`\`html
<section class="hero-section">
  <div class="announcement-banner">
    <span class="new-badge">New!</span>
    <span class="announcement-text">Introducing the new Platform: Canvas, Agents, and a bold rebrand.</span>
  </div>
  <div class="hero-content">
    <h1 class="hero-headline">AI built for marketers</h1>
    <p class="hero-description">Platform description and value proposition</p>
    <div class="hero-cta-container">
      <button class="cta-primary">Start Free Trial</button>
      <button class="cta-secondary">Get A Demo</button>
    </div>
  </div>
  <div class="hero-illustration">
    <!-- Complex illustration with person and graphics -->
  </div>
</section>
\`\`\`

**Layout Measurements**:
- Section min-height: \`823px\`
- Content max-width: \`1360px\` centered
- Hero illustration: Positioned with complex layering
- Announcement banner: Top positioning with green badge
- CTA buttons: Dual-button horizontal layout

#### Typography System

**Hero Headline (H1)**:
\`\`\`css
.hero-headline {
  font-family: 'ABC ROM', Arial, sans-serif;
  font-size: clamp(48px, 8vw, 72px); /* Fluid responsive sizing */
  font-weight: 700; /* Bold */
  line-height: 1.1; /* Tight for impact */
  color: #00063D;
  text-align: center;
  margin-bottom: 24px;
  font-variation-settings: 'opsz' 45;
}
\`\`\`

**Announcement Banner Typography**:
\`\`\`css
.announcement-banner {
  background: rgba(210, 255, 193, 0.3); /* Light green background */
  border-radius: 24px;
  padding: 12px 24px;
  margin-bottom: 48px;
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.new-badge {
  background: #32D74B; /* iOS Green */
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.announcement-text {
  font-size: 16px;
  font-weight: 450;
  color: #00063D;
  line-height: 1.4;
}
\`\`\`

**Hero Description Typography**:
\`\`\`css
.hero-description {
  font-size: 20px;
  line-height: 28px; /* 1.4 ratio */
  font-weight: 450;
  color: #00063D;
  max-width: 600px;
  margin: 0 auto 32px;
  text-align: center;
}
\`\`\`

#### Color System

**Hero Background System**:
\`\`\`css
.hero-section {
  background: linear-gradient(
    135deg,
    rgba(246, 248, 250, 0.6) 0%,
    rgba(255, 255, 255, 1) 50%,
    rgba(248, 250, 252, 0.8) 100%
  );
  position: relative;
  overflow: hidden;
}
\`\`\`

**Illustration Color Palette**:
- Grid Background: \`#32D74B\` (Bright Green) with opacity variations
- Accent Shapes: \`#FF1D8E\` (Magenta Pink) for geometric elements
- Person Image: Natural skin tones with professional styling
- UI Elements: Blues, greens, and teals for dashboard mockups

#### Component Styling

**Primary CTA Button**:
\`\`\`css
.cta-primary {
  background: #FA4028;
  border: 2px solid #FA4028;
  color: white;
  font-size: 16px;
  font-weight: 500;
  padding: 16px 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'ABC ROM', Arial, sans-serif;
  letter-spacing: -0.01em;
}

.cta-primary:hover {
  background: #E6331A;
  border-color: #E6331A;
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(250, 64, 40, 0.3);
}
\`\`\`

**Secondary CTA Button**:
\`\`\`css
.cta-secondary {
  background: transparent;
  border: 2px solid #00063D;
  color: #00063D;
  font-size: 16px;
  font-weight: 500;
  padding: 16px 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'ABC ROM', Arial, sans-serif;
  letter-spacing: -0.01em;
  margin-left: 16px;
}

.cta-secondary:hover {
  background: #00063D;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 6, 61, 0.2);
}
\`\`\`

#### Interactive Elements

**Hover Animation Implementation**:
\`\`\`css
.cta-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
  gap: 16px;
}

.cta-button {
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.cta-button::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(45deg, transparent, rgba(255,255,255,0.2), transparent);
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.cta-button:hover::before {
  transform: translateX(100%);
}
\`\`\`

#### Responsive Design

**Mobile Hero Adjustments**:
\`\`\`css
@media (max-width: 768px) {
  .hero-headline {
    font-size: 42px;
    line-height: 1.2;
  }
  
  .hero-description {
    font-size: 18px;
    line-height: 26px;
  }
  
  .cta-container {
    flex-direction: column;
    gap: 12px;
  }
  
  .cta-secondary {
    margin-left: 0;
    width: 100%;
  }
  
  .hero-illustration {
    margin-top: 48px;
    max-width: 100%;
  }
}
\`\`\`

---

### Client Logos Section

#### Structure & Layout

**Section Container**:
\`\`\`css
.client-logos-section {
  padding: 80px 0;
  background: #FAFBFC;
}

.client-logos-container {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.client-logos-grid {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 48px;
  flex-wrap: wrap;
  margin-top: 32px;
}
\`\`\`

#### Typography System

**Section Heading**:
\`\`\`css
.client-logos-heading {
  font-size: 24px;
  font-weight: 500;
  color: #00063D;
  margin-bottom: 32px;
  text-align: center;
  line-height: 1.3;
}
\`\`\`

#### Component Styling

**Logo Treatment**:
\`\`\`css
.client-logo {
  height: 40px;
  width: auto;
  opacity: 0.7;
  filter: grayscale(100%);
  transition: all 0.3s ease;
}

.client-logo:hover {
  opacity: 1;
  filter: grayscale(0%);
  transform: scale(1.05);
}
\`\`\`

---

### Platform Overview Section

#### Structure & Layout

**Section Architecture**:
\`\`\`css
.platform-section {
  padding: 120px 0;
  background: white;
  position: relative;
}

.platform-container {
  max-width: 1360px;
  margin: 0 auto;
  padding: 0 24px;
}
\`\`\`

#### Typography System

**Main Section Heading**:
\`\`\`css
.platform-main-heading {
  font-size: 56px;
  font-weight: 700;
  line-height: 1.1;
  color: #00063D;
  text-align: center;
  margin-bottom: 24px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}
\`\`\`

**Section Description**:
\`\`\`css
.platform-description {
  font-size: 20px;
  line-height: 28px;
  font-weight: 450;
  color: rgba(0, 6, 61, 0.8);
  text-align: center;
  max-width: 700px;
  margin: 0 auto 48px;
}
\`\`\`

#### Component Styling

**Explore Button**:
\`\`\`css
.explore-button {
  background: #00063D;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 48px auto;
  display: block;
}

.explore-button:hover {
  background: #0011A7;
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 6, 61, 0.3);
}
\`\`\`

---

### Product Cards Section

#### Structure & Layout

**Card Grid System**:
\`\`\`css
.product-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-top: 64px;
  padding: 0 24px;
}
\`\`\`

#### Typography System

**Card Headings**:
\`\`\`css
.card-heading {
  font-size: 32px;
  font-weight: 700;
  color: #00063D;
  margin-bottom: 16px;
  line-height: 1.2;
}
\`\`\`

**Card Descriptions**:
\`\`\`css
.card-description {
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 6, 61, 0.8);
  margin-bottom: 24px;
  font-weight: 450;
}
\`\`\`

#### Component Styling

**Product Card Design**:
\`\`\`css
.product-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 6, 61, 0.1);
  min-height: 400px;
}

.product-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 6, 61, 0.1);
  border-color: rgba(0, 6, 61, 0.2);
}
\`\`\`

**Canvas Card (Green Theme)**:
\`\`\`css
.canvas-card {
  background: linear-gradient(135deg, #D2FFC1 0%, #E8FFE8 100%);
}

.canvas-illustration {
  width: 100%;
  height: 200px;
  background: url('canvas-illustration.png') no-repeat center;
  background-size: contain;
  margin-bottom: 24px;
}
\`\`\`

**Studio Card (Orange Theme)**:
\`\`\`css
.studio-card {
  background: linear-gradient(135deg, #FFB3A3 0%, #FFEEE8 100%);
}
\`\`\`

**Jasper IQ Card (Blue Theme)**:
\`\`\`css
.jasper-iq-card {
  background: linear-gradient(135deg, #CEEBFF 0%, #E8F4FF 100%);
}
\`\`\`

**Trust Card (Yellow Theme)**:
\`\`\`css
.trust-card {
  background: linear-gradient(135deg, #FFE066 0%, #FFF2B3 100%);
}
\`\`\`

#### Interactive Elements

**Card Arrow Hover**:
\`\`\`css
.card-arrow {
  position: absolute;
  bottom: 24px;
  right: 24px;
  width: 32px;
  height: 32px;
  background: rgba(0, 6, 61, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.product-card:hover .card-arrow {
  background: #00063D;
  color: white;
  transform: translateX(8px);
}
\`\`\`

---

### Marketing Teams Section

#### Structure & Layout

**Section Design**:
\`\`\`css
.marketing-teams-section {
  padding: 120px 0;
  background: #F8F9FA;
  text-align: center;
}

.teams-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}
\`\`\`

#### Typography System

**Section Heading**:
\`\`\`css
.teams-heading {
  font-size: 48px;
  font-weight: 700;
  color: #00063D;
  line-height: 1.1;
  margin-bottom: 48px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}
\`\`\`

**Persona Tags**:
\`\`\`css
.persona-tag {
  background: #32D74B;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  display: inline-block;
  margin: 0 8px 16px 0;
  transition: all 0.2s ease;
}

.persona-tag:hover {
  background: #28CD3F;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(50, 215, 75, 0.3);
}
\`\`\`

#### Component Styling

**Marketing Persona Cards**:
\`\`\`css
.persona-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin: 16px;
  box-shadow: 0 4px 16px rgba(0, 6, 61, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.persona-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #32D74B, #FF1D8E, #007AFF);
}

.persona-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 32px rgba(0, 6, 61, 0.15);
}
\`\`\`

---

### Features Grid Section

#### Structure & Layout

**Grid Implementation**:
\`\`\`css
.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-top: 80px;
  padding: 0 24px;
}

@media (max-width: 768px) {
  .features-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}
\`\`\`

#### Typography System

**Feature Titles**:
\`\`\`css
.feature-title {
  font-size: 24px;
  font-weight: 700;
  color: #00063D;
  margin-bottom: 16px;
  line-height: 1.2;
}
\`\`\`

**Feature Descriptions**:
\`\`\`css
.feature-description {
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 6, 61, 0.7);
  margin-bottom: 24px;
  font-weight: 450;
}
\`\`\`

#### Component Styling

**Feature Cards by Theme**:

**Connect with Community (Green)**:
\`\`\`css
.community-card {
  background: linear-gradient(135deg, #D2FFC1 0%, #E8FFE8 100%);
  padding: 32px;
  border-radius: 16px;
  position: relative;
}

.community-illustration {
  /* Green themed icons and graphics */
  filter: hue-rotate(120deg) saturate(1.2);
}
\`\`\`

**Watch Foundations (Blue)**:
\`\`\`css
.foundations-card {
  background: linear-gradient(135deg, #CEEBFF 0%, #E8F4FF 100%);
  padding: 32px;
  border-radius: 16px;
}
\`\`\`

**Search Knowledge Center (Yellow)**:
\`\`\`css
.knowledge-card {
  background: linear-gradient(135deg, #FFE066 0%, #FFF2B3 100%);
  padding: 32px;
  border-radius: 16px;
}
\`\`\`

**Additional Cards (Pink, Green, Orange)**:
Following the same pattern with theme-specific gradient backgrounds.

#### Interactive Elements

**Learn More Links**:
\`\`\`css
.learn-more-link {
  color: #FA4028;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.learn-more-link:hover {
  color: #E6331A;
  transform: translateX(4px);
}

.learn-more-arrow {
  transition: transform 0.2s ease;
}

.learn-more-link:hover .learn-more-arrow {
  transform: translateX(4px);
}
\`\`\`

---

### Footer Section

#### Structure & Layout

**Footer Architecture**:
\`\`\`css
.footer {
  background: #00063D;
  color: white;
  padding: 80px 0 40px;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.footer-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 48px;
  margin-bottom: 48px;
}
\`\`\`

#### Typography System

**Footer Column Headings**:
\`\`\`css
.footer-column-heading {
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin-bottom: 24px;
  line-height: 1.2;
}
\`\`\`

**Footer Links**:
\`\`\`css
.footer-link {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 450;
  line-height: 20px;
  text-decoration: none;
  display: block;
  margin-bottom: 12px;
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: white;
}
\`\`\`

#### Component Styling

**Footer CTA Buttons**:
\`\`\`css
.footer-cta-primary {
  background: #FA4028;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 16px;
}

.footer-cta-secondary {
  background: transparent;
  color: white;
  border: 2px solid white;
  padding: 14px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}
\`\`\`

---

## Code Implementation Examples

### JSX Component Structure
\`\`\`jsx
const AIContentPlatformLanding = () => {
  return (
    <div style={{
      fontFamily: '"ABC ROM", Arial, sans-serif',
      color: '#00063D',
      backgroundColor: '#FFFFFF',
      fontVariationSettings: '"opsz" 45'
    }}>
      <Header />
      <AnnouncementBanner />
      <HeroSection />
      <ClientLogos />
      <PlatformOverview />
      <ProductCards />
      <MarketingTeams />
      <FeaturesGrid />
      <Footer />
    </div>
  );
};
\`\`\`

### CSS Custom Properties System
\`\`\`css
:root {
  /* Brand Colors */
  --brand-primary: #FA4028;
  --brand-secondary: #00063D;
  --background-primary: #FFFFFF;
  --background-secondary: #F8F9FA;
  --background-tertiary: #FAFBFC;
  
  /* Theme Colors */
  --theme-blue: #CEEBFF;
  --theme-orange: #FFB3A3;
  --theme-green: #D2FFC1;
  --theme-pink: #FFB3DE;
  --theme-yellow: #FFE066;
  
  /* Typography */
  --font-family-primary: 'ABC ROM', Arial, sans-serif;
  --font-variation-settings: 'opsz' 45;
  --font-weight-regular: 450;
  --font-weight-medium: 500;
  --font-weight-semibold: 600;
  --font-weight-bold: 700;
  
  /* Spacing System */
  --spacing-xs: 8px;
  --spacing-sm: 16px;
  --spacing-md: 24px;
  --spacing-lg: 32px;
  --spacing-xl: 48px;
  --spacing-2xl: 64px;
  --spacing-3xl: 80px;
  --spacing-4xl: 120px;
  
  /* Layout */
  --container-max-width: 1360px;
  --grid-gap: 16px;
  --border-radius-sm: 8px;
  --border-radius-md: 16px;
  --border-radius-lg: 24px;
  
  /* Transitions */
  --transition-fast: 0.1s ease;
  --transition-normal: 0.2s ease;
  --transition-slow: 0.3s ease;
  
  /* Effects */
  --backdrop-blur: blur(20px);
  --shadow-sm: 0 4px 8px rgba(0, 6, 61, 0.1);
  --shadow-md: 0 8px 16px rgba(0, 6, 61, 0.15);
  --shadow-lg: 0 12px 32px rgba(0, 6, 61, 0.2);
}
\`\`\`

### Advanced Grid System
\`\`\`css
.responsive-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--grid-gap);
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 24px;
}

.grid-item-full {
  grid-column: 1 / -1;
}

.grid-item-half {
  grid-column: span 6;
}

.grid-item-third {
  grid-column: span 4;
}

.grid-item-quarter {
  grid-column: span 3;
}

@media (max-width: 768px) {
  .grid-item-half,
  .grid-item-third,
  .grid-item-quarter {
    grid-column: 1 / -1;
  }
}
\`\`\`

### Component Theme System
\`\`\`css
.component-blue {
  --theme-primary: var(--theme-blue);
  --theme-hover: rgba(206, 235, 255, 0.8);
  --theme-text: #0066CC;
}

.component-orange {
  --theme-primary: var(--theme-orange);
  --theme-hover: rgba(255, 179, 163, 0.8);
  --theme-text: #CC4400;
}

.component-green {
  --theme-primary: var(--theme-green);
  --theme-hover: rgba(210, 255, 193, 0.8);
  --theme-text: #00AA44;
}

.themed-component {
  background: linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-hover) 100%);
  color: var(--theme-text);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-lg);
  transition: transform var(--transition-normal);
}

.themed-component:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-md);
}
\`\`\`

### Advanced Typography System
\`\`\`css
.text-display-large {
  font-size: clamp(48px, 8vw, 72px);
  font-weight: var(--font-weight-bold);
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-variation-settings: var(--font-variation-settings);
}

.text-display-medium {
  font-size: clamp(36px, 6vw, 56px);
  font-weight: var(--font-weight-bold);
  line-height: 1.1;
  letter-spacing: -0.01em;
  font-variation-settings: var(--font-variation-settings);
}

.text-heading-large {
  font-size: clamp(28px, 4vw, 40px);
  font-weight: var(--font-weight-bold);
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.text-body-large {
  font-size: 20px;
  line-height: 1.4;
  font-weight: var(--font-weight-regular);
  letter-spacing: -0.01em;
}

.text-body-regular {
  font-size: 16px;
  line-height: 1.4;
  font-weight: var(--font-weight-regular);
  letter-spacing: -0.16px;
}
\`\`\`
`
    }
  ];

  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="w-full max-w-6xl h-full flex flex-col py-2"
    >
      {/* Back button */}
      <motion.button
        whileHover={{ x: -4 }}
        onClick={onBack}
        className="flex items-center text-black/60 hover:text-black transition-colors mb-4"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </motion.button>

      <div className="text-center mb-4">
        <h1 className="text-2xl font-light text-black mb-1 tracking-tight">
          Choose your design
        </h1>
        <p className="text-black/60 text-sm">
          Select the visual style for your app
        </p>
      </div>

      {/* Design Options - More compact */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
          {/* Render default design types */}
          {designTypes.map((design, index) => (
            <motion.div
              key={design.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="group"
            >
              <motion.button
                whileHover={{ y: -8 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleDesignSelect(design)}
                className={`w-full text-left transition-all duration-500 ${
                  selectedDesign === design.id
                    ? 'ring-2 ring-vibe-cyan'
                    : 'hover:ring-1 hover:ring-black/20'
                }`}
                style={{
                  borderRadius: '24px',
                  overflow: 'hidden'
                }}
              >
                {/* Design Preview */}
                <div className="relative aspect-[3/2] bg-white/60 backdrop-blur-xl border border-black/10 overflow-hidden"
                  style={{ 
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)'
                  }}
                >
                  <img
                    src={design.image}
                    alt={design.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  
                  {/* Selection indicator */}
                  {selectedDesign === design.id && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-3 right-3 w-5 h-5 bg-vibe-cyan rounded-full flex items-center justify-center"
                    >
                      <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </motion.div>
                  )}
                </div>

                {/* Design Info */}
                <div className="p-4 bg-white/80 backdrop-blur-xl border-x border-b border-black/10"
                  style={{ 
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)'
                  }}
                >
                  <h3 className="text-lg font-medium text-black mb-1">
                    {design.name}
                  </h3>
                  <p className="text-black/60 font-light text-sm mb-2">
                    {design.description}
                  </p>
                  
                  {/* UI Components Info */}
                  {(() => {
                    const uiComponents = getUIComponentsForDesign(design.id);
                    if (uiComponents) {
                      return (
                        <div className="mt-2 pt-2 border-t border-black/10">
                          <div className="flex items-center mb-1">
                            <svg className="w-3 h-3 mr-1 text-black/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                            </svg>
                            <span className="text-xs font-medium text-black/50">UI Components</span>
                          </div>
                          <div className="text-xs text-black/40 mb-1">
                            {uiComponents.uiLibrary} • {uiComponents.components.length} components
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {uiComponents.components.slice(0, 3).map((component, idx) => (
                              <span key={idx} className="text-xs bg-black/5 text-black/60 px-2 py-0.5 rounded-full">
                                {component.name}
                              </span>
                            ))}
                            {uiComponents.components.length > 3 && (
                              <span className="text-xs text-black/40">
                                +{uiComponents.components.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </motion.button>
            </motion.div>
          ))}

          {/* Render uploaded designs */}
          {uploadedDesigns.map((design, index) => (
            <motion.div
              key={design.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (designTypes.length + index) * 0.1, duration: 0.5 }}
              className="group"
            >
              <motion.button
                whileHover={{ y: -8 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleDesignSelect(design)}
                className={`w-full text-left transition-all duration-500 ${
                  selectedDesign === design.id
                    ? 'ring-2 ring-vibe-cyan'
                    : 'hover:ring-1 hover:ring-black/20'
                }`}
                style={{
                  borderRadius: '24px',
                  overflow: 'hidden'
                }}
              >
                {/* Custom Design Preview */}
                <div className="relative aspect-[3/2] bg-white/60 backdrop-blur-xl border border-black/10 overflow-hidden"
                  style={{ 
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)'
                  }}
                >
                  <img
                    src={design.image}
                    alt={design.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  
                  {/* Custom design indicator */}
                  <div className="absolute top-3 left-3 bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                    AI Generated
                  </div>
                  
                  {/* Selection indicator */}
                  {selectedDesign === design.id && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-3 right-3 w-5 h-5 bg-vibe-cyan rounded-full flex items-center justify-center"
                    >
                      <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </motion.div>
                  )}
                </div>

                {/* Custom Design Info */}
                <div className="p-4 bg-white/80 backdrop-blur-xl border-x border-b border-black/10"
                  style={{ 
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)'
                  }}
                >
                  <h3 className="text-lg font-medium text-black mb-1">
                    {design.name}
                  </h3>
                  <p className="text-black/60 font-light text-sm">
                    {design.description}
                  </p>
                </div>
              </motion.button>
            </motion.div>
          ))}

          {/* Upload Design Inspiration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (designTypes.length + uploadedDesigns.length) * 0.1, duration: 0.5 }}
            className="group"
          >
            <div
              className="w-full cursor-pointer"
              style={{
                borderRadius: '24px',
                overflow: 'hidden'
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {/* Upload Area */}
              <div className={`relative aspect-[3/2] bg-white/40 backdrop-blur-xl border-2 border-dashed transition-all duration-300 flex items-center justify-center ${
                dragActive ? 'border-vibe-cyan bg-vibe-cyan/10' : 'border-black/20 hover:border-black/40'
              }`}
                style={{ 
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)'
                }}
              >
                {isAnalyzing ? (
                  <div className="text-center">
                    <div className="w-10 h-10 border-2 border-black/20 border-t-black/60 rounded-full mx-auto mb-2 animate-spin"></div>
                    <p className="text-black/60 text-xs font-light">Analyzing design...</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-10 h-10 bg-black/10 rounded-full mx-auto mb-2 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                      <svg className="w-5 h-5 text-black/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <p className="text-black/60 text-xs font-light">
                      {dragActive ? 'Drop your image here' : 'Upload design inspiration'}
                    </p>
                  </div>
                )}
              </div>

              {/* Upload Info */}
              <div className="p-4 bg-white/60 backdrop-blur-xl border-x border-b border-black/10"
                style={{ 
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)'
                }}
              >
                <h3 className="text-lg font-medium text-black/80 mb-1">
                  Upload Inspiration
                </h3>
                <p className="text-black/50 font-light text-sm">
                  AI will analyze your design and create a specification
                </p>
              </div>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
            />
          </motion.div>
        </div>
      </div>

        {/* Color Palette Editor */}
        {selectedDesign && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 relative"
          >
            <div className="bg-white/60 backdrop-blur-xl border border-black/10 rounded-2xl p-4"
              style={{ 
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)'
              }}
            >
              <h3 className="text-base font-medium text-black mb-3">Customize Colors</h3>
              
                              {/* Color Grid */}
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 mb-3">
                  {extractColors(selectedDesign).map((colorInfo, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => openColorPicker(index, customColors[index] || colorInfo.color)}
                      className="group relative"
                    >
                      <div
                        className="w-8 h-8 rounded-full border-2 border-white shadow-md cursor-pointer transition-all duration-200 group-hover:shadow-lg"
                        style={{ backgroundColor: customColors[index] || colorInfo.color }}
                      />
                      <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                        <div className="bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          {colorInfo?.name || 'Color'}
                        </div>
                      </div>
                    </motion.button>
                  ))}
              </div>

              {/* Add Color Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  const colors = extractColors(selectedDesign);
                  const newIndex = colors.length;
                  openColorPicker(newIndex, '#C1F2E8');
                }}
                className="flex items-center text-black/60 hover:text-black transition-colors text-xs"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add custom color
              </motion.button>
            </div>

                      {/* Figma-style Color Picker */}
            <AnimatePresence>
              {showColorPicker && (
                <>
                  {/* Backdrop overlay */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9998]"
                    onClick={() => setShowColorPicker(false)}
                  />
                  
                  {/* Color picker modal */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[9999]"
                  >
                    <div
                      ref={colorPickerRef}
                      className="bg-white/98 backdrop-blur-xl border border-black/10 rounded-xl p-4 shadow-2xl"
                      style={{ 
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)'
                      }}
                    >
                    {/* Color Preview */}
                    <div className="flex items-center mb-3">
                      <div
                        className="w-10 h-10 rounded-lg border-2 border-black/10 mr-3"
                        style={{ backgroundColor: hslToHex(hue, saturation, lightness) }}
                      />
                      <div className="flex-1">
                        <div className="text-xs text-black/60 mb-1">Color</div>
                        <div className="text-sm font-mono text-black">
                          {hslToHex(hue, saturation, lightness).toUpperCase()}
                        </div>
                      </div>
                    </div>

                    {/* Color Area */}
                    <div 
                      className="relative w-48 h-32 mb-3 rounded-lg overflow-hidden cursor-crosshair"
                     onMouseDown={(e) => {
                       const rect = e.currentTarget.getBoundingClientRect();
                       const handleMouseMove = (e) => {
                         const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                         const y = Math.max(0, Math.min(100, 100 - ((e.clientY - rect.top) / rect.height) * 100));
                         setSaturation(x);
                         setLightness(y);
                       };
                       const handleMouseUp = () => {
                         document.removeEventListener('mousemove', handleMouseMove);
                         document.removeEventListener('mouseup', handleMouseUp);
                       };
                       document.addEventListener('mousemove', handleMouseMove);
                       document.addEventListener('mouseup', handleMouseUp);
                       
                       // Initial click
                       const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                       const y = Math.max(0, Math.min(100, 100 - ((e.clientY - rect.top) / rect.height) * 100));
                       setSaturation(x);
                       setLightness(y);
                     }}
                   >
                     <div
                       className="w-full h-full"
                       style={{
                         background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hue}, 100%, 50%))`
                       }}
                     />
                     <div
                       className="absolute w-4 h-4 border-2 border-white rounded-full shadow-lg pointer-events-none transform -translate-x-2 -translate-y-2"
                       style={{
                         left: `${saturation}%`,
                         top: `${100 - lightness}%`,
                         backgroundColor: hslToHex(hue, saturation, lightness)
                       }}
                     />
                   </div>

                                                         {/* Hue Slider */}
                    <div 
                      className="relative w-full h-4 mb-3 rounded-full overflow-hidden cursor-pointer"
                     onMouseDown={(e) => {
                       const rect = e.currentTarget.getBoundingClientRect();
                       const handleMouseMove = (e) => {
                         const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                         setHue((x / 100) * 360);
                       };
                       const handleMouseUp = () => {
                         document.removeEventListener('mousemove', handleMouseMove);
                         document.removeEventListener('mouseup', handleMouseUp);
                       };
                       document.addEventListener('mousemove', handleMouseMove);
                       document.addEventListener('mouseup', handleMouseUp);
                       
                       // Initial click
                       const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
                       setHue((x / 100) * 360);
                     }}
                   >
                     <div
                       className="w-full h-full"
                       style={{
                         background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
                       }}
                     />
                                           <div
                        className="absolute top-1/2 w-3 h-3 bg-white border-2 border-black/20 rounded-full shadow-lg pointer-events-none transform -translate-x-1.5 -translate-y-1.5"
                        style={{ left: `${(hue / 360) * 100}%` }}
                      />
                   </div>

                    {/* Preset Colors */}
                    <div className="mb-3">
                      <div className="text-xs text-black/60 mb-2">Presets</div>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          '#FF6B35', '#F7931E', '#FFD23F', '#06FFA5', '#1FB6FF', '#5865F2',
                          '#8B5CF6', '#EC4899', '#EF4444', '#10B981', '#3B82F6', '#6366F1'
                        ].map((presetColor) => (
                          <button
                            key={presetColor}
                            onClick={() => {
                              const [h, s, l] = hexToHsl(presetColor);
                              setHue(h);
                              setSaturation(s);
                              setLightness(l);
                            }}
                            className="w-6 h-6 rounded-md border border-black/10 hover:scale-110 transition-transform"
                            style={{ backgroundColor: presetColor }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setShowColorPicker(false)}
                        className="px-3 py-1.5 text-black/60 hover:text-black transition-colors text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => setShowColorPicker(false)}
                        className="px-3 py-1.5 bg-black text-white rounded-lg hover:bg-black/90 transition-colors text-sm"
                      >
                        Done
                      </button>
                    </div>
                                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
        </motion.div>
      )}

        {/* Continue Button */}
        {selectedDesign && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            ref={continueSectionRef}
            className="pt-4 border-t border-black/5 mt-auto"
          >
            <div className="flex justify-center space-x-4">
              {onBack && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onBack}
                  className="px-8 py-3 border border-black/10 rounded-xl font-medium text-black/70 hover:bg-black/5 transition-colors"
                >
                  Back
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleContinue}
                className="px-8 py-3 rounded-full font-light bg-black text-white hover:bg-black/90 shadow-lg shadow-black/20 transition-all duration-300"
              >
                Continue
              </motion.button>
            </div>
          </motion.div>
        )}
    </motion.div>
  );
};

export default DesignSelector; 